import { z } from 'zod';
import { MAX_LIMIT, photoSchema, ContactType, SocialProfileType, locationSchema, editorBlockSchema, SaleOrderStatus, AuctionStatus, TokenStatus, ArtAssetType, Status, ArtAssetFormat, ArtAssetContentFlags, ArtistStatus, ArtistVerificationStatus, SaleStatus, digitalOriginalSchema as digitalOriginalSchema$1, saleDataSchema, classificationItemSchema as classificationItemSchema$2, exifSchema, TokenHistoryRecordKind, SiteStatus, InviteStatus, InstitutionType, InstitutionVerificationStatus, InstitutionUserRole, passwordSchema, UserStatus, strongPasswordSchema, optionSchema, Gender, ArtistType, DocumentType, DocumentSubtype, ContactUsFrom, ContactUsInquiryType, ArtworkRequestStatus, ArtistsWaitlistItemStatus, NewsPublicationTarget, directusItemStatusSchema, SelectionStatus, COLLECTIONS, DirectusFileTagsType, ReportType, ReportedCollection, SubscriptionFrom, LeadType, LeadFrom } from '@do/models';

const AssetPresetKey = {
    Jpeg300x69: 'jpeg-300x69',
    Webp300x69: 'webp-300x69',
    Jpeg600x138: 'jpeg-600x138',
    Webp600x138: 'webp-600x138',
    Jpeg900x206: 'jpeg-900x206',
    Webp900x206: 'webp-900x206',
    Jpeg60x60: 'jpeg-60x60',
    Webp60x60: 'webp-60x60',
    Jpeg120x120: 'jpeg-120x120',
    Webp120x120: 'webp-120x120',
    Jpeg180x180: 'jpeg-180x180',
    Webp180x180: 'webp-180x180',
    Default500wWebp: 'default-500w-webp',
    Default1000wWebp: 'default-1000w-webp',
    Default1500wWebp: 'default-1500w-webp',
    Default1900wWebp: 'default-1900w-webp',
    Default500wJpeg: 'default-500w-jpeg',
    Default1000wJpeg: 'default-1000w-jpeg',
    Default1500wJpeg: 'default-1500w-jpeg',
    VR1024Jpeg100dpi: 'VR-1024-jpeg100dpi',
    VR2048Jpeg100dpi: 'VR-2048-jpeg100dpi',
    VR3072Jpeg100dpi: 'VR-3072-jpeg100dpi',
    VR4096Jpeg100dpi: 'VR-4096-jpeg100dpi',
    Default: 'default',
};
const assetsTransformationParamItem = z.object({
    key: z.string().nullable(),
    fit: z.string().optional().nullable(),
    width: z.number().nullable(),
    height: z.number().optional().nullable(),
    quality: z.number().nullable(),
    withoutEnlargement: z.boolean().optional().nullable(),
    format: z.string().optional().nullable(),
    transforms: z.array(z.unknown()).optional().nullable(),
});
const assetsTransformationParamsList = z.array(assetsTransformationParamItem);

const currencySchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    symbol: z.string(),
    decimals: z.number().int(),
    address: z.string(),
    blockchain: z.string(),
    rates: z.record(z.number()),
});
const currenciesListSchema = z.array(currencySchema);

const evmAddressSchema = z.string().regex(/^0x[a-fA-F0-9]{40}$/);
const evmAddressWithExplorerLinkSchema = z.object({
    value: evmAddressSchema,
    explorer_url: z.string(),
});
const hashSchema = z.string().regex(/^0x[a-fA-F0-9]{64}$/);
const hashWithExplorerLinkSchema = z.object({
    value: hashSchema,
    explorer_url: z.string(),
});

const Order = {
    Asc: 'asc',
    Desc: 'desc',
};
const orderSchema$1 = z.nativeEnum(Order);
const minMaxSchema$1 = z.tuple([z.number(), z.number()]);
const minMaxOptionSchema = z
    .string()
    .regex(/^\d+,\d+$/)
    .transform((val) => val.split(',').map((v) => Number(v)));
const dateRangeSchema = z.tuple([z.string(), z.string()]);
const arrayStringsOption$3 = z.string().transform((val) => val.split(',').map((v) => v));
const arrayIdsOption$2 = z
    .string()
    .transform((val) => val.split(',').map((v) => Number(v)))
    .pipe(z.array(z.number()));
const integerQueryParamSchema = z.union([
    z
        .string()
        .transform((arg) => parseInt(arg, 10))
        .pipe(z.number().int().positive()),
    z.number().int().gte(0),
]);
const booleanOptionSchema = z.enum(['true', 'false']).transform((v) => v === 'true');
const paginationQuerySchema = z.object({
    limit: z
        .string()
        .transform((v) => Number(v))
        .pipe(z.number().int().min(1).max(MAX_LIMIT)),
    offset: z
        .string()
        .optional()
        .transform((v) => Number(v) || 0)
        .pipe(z.number().nonnegative()),
});

const numberParamSchema = z.string().transform((v) => Number(v));
const idArraySchema = z.array(z.union([z.string(), z.number()]));

const stringOrInt = z.union([
    z
        .string()
        .transform((arg) => parseInt(arg, 10))
        .pipe(z.number().int().positive()),
    z.number().int().gte(0),
]);

var index$6 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    photoSchema: photoSchema,
    AssetPresetKey: AssetPresetKey,
    assetsTransformationParamItem: assetsTransformationParamItem,
    assetsTransformationParamsList: assetsTransformationParamsList,
    currencySchema: currencySchema,
    currenciesListSchema: currenciesListSchema,
    evmAddressSchema: evmAddressSchema,
    evmAddressWithExplorerLinkSchema: evmAddressWithExplorerLinkSchema,
    hashSchema: hashSchema,
    hashWithExplorerLinkSchema: hashWithExplorerLinkSchema,
    Order: Order,
    orderSchema: orderSchema$1,
    minMaxSchema: minMaxSchema$1,
    minMaxOptionSchema: minMaxOptionSchema,
    dateRangeSchema: dateRangeSchema,
    arrayStringsOption: arrayStringsOption$3,
    arrayIdsOption: arrayIdsOption$2,
    integerQueryParamSchema: integerQueryParamSchema,
    booleanOptionSchema: booleanOptionSchema,
    paginationQuerySchema: paginationQuerySchema,
    numberParamSchema: numberParamSchema,
    idArraySchema: idArraySchema,
    stringOrInt: stringOrInt
});

const phoneContactSchema = z.object({
    type: z.literal(ContactType.Phone),
    value: z.string().trim(),
}, { invalid_type_error: 'Please enter a valid phone' });
const emailContactSchema = z.object({
    type: z.literal(ContactType.Email),
    value: z.string().trim().email({ message: 'Please enter a valid email' }),
});
const contactSchema = z.discriminatedUnion('type', [phoneContactSchema, emailContactSchema]);
const contactsSchema = z.array(contactSchema);

const socialProfileSchema = z.object({
    type: z.nativeEnum(SocialProfileType),
    value: z.string().trim(),
});
const socialProfilesSchema = z.array(socialProfileSchema);

const createOneAgencyRequestBodySchema$1 = z.object({
    name: z.string(),
    slug: z.string().nullable(),
    contacts: contactsSchema.optional(),
    social_profiles: socialProfilesSchema.optional(),
    locations: z.array(locationSchema).nullable().optional(),
});
const createOneAgencyResponseBodySchema$1 = z.object({
    id: z.number(),
});
const updateOneAgencyRequestBodySchema$1 = z
    .object({
    name: z.string(),
    biography: editorBlockSchema.nullable(),
    contacts: contactSchema.array().nullable(),
    social_profiles: socialProfileSchema.array().nullable(),
    locations: z.array(locationSchema).nullable(),
})
    .partial();
const updateOneAgencyResponseBodySchema$1 = z.object({
    id: z.number(),
});
const updateOneAgencyQuerySchema$1 = z.object({
    institution_id: z.string(),
});
const readOneAgencyResponseBodySchema$2 = z.object({
    id: z.number(),
    slug: z.string().nullable(),
    name: z.string(),
    biography: editorBlockSchema.nullable().optional(),
    contacts: contactsSchema.nullable().optional(),
    social_profiles: socialProfileSchema.array().nullable().optional(),
    locations: z.array(locationSchema).nullable().optional(),
});
const readOneAgencyQuerySchema$2 = z.object({
    institution_id: z.string(),
});
const agencyIdParamsSchema$2 = z.object({
    id: z.string(),
});

const artAssetSaleOrderInfoSchema = z.object({
    id: z.string().uuid(),
    status: z.nativeEnum(SaleOrderStatus),
    price: z.string(),
    price_formatted: z.number(),
    seller_fee_percent: z.number(),
    buyer_fee_percent: z.number(),
    storage_fee_usd: z.number(),
    secondary_market_royalty: z.number(),
    date_created: z.union([z.string(), z.date()]),
    currency: currencySchema,
    realized_price_usd: z.number().nullable().optional(),
    storage_fee_value: z.string().nullable().optional(),
    storage_fee_value_formatted: z.number().nullable().optional(),
});
const artAssetAuctionInfoSchema = z.object({
    id: z.string().uuid(),
    bid_step_value: z.string(),
    buyer_fee_premium_percent: z.number(),
    currency: currencySchema,
    start_date: z.coerce.date().nullable().optional(),
    end_date: z.coerce.date().nullable().optional(),
    duration: z.number().int(),
    initial_bid_value: z.string(),
    secondary_market_royalty: z.number(),
    seller_fee_percent: z.number(),
    status: z.nativeEnum(AuctionStatus),
    storage_fee_value: z.string(),
});
const artAssetSalesStatusSchema = z.object({
    minted: z.boolean(),
    is_on_auction: z.boolean(),
    is_on_direct_sale: z.boolean(),
    sale_order: artAssetSaleOrderInfoSchema.optional(),
    auction: artAssetAuctionInfoSchema.optional(),
});
const tokenMetadataSchema = z.object({
    metadata: z.string().url(),
    image: z.string().url(),
    digital_original: z.string().url(),
    source_file: z.string().url().optional(),
});
const arrayIdsOption$1 = z
    .string()
    .transform((val) => val.split(',').map((v) => Number(v)))
    .pipe(z.array(z.number()));
const arrayStringsOption$2 = z.string().transform((val) => val.split(',').map((v) => v));
const minMaxOption$2 = z
    .string()
    .regex(/^\d+,\d+$/)
    .transform((val) => val.split(',').map((v) => Number(v)));
const booleanOption$2 = z.enum(['true', 'false']).transform((v) => v === 'true');
const artAssetFilterSchema = z
    .object({
    auctions_visible: booleanOption$2,
    artists: arrayIdsOption$1,
    art_assets: arrayIdsOption$1,
    institutions: arrayIdsOption$1,
    styles: arrayIdsOption$1,
    tags: arrayIdsOption$1,
    mediums: arrayIdsOption$1,
    countries: arrayIdsOption$1,
    original_size_x: minMaxOption$2,
    original_size_y: minMaxOption$2,
    original_size_z: minMaxOption$2,
    price: minMaxOption$2,
    dlct: booleanOption$2,
    documents: booleanOption$2,
    exclude_art_assets: arrayIdsOption$1,
    statuses: arrayStringsOption$2,
    token_status: arrayStringsOption$2,
    search: z.string().min(2),
    styles_slugs: arrayStringsOption$2,
    mediums_slugs: arrayStringsOption$2,
    countries_slugs: arrayStringsOption$2,
    artists_slugs: arrayStringsOption$2,
    creation_year: minMaxOption$2,
    curator_review: booleanOption$2,
    curators: arrayIdsOption$1,
    curators_slugs: arrayStringsOption$2,
    selections: arrayIdsOption$1,
    selections_slugs: arrayStringsOption$2,
    art_asset_type: arrayStringsOption$2,
})
    .partial();
const artAssetFilterParamsSchema = artAssetFilterSchema;
const artAssetFilterOptionsResponse = z.object({
    artists: z.array(z.object({ id: z.number(), name: z.string(), slug: z.string().nullable(), art_assets_count: z.number() })),
    art_assets: z.array(z.object({ id: z.number(), name: z.string() })),
    institutions: z.array(z.object({ id: z.number(), name: z.string() })),
    styles: z.array(z.object({ id: z.number(), name: z.string(), slug: z.string(), art_assets_count: z.number() })),
    medium: z.array(z.object({ id: z.number(), name: z.string(), slug: z.string(), art_assets_count: z.number() })),
    tags: z.array(z.object({ id: z.number(), name: z.string() })),
    original_size_x: z.tuple([z.number(), z.number()]).optional(),
    original_size_y: z.tuple([z.number(), z.number()]).optional(),
    original_size_z: z.tuple([z.number(), z.number()]).optional(),
    price: z.tuple([z.number(), z.number()]).optional(),
    dlct: z.boolean().optional(),
    documents: z.boolean().optional(),
    items_total_count: z.number(),
    token_status: z.array(z.nativeEnum(TokenStatus)),
    countries: z.array(z.object({ id: z.number(), name: z.string(), slug: z.string(), art_assets_count: z.number() })),
    creation_year: z.tuple([z.number(), z.number()]).optional(),
    curator_review: z.boolean().optional(),
    curators: z.array(z.object({
        id: z.number(),
        first_name: z.string(),
        last_name: z.string(),
        slug: z.string().nullable(),
        art_assets_count: z.number(),
    })),
    selections: z.array(z.object({ id: z.number(), slug: z.string(), title: z.string(), art_assets_count: z.number() })),
    art_asset_type: z.array(z.nativeEnum(ArtAssetType)),
});
const sortSchema = z.object({
    column: z
        .enum(['name_original', 'name_english', 'date_created', 'date_updated', 'year', 'status', 'artist', 'price'])
        .optional()
        .default('name_english'),
    order: orderSchema$1.optional().default('asc'),
});
const artAssetsListQueryParametersSchema = artAssetFilterSchema
    .and(sortSchema.partial())
    .and(paginationQuerySchema.partial());
const artAssetListItemSchema = z.object({
    id: z.number(),
    status: z.nativeEnum(Status),
    slug: z.string().nullable(),
    token_status: z.nativeEnum(TokenStatus),
    name_english: z.string().nullable(),
    name_original: z.string().nullable().optional(),
    art_asset_type: z.nativeEnum(ArtAssetType),
    format: z.nativeEnum(ArtAssetFormat),
    content_flags: z.array(z.nativeEnum(ArtAssetContentFlags)),
    creation_year: z.string().nullable().optional(),
    estimate_price: z.number().nullable(),
    realized_price: z.number().nullable(),
    physical_purchase_price: z.number().nullable(),
    initial_price: z.number().nullable(),
    date_created: z.string(),
    date_updated: z.string().nullable(),
    viewed: z.boolean(),
    sale_order: artAssetSaleOrderInfoSchema.optional().nullable(),
    auction: artAssetAuctionInfoSchema.optional().nullable(),
    main_photo: photoSchema.nullable(),
    digital_original: z
        .object({
        file_id: z.string(),
        width: z.string().nullable().optional(),
        height: z.string().nullable().optional(),
        size: z.string().nullable().optional(),
        mpx: z.number().nullable().nullable(),
    })
        .nullable(),
    digital_common: z
        .object({
        id: z.number(),
        preview: z.string().nullable(),
        file: z.string().nullable(),
        filesize: z.string().nullable(),
        filename_disk: z.string().nullable(),
        type: z.string().nullable(),
    })
        .nullable(),
    artists: z
        .array(z.object({
        id: z.number(),
        status: z.nativeEnum(ArtistStatus),
        name: z.string(),
        verification_status: z.nativeEnum(ArtistVerificationStatus),
    }))
        .nullable(),
    institutions: z
        .array(z.object({
        id: z.number(),
        name: z.string(),
    }))
        .nullable(),
    original_size_x: z.number().nullable().optional(),
    original_size_y: z.number().nullable().optional(),
    original_size_z: z.number().nullable().optional(),
    documents_count: z.number(),
    featured: z.boolean(),
});
const readManyArtAssetsResponseSchema$1 = z.array(artAssetListItemSchema);
const readManyArtAssetsResponseSchemaNew = z.array(z.object({
    id: z.number(),
    status: z.nativeEnum(Status),
    slug: z.string().nullable(),
    token_status: z.nativeEnum(TokenStatus),
    art_asset_type: z.nativeEnum(ArtAssetType),
    format: z.nativeEnum(ArtAssetFormat),
    content_flags: z.array(z.nativeEnum(ArtAssetContentFlags)),
    name_english: z.string().nullable(),
    name_original: z.string().nullable().optional(),
    creation_year: z.string().nullable().optional(),
    estimate_price: z.number().nullable(),
    realized_price: z.number().nullable(),
    physical_purchase_price: z.number().nullable(),
    initial_price: z.number().nullable(),
    date_created: z.union([z.string(), z.date()]),
    date_updated: z.union([z.string(), z.date()]).nullable(),
    viewed: z.boolean(),
    sale_order: artAssetSaleOrderInfoSchema.optional().nullable(),
    auction: artAssetAuctionInfoSchema.optional().nullable(),
    main_photo: photoSchema.nullable(),
    digital_common: z
        .object({
        id: z.number(),
        preview: z.string().nullable(),
        file: z.string().nullable(),
        filesize: z.string().nullable(),
        filename_disk: z.string().nullable(),
        type: z.string().nullable(),
    })
        .nullable(),
    digital_original: z
        .object({
        id: z.number(),
        file_id: z.string(),
        mpx: z.number(),
        size: z.string().nullable(),
        height: z.string().optional().nullable(),
        width: z.string().nullable().optional(),
        dng: z.string().nullable(),
    })
        .nullable(),
    artists: z
        .array(z.object({
        id: z.number(),
        status: z.nativeEnum(ArtistStatus),
        name: z.string(),
        verification_status: z.nativeEnum(ArtistVerificationStatus),
    }))
        .nullable(),
    institutions: z
        .array(z.object({
        id: z.number(),
        name: z.string(),
    }))
        .nullable(),
    original_size_x: z.number().nullable().optional(),
    original_size_y: z.number().nullable().optional(),
    original_size_z: z.number().nullable().optional(),
    documents_count: z.number(),
    featured: z.boolean(),
}));
const artAssetListCountersSchema = z.object({
    total_count: z.number(),
    documents_count: z.number(),
    artists_count: z.number(),
    do_count: z.number(),
});
const readManyUFDAPhotosResponseBodySchema$1 = z.array(z.object({ id: z.number(), slug: z.string(), main_photo: photoSchema.nullable() }));
const readManyArtAssetsByOwnerWalletRequestQuerySchema$1 = z.object({ address: z.string() });
const readManyArtAssetsByOwnerWalletResponseBodySchema$1 = z.array(artAssetListItemSchema.omit({
    estimate_price: true,
    physical_purchase_price: true,
    initial_price: true,
    viewed: true,
    sale_order: true,
    auction: true,
    institutions: true,
    documents_count: true,
    digital_original: true,
    digital_common: true,
    featured: true,
}));
const readOneArtAssetRequestQuerySchema = z.object({ auctions_visible: booleanOption$2.optional() });
const similarArtAssetsFilterSchema = z.object({
    artists: arrayIdsOption$1.optional(),
    mediums: arrayIdsOption$1.optional(),
    styles: arrayIdsOption$1.optional(),
    exclude_art_assets: arrayIdsOption$1,
    institutions: arrayIdsOption$1.optional(),
    statuses: arrayStringsOption$2.optional(),
});
const similarArtAssetsListQueryParametersSchema = similarArtAssetsFilterSchema
    .and(sortSchema.partial())
    .and(paginationQuerySchema);
const similarArtAssetsParamsSchema = z.object({
    id: z.coerce.number().int(),
});
const similarArtAssetsQuerySchema = z
    .object({
    statuses: arrayStringsOption$2.optional(),
    institutions: arrayIdsOption$1,
})
    .and(paginationQuerySchema);
const sizeError$1 = {
    errorMap: () => ({
        message: 'Please enter a numeric size value',
    }),
};
const createManyArtAssetsFromPhotosRequestBodySchema$1 = z.object({
    institution_id: z.number(),
    photos: z.array(z.string()),
});
const createManyArtAssetsFromPhotosResponseBodySchema$1 = z.object({ ids: z.array(z.number()) });
const createOneArtAssetFromPhotoRequestBodySchema$1 = z.object({
    institution_id: z.number(),
    photo: z.string(),
});
const createOneArtAssetFromPhotoResponseBodySchema$1 = z.object({ id: z.number() });
const createArtAssetAddArtistPayloadSchema$1 = z.object({
    id: z.number().int(),
});
const isCreateArtAssetAddArtistPayload$1 = (payload) => {
    return createArtAssetAddArtistPayloadSchema$1.safeParse(payload).success;
};
const createArtAssetNewArtistPayloadSchema$1 = z.object({
    name: z.string(),
});
const isCreateArtAssetNewArtistPayloadSchema$1 = (payload) => {
    return createArtAssetNewArtistPayloadSchema$1.safeParse(payload).success;
};
const createArtAssetArtistsPayloadSchema = z.object({
    create: z.array(z.object({
        artists_id: z.union([createArtAssetAddArtistPayloadSchema$1, createArtAssetNewArtistPayloadSchema$1]),
    })),
});
const createArtAssetParamsSchema$1 = z.object({
    name_english: z.string().min(1),
    name_original: z.string().nullable().optional(),
    art_asset_type: z.nativeEnum(ArtAssetType),
    format: z.nativeEnum(ArtAssetFormat),
    content_flags: z.array(z.nativeEnum(ArtAssetContentFlags)).default([]),
    uid: z.string().nullable().optional(),
    artists: z.array(z.union([createArtAssetAddArtistPayloadSchema$1, createArtAssetNewArtistPayloadSchema$1])),
    initial_price: z
        .number({
        errorMap: () => ({
            message: 'Please enter a numeric price value',
        }),
    })
        .positive()
        .nullable()
        .optional(),
    physical_purchase_price: z
        .number({
        errorMap: () => ({
            message: 'Please enter a numeric price value',
        }),
    })
        .positive(),
    physical_purchase_date: z.coerce
        .date({
        errorMap: () => ({
            message: 'Please enter a valid purchase date',
        }),
    })
        .nullable()
        .optional(),
    creation_year: z.string({
        errorMap: () => ({
            message: 'Please enter a year when the artwork was done',
        }),
    }),
    styles: z.array(z.union([z.string(), z.number()])).optional(),
    medium: z.array(z.union([z.string(), z.number()])).optional(),
    locations: z
        .array(locationSchema, {
        errorMap: () => ({
            message: 'Please add a location to the artwork.',
        }),
    })
        .min(1),
    framed_units: z.string().nullable().optional(),
    framed_size_x: z.number(sizeError$1).positive().nullable().optional(),
    framed_size_y: z.number(sizeError$1).positive().nullable().optional(),
    framed_size_z: z.number(sizeError$1).positive().nullable().optional(),
    original_units: z.string().nullable().optional(),
    original_size_x: z.number(sizeError$1).positive().nullable().optional(),
    original_size_y: z.number(sizeError$1).positive().nullable().optional(),
    original_size_z: z.number(sizeError$1).positive().nullable().optional(),
    description: editorBlockSchema.nullable().optional(),
    provenance: editorBlockSchema.nullable().optional(),
    photos: z.array(z.string()).optional(),
    main_photo: z.string().nullable().optional(),
    digital_common: z.string().nullable().optional(),
    documents: z
        .array(z.union([z.string(), z.number()]), {
        errorMap: () => ({
            message: 'You have to select or add a document that confirms your rights to this artwork',
        }),
    })
        .optional(),
    institution_id: z.number(),
    curator_review: z
        .object({
        curator: z.number(),
        description: editorBlockSchema.nullable().optional(),
    })
        .optional(),
});
const createArtAssetResponseBodySchema$1 = z.object({
    id: z.number(),
});
const updateArtAssetAddArtistPayloadSchema$1 = createArtAssetAddArtistPayloadSchema$1;
const isUpdateArtAssetAddArtistPayload$1 = (payload) => {
    return updateArtAssetAddArtistPayloadSchema$1.safeParse(payload).success;
};
const updateArtAssetNewArtistPayloadSchema$1 = createArtAssetNewArtistPayloadSchema$1;
const isUpdateArtistAssetNewArtistPayloadSchema$1 = (payload) => {
    return updateArtAssetNewArtistPayloadSchema$1.safeParse(payload).success;
};
const updateArtAssetArtistsPayloadSchema = z.array(z.union([updateArtAssetAddArtistPayloadSchema$1, updateArtAssetNewArtistPayloadSchema$1]));
const updateOneArtAssetsParamsSchema = z.object({
    id: z.coerce.number(),
});
const updateOneArtAssetBodySchema = z.object({
    name_english: z.string().min(1).optional(),
    name_original: z.string().nullable().optional(),
    art_asset_type: z.nativeEnum(ArtAssetType).optional(),
    format: z.nativeEnum(ArtAssetFormat).optional(),
    content_flags: z.array(z.nativeEnum(ArtAssetContentFlags)).nullable().optional(),
    uid: z.string().nullable().optional(),
    artists: updateArtAssetArtistsPayloadSchema.optional(),
    initial_price: z
        .number({
        errorMap: () => ({
            message: 'Please enter a numeric price value',
        }),
    })
        .positive()
        .nullable()
        .optional(),
    physical_purchase_price: z
        .number({
        errorMap: () => ({
            message: 'Please enter a numeric price value',
        }),
    })
        .positive()
        .optional(),
    physical_purchase_date: z.coerce
        .date({
        errorMap: () => ({
            message: 'Please enter a valid purchase date',
        }),
    })
        .nullable()
        .optional(),
    creation_year: z
        .string({
        errorMap: () => ({
            message: 'Please enter a year when the artwork was done',
        }),
    })
        .optional(),
    styles: z.array(z.union([z.string(), z.number()])).optional(),
    medium: z.array(z.union([z.string(), z.number()])).optional(),
    locations: z
        .array(locationSchema, {
        errorMap: () => ({
            message: 'Please add a location to the artwork.',
        }),
    })
        .min(1)
        .optional(),
    framed_units: z.string().nullable().optional(),
    framed_size_x: z.number(sizeError$1).positive().nullable().optional(),
    framed_size_y: z.number(sizeError$1).positive().nullable().optional(),
    framed_size_z: z.number(sizeError$1).positive().nullable().optional(),
    original_units: z.string().nullable().optional(),
    original_size_x: z.number(sizeError$1).positive().nullable().optional(),
    original_size_y: z.number(sizeError$1).positive().nullable().optional(),
    original_size_z: z.number(sizeError$1).positive().nullable().optional(),
    description: editorBlockSchema.nullable().optional(),
    provenance: editorBlockSchema.nullable().optional(),
    photos: z.array(z.string()).optional(),
    main_photo: z.string().nullable().optional(),
    digital_common: z.string().nullable().optional(),
    documents: z
        .array(z.union([z.string(), z.number()]), {
        errorMap: () => ({
            message: 'You have to select or add a document that confirms your rights to this artwork',
        }),
    })
        .optional(),
    curator_review: z
        .object({
        id: z.number(),
        curator: z.number().optional(),
        description: editorBlockSchema.nullable().optional(),
    })
        .optional(),
    institution_id: z.number().optional(),
});
const artAssetStatusChangeParamsSchema$1 = z.object({
    id: z.coerce.number(),
});
const deleteOneArtAssetsParamsSchema = z.object({
    id: z.coerce.number(),
});
const reaArtAssetTotalCountResponseSchema = z.number();
const readOneArtAssetResponseSchema$1 = z
    .object({
    id: z.number(),
    status: z.nativeEnum(Status),
    date_created: z.string(),
    date_updated: z.string().nullable(),
    art_asset_type: z.nativeEnum(ArtAssetType),
    format: z.nativeEnum(ArtAssetFormat),
    content_flags: z.array(z.nativeEnum(ArtAssetContentFlags)),
    token_status: z.nativeEnum(TokenStatus),
    token_id: z.number().nullable(),
    sale_status: z.nativeEnum(SaleStatus),
    name_english: z.string(),
    name_original: z.string().nullable(),
    description: editorBlockSchema.nullable(),
    provenance: editorBlockSchema.nullable(),
    original_units: z.string().nullable(),
    framed_units: z.string().nullable(),
    original_size_x: z.number().nullable(),
    original_size_y: z.number().nullable(),
    original_size_z: z.number().nullable(),
    framed_size_x: z.number().nullable(),
    framed_size_y: z.number().nullable(),
    framed_size_z: z.number().nullable(),
    locations: z.array(locationSchema).nullable(),
    digital_original: digitalOriginalSchema$1.nullable(),
    digital_common: z
        .object({
        id: z.number(),
        preview: z.string().nullable(),
        file: z.string().nullable(),
        filesize: z.string().nullable(),
        filename_disk: z.string().nullable(),
        type: z.string().nullable(),
    })
        .nullable(),
    main_photo: photoSchema.nullable(),
    creation_year: z.string().nullable(),
    sale_data: saleDataSchema.nullable(),
    estimate_price: z.number().nullable(),
    meta_description: z.string().nullable(),
    meta_image: z.string().nullable(),
    meta_title: z.string().nullable(),
    slug: z.string(),
    initial_price: z.number().nullable(),
    realized_price: z.number().nullable(),
    medium: z.array(classificationItemSchema$2),
    styles: z.array(classificationItemSchema$2),
    artists: z.array(z.object({
        id: z.number(),
        name: z.string(),
        slug: z.string(),
        status: z.nativeEnum(ArtistStatus),
        biography: editorBlockSchema.nullable(),
        verification_status: z.nativeEnum(ArtistVerificationStatus),
        date_of_birth: z.string().nullable(),
        date_of_death: z.string().nullable(),
        countries: z.array(z.object({
            id: z.number(),
            name: z.string(),
            slug: z.string(),
        })),
        photo: photoSchema.nullable(),
    })),
    curator_review: z
        .object({
        id: z.number(),
        description: editorBlockSchema.nullable(),
        curator: z.object({
            id: z.number(),
            slug: z.string(),
            first_name: z.string(),
            last_name: z.string(),
            position: z.string().nullable().optional(),
            photo: photoSchema.nullable(),
        }),
    })
        .nullable(),
    auction: z
        .object({
        id: z.string(),
    })
        .nullable(),
    sale_order: z
        .object({
        id: z.string().uuid(),
        status: z.nativeEnum(SaleOrderStatus),
        price: z.string(),
        price_formatted: z.number(),
        seller_fee_percent: z.number(),
        buyer_fee_percent: z.number(),
        storage_fee_usd: z.number(),
        secondary_market_royalty: z.number(),
        date_created: z.string(),
        realized_price_usd: z.number().nullable().optional(),
        storage_fee_value: z.string().nullable().optional(),
        storage_fee_value_formatted: z.number().nullable().optional(),
        currency: currencySchema.nullable(),
    })
        .nullable(),
    institutions: z.array(z.object({
        id: z.number(),
        name: z.string(),
        slug: z.string(),
        expert: z
            .object({
            id: z.number(),
            full_name: z.string(),
            role: z.string(),
            social_profiles: socialProfilesSchema,
            avatar: photoSchema.nullable(),
        })
            .nullable(),
    })),
})
    .nullable();
const validateArtAssetParams = z.object({
    id: z.string(),
});
const refuteManyArtAssetsBodyRequestSchema = z.object({
    ids: z.array(z.number()),
});
const makeReadyForSaleManyArtAssetsBodyRequestSchema = z.object({
    ids: z.array(z.number()),
});

const readArtistWalletAuthorizationsParamsSchema = z.object({
    id: z.string(),
    ip: z.string(),
});
const readArtistWalletAuthorizationDataResponseSchema = z.object({
    artist_name: z.string(),
    institution_id: z.number(),
    connected_wallet: z.string().nullable(),
    expiration_date: z.string().nullable(),
});
const unauthorizeArtistWalletParamsSchema$1 = z.object({
    id: z.string().uuid(),
});

const auctionArtistShareSchema = z.object({
    artist_id: z.number().int(),
    share: z.number().nonnegative().max(100).min(0.01),
});
const estimateAuctionParamsSchema$1 = z.object({
    art_asset_id: z.number().int(),
    initial_price: z.string(),
    currency: z.string(),
    artists_shares: z.array(auctionArtistShareSchema),
});
const auctionEstimationShareSchema = z.object({
    wallet: z.string().uuid(),
    percent: z.number(),
    equivalent: z.string(),
    equivalent_formatted: z.string(),
});
const auctionEstimationSchema$1 = z.object({
    bid: z.string(),
    bid_formatted: z.string(),
    bid_fee: z.string(),
    bid_fee_formatted: z.string(),
    storage_fee: z.string(),
    storage_fee_formatted: z.string(),
    buyer_fee_premium: z.string(),
    buyer_fee_premium_formatted: z.string(),
    seller_fee: z.string(),
    seller_fee_formatted: z.string(),
    seller_fee_percent: z.number(),
    seller_profit_value: z.string(),
    seller_profit_value_formatted: z.string(),
    buyer_fee_premium_percent: z.number(),
    secondary_market_royalty: z.number(),
    shares: z.array(auctionEstimationShareSchema),
    currency: currencySchema,
});
const readManyAuctionsSortColumnSchema = z
    .enum([
    'art_asset_name',
    'artist',
    'start_date',
    'duration',
    'status',
    'initial_bid_value',
    'best_bid',
    'date_updated',
    'date_created',
    'end_date',
])
    .default('date_updated');
const sortOrderSchema = z.enum(['asc', 'desc']).default('desc');
const readManyAuctionsQuerySchema$2 = z
    .object({
    statuses: arrayStringsOption$3.optional(),
    exclude_auctions: arrayStringsOption$3.optional(),
    artists: arrayIdsOption$2.optional(),
    artists_slugs: arrayStringsOption$3.optional(),
    visible: booleanOptionSchema.optional(),
    sort_column: readManyAuctionsSortColumnSchema,
    sort_order: sortOrderSchema,
    search: z.string().min(2).optional(),
    institution_id: z.union([z.number().int(), z.string().transform((val) => parseInt(val, 10))]).optional(),
})
    .merge(paginationQuerySchema);
const readAuctionsTotalCountParamsSchema = readManyAuctionsQuerySchema$2.pick({
    institution_id: true,
    search: true,
    statuses: true,
    exclude_auctions: true,
    artists: true,
    artists_slugs: true,
    visible: true,
});
const auctionListItemSchema$2 = z.object({
    id: z.string().uuid(),
    art_asset: z.object({
        id: z.number().int(),
        slug: z.string(),
        name_english: z.string(),
        name_original: z.string().optional().nullable(),
        art_asset_type: z.nativeEnum(ArtAssetType),
        format: z.nativeEnum(ArtAssetFormat),
        content_flags: z.array(z.nativeEnum(ArtAssetContentFlags)),
        original_units: z.string(),
        original_size_x: z.number(),
        original_size_y: z.number(),
        original_size_z: z.number().nullable(),
        creation_year: z.string().nullable(),
        main_photo: photoSchema.nullable().optional(),
        artists: z
            .array(z.object({
            id: z.number().int(),
            status: z.nativeEnum(ArtistStatus),
            slug: z.string().nullable(),
            name: z.string(),
            pseudonym: z.string().nullable().optional(),
            name_transliteration: z.string().nullable().optional(),
            photo: photoSchema.nullable(),
        }))
            .default([]),
        styles: z
            .array(z.object({
            id: z.number().int(),
            name: z.string(),
            slug: z.string(),
        }))
            .default([]),
        medium: z
            .array(z.object({
            id: z.number().int(),
            name: z.string(),
            slug: z.string(),
        }))
            .default([]),
        featured: z.boolean(),
        digital_common: z
            .object({
            id: z.number(),
            preview: z.string().nullable(),
            file: z.string().nullable(),
            filesize: z.string().nullable(),
            filename_disk: z.string().nullable(),
            type: z.string().nullable(),
        })
            .nullable(),
    }),
    start_date: z.coerce.date().optional().nullable(),
    end_date: z.coerce.date().optional().nullable(),
    duration: z.number().int(),
    lot_id: z.number().int(),
    status: z.nativeEnum(AuctionStatus),
    on_chain_id: z.string().nullable(),
    initial_bid_value: z.string(),
    initial_bid_value_formatted: z.number(),
    currency: currencySchema,
    best_bid: z
        .object({
        id: z.string().uuid(),
        value: z.string(),
        value_formatted: z.number(),
        bidder_wallet: z.string(),
    })
        .optional()
        .nullable(),
    bids: z
        .object({
        data: z.array(z.object({
            id: z.string().uuid(),
            value: z.string(),
            value_formatted: z.number(),
            bidder_wallet: z.string(),
        })),
        count: z.number(),
        wallets: z.array(z.string()),
    })
        .nullable(),
    date_updated: z.coerce.date(),
    viewed: z.boolean(),
});
const auctionListSchema$1 = z.object({
    data: z.array(auctionListItemSchema$2),
    total: z.number().int(),
});
const ClassificationWithArtAssetIdSchema = classificationItemSchema$2.merge(z.object({ art_assets_id: z.number() }));
const readAuctionSharesResponseSchema$1 = z.object({
    value: z.string(),
    value_formatted: z.string(),
    currency: currencySchema,
    secondary_market_royalty: z.number(),
    shares: z.array(z.object({
        id: z.string().uuid(),
        percent: z.number(),
        value: z.string(),
        value_formatted: z.string(),
        wallet: z.object({
            id: z.string().uuid(),
            address: z.string(),
            artist: z.object({ id: z.number().int(), name: z.string() }).optional().nullable(),
            institution: z.object({ id: z.number().int(), name: z.string() }).optional().nullable(),
            verified: z.boolean(),
            is_platform_address: z.boolean(),
        }),
    })),
});
const readAuctionSharesByArtAssetIdParamsSchema$1 = z.object({
    art_asset_id: integerQueryParamSchema,
});
const readLatestAuctionBidParamsSchema = z.object({
    auctionId: z.string().uuid(),
});
const readLatestAuctionBidResponseSchema = z
    .object({
    id: z.string().uuid(),
    value: z.string(),
    value_formatted: z.number(),
    bidder_wallet: z.string(),
})
    .nullable();
const createAuctionShareParamsSchema$1 = z.object({
    wallet: z.string().uuid(),
    percent: z.number().positive().max(100).min(0.01),
});
const createAuctionParamsDtoSchema = z.object({
    initial_bid_value: z.string(),
    currency: z.string(),
    bid_step_value: z.string(),
    storage_fee_value: z.string(),
    duration: z.number().int(),
    art_asset: z.number().int(),
    secondary_market_royalty: z.number().nonnegative(),
    seller_fee_percent: z.number().nonnegative(),
    buyer_fee_premium_percent: z.number().nonnegative(),
    autocomplete: z.boolean(),
    auction_shares: z.object({
        create: z.array(createAuctionShareParamsSchema$1),
    }),
});
const createAuctionResponseSchema$1 = z.object({
    id: z.string().uuid(),
});
const updateAuctionParamsDtoSchema = z.object({
    initial_bid_value: z.string().optional(),
    bid_step_value: z.string().optional(),
    duration: stringOrInt.optional(),
    art_asset: z.number().int().optional(),
    storage_fee_value: z.string().optional(),
    seller_fee_percent: z.number().optional(),
    buyer_fee_premium_percent: z.number().optional(),
    secondary_market_royalty: z.number().optional(),
    autocomplete: z.boolean().optional(),
    auction_shares: z
        .object({
        create: z.array(createAuctionShareParamsSchema$1).optional(),
        delete: z.array(z.string().uuid()).optional(),
    })
        .optional(),
});
const approveAuctionRunParamsSchema$1 = z.object({
    auctionId: z.string().uuid(),
});
const auctionRunApprovalSchema$1 = z.object({
    struct: z.object({
        auctionId: z.string(),
        tokenId: z.number().int(),
        tokenURI: z.string(),
        price: z.string(),
        fee: z.string(),
        step: z.string(),
        endTime: z.number().int(),
        participants: z.array(z.string()),
        shares: z.array(z.string()),
        deadline: z.number().int(),
    }),
    signature: z.string(),
});
const deleteAuctionParamsSchema = z.object({
    auctionId: z.string().uuid(),
});
const readOneAuctionQuerySchema = z.object({
    institution_id: z.union([z.number().int(), z.string().transform((val) => parseInt(val, 10))]).optional(),
});
const auctionMarkViewedQuerySchema = z.object({
    institution_id: z.union([z.number().int(), z.string().transform((val) => parseInt(val, 10))]),
});
const readOneAuctionRequestQuerySchema = z.object({
    visible: booleanOptionSchema.optional(),
});
const readOneAuctionParamsSchema$2 = z.object({
    id: z.string(),
});
const auctionArtAssetdigitalOriginalSchema = z.object({
    id: z.number(),
    mpx: z.number(),
    exif: exifSchema,
});
const auctionArtAssetDigitalCommonSchema = z.object({
    id: z.number(),
    preview: z.string().nullable(),
    file: z.string().nullable(),
    filesize: z.string().nullable(),
    filename_disk: z.string().nullable(),
    type: z.string().nullable(),
});
const auctionArtAssetArtistSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    pseudonym: z.string().optional(),
    name_transliteration: z.string(),
    status: z.nativeEnum(ArtistStatus),
    photo: photoSchema.optional().nullable(),
});
const auctionArtAssetSchema = z.object({
    id: z.number().int(),
    name_english: z.string(),
    name_original: z.string().optional().nullable(),
    art_asset_type: z.nativeEnum(ArtAssetType),
    format: z.nativeEnum(ArtAssetFormat),
    content_flags: z.array(z.nativeEnum(ArtAssetContentFlags)),
    creation_year: z.string().nullable(),
    main_photo: photoSchema.nullable().optional(),
    token_id: z.number().optional().nullable(),
    digital_original: auctionArtAssetdigitalOriginalSchema.nullable(),
    digital_common: auctionArtAssetDigitalCommonSchema.nullable(),
    artists: z.array(auctionArtAssetArtistSchema),
});
const readOneAuctionResponseBodySchema$1 = z.object({
    id: z.string().uuid(),
    lot_id: z.number().int(),
    on_chain_id: z.string(),
    bid_step_value: z.string(),
    buyer_fee_premium_percent: z.number(),
    currency: currencySchema,
    start_date: z.coerce.date().nullable().optional(),
    duration: z.string(),
    initial_bid_value: z.string(),
    secondary_market_royalty: z.number(),
    seller_fee_percent: z.number(),
    status: z.nativeEnum(AuctionStatus),
    storage_fee_value: z.string(),
    auction_shares: z.array(z.object({
        id: z.string().uuid(),
        percent: z.number(),
        wallet: z.object({
            id: z.string().uuid(),
            address: z.string(),
            artist_id: z.number().int().optional().nullable(),
            institution_id: z.number().int().optional().nullable(),
            is_platform_address: z.boolean(),
        }),
    })),
    best_bid: z
        .object({
        id: z.string().uuid(),
        value: z.string(),
        value_formatted: z.number(),
        bidder_wallet: z.string(),
    })
        .optional()
        .nullable(),
    art_asset: auctionArtAssetSchema,
    bids_count: z.number(),
    bids_wallets: z.array(z.string()),
    page_viewed: z.boolean(),
    date_created: z.string(),
    date_updated: z.string().nullable(),
});
const readOneAuctionByOnChainIdParamsSchema$1 = z.object({
    onChainId: z.string(),
});

const readOneDocumentResponseSchema = z.object({
    id: z.number(),
    name: z.string(),
    date_created: z.date(),
    date_updated: z.date().nullable(),
    date_signed: z.date().nullable(),
    type: z.string(),
    subtype: z.string().nullable(),
    docusign_url: z.string().nullable(),
    art_assets: z
        .array(z.object({
        id: z.number(),
        slug: z.string().nullable(),
        main_photo: photoSchema.nullable(),
        name_english: z.string().nullable(),
        name_original: z.string().nullable(),
        art_asset_type: z.nativeEnum(ArtAssetType),
        format: z.nativeEnum(ArtAssetFormat),
        content_flags: z.array(z.nativeEnum(ArtAssetContentFlags)).nullable(),
        artists: z
            .array(z.object({
            id: z.number(),
            name: z.string(),
            slug: z.string().nullable(),
        }))
            .nullable(),
    }))
        .nullable(),
    artists: z
        .array(z.object({
        id: z.number(),
        name: z.string(),
        slug: z.string().nullable(),
        photo: photoSchema.nullable(),
    }))
        .nullable(),
    files: z
        .array(z.object({
        id: z.string(),
        title: z.string(),
        filesize: z.coerce.number(),
        filename_download: z.string(),
    }))
        .nullable(),
});
const readManyDocumentsResponseSchema = z
    .object({
    id: z.number(),
    name: z.string(),
    date_created: z.date(),
    date_updated: z.date().nullable(),
    date_signed: z.date().nullable(),
    type: z.string(),
    subtype: z.string().nullable(),
    art_assets: z
        .array(z.object({
        id: z.number(),
        slug: z.string().nullable(),
        name_english: z.string().nullable(),
        name_original: z.string().nullable(),
        art_asset_type: z.nativeEnum(ArtAssetType),
        format: z.nativeEnum(ArtAssetFormat),
        content_flags: z.array(z.nativeEnum(ArtAssetContentFlags)).nullable(),
    }))
        .nullable(),
    artists: z
        .array(z.object({
        id: z.number(),
        name: z.string(),
        slug: z.string().nullable(),
    }))
        .nullable(),
})
    .array();
const artistOptionSchema = z.object({
    name: z.string(),
    id: z.number(),
    pseudonym: z.string().optional(),
    name_transliteration: z.string(),
});
const filterOptionsResponseSchema = z.object({
    artists: z.array(artistOptionSchema),
    types: z.array(z.string()),
    subtypes: z.array(z.string()),
    date_created: z.array(z.string()).optional(),
    date_signed: z.array(z.string()).optional(),
    items_total_count: z.number(),
    assigned: booleanOptionSchema.optional(),
});

const createOneGalleryRequestBodySchema$1 = z.object({
    name: z.string(),
    contacts: contactsSchema.optional(),
    social_profiles: socialProfilesSchema.optional(),
    locations: z.array(locationSchema).nullable().optional(),
});
const createOneGalleryResponseBodySchema$1 = z.object({
    id: z.number(),
});
const updateOneGalleryRequestBodySchema$1 = z
    .object({
    name: z.string(),
    biography: editorBlockSchema.nullable(),
    contacts: contactSchema.array().nullable(),
    social_profiles: socialProfileSchema.array().nullable(),
    locations: z.array(locationSchema).nullable(),
})
    .partial();
const updateOneGalleryResponseBodySchema$1 = z.object({
    id: z.number(),
});
const updateOneGalleryQuerySchema$1 = z.object({
    institution_id: z.coerce.number(),
});
const readOneGalleryResponseBodySchema$2 = z.object({
    id: z.number(),
    slug: z.string().nullable(),
    name: z.string(),
    biography: editorBlockSchema.nullable().optional(),
    contacts: contactsSchema.nullable().optional(),
    social_profiles: socialProfileSchema.array().nullable().optional(),
    locations: z.array(locationSchema).nullable().optional(),
});
const readOneGalleryQuerySchema$2 = z.object({
    institution_id: z.string(),
});
const galleryIdParamsSchema$2 = z.object({
    id: z.string(),
});

const percentSchema = z.number().min(0).max(100);
const createSharePayloadSchema$1 = z.object({
    wallet: z.string(),
    percent: percentSchema,
});
const createSaleOrderParamsSchema$1 = z.object({
    status: z.literal(SaleOrderStatus.Placed, { description: 'You can create orders with only "placed" status' }),
    currency: z.string().uuid(),
    price: z.string(),
    art_asset: z.number().int(),
    seller_fee_percent: z.number().nonnegative(),
    buyer_fee_percent: z.number().nonnegative(),
    storage_fee_usd: z.number().positive(),
    secondary_market_royalty: percentSchema,
    sale_order_shares: z.object({
        create: z.array(createSharePayloadSchema$1).min(1, 'Shares is required for sale order'),
    }, { required_error: 'Shares is required for sale order' }),
});
const artistShareSchema = z.object({
    artist_id: z.number().positive().int(),
    percent: percentSchema,
});
const estimateSaleOrderParamsSchema$1 = z.object({
    art_asset_id: z.number().int(),
    currency: z.string(),
    price: z.number().positive(),
    artist_shares: z.array(artistShareSchema).default([]),
});
const saleOrderEstimationSchema = z.object({
    price: z.string(),
    price_formatted: z.string(),
    fee: z.string(),
    fee_formatted: z.string(),
    storage_fee: z.string(),
    storage_fee_formatted: z.string(),
    seller_fee: z.string(),
    seller_fee_formatted: z.string(),
    buyer_fee_premium: z.string(),
    buyer_fee_premium_formatted: z.string(),
    seller_profit: z.string(),
    seller_profit_formatted: z.string(),
    secondary_market_royalty: z.number(),
    buyer_fee_premium_percent: z.number(),
    seller_fee_percent: z.number(),
    currency: currencySchema,
    shares: z.array(z.object({
        wallet: z.string(),
        percent: z.number(),
        equivalent: z.string(),
        equivalent_formatted: z.string(),
    })),
    create_payload: createSaleOrderParamsSchema$1,
});
const getSaleOrderPurchaseApproveParamsSchema$1 = z.object({
    sale_order_id: z.string().uuid(),
    buyer_address: z.string(),
});
const saleApprovalSchema = z.object({
    to: z.string(),
    tokenId: z.number().int(),
    tokenURI: z.string(),
    price: z.string(),
    participants: z.array(z.string()),
    shares: z.array(z.string()),
    deadline: z.number().int(),
    signature: z.string(),
    currency: currencySchema,
});
const readSaleOrderSharesResponseSchema$1 = z.object({
    value: z.string(),
    value_formatted: z.string(),
    currency: currencySchema,
    secondary_market_royalty: z.number(),
    shares: z.array(z.object({
        id: z.string().uuid(),
        percent: z.number(),
        value: z.string(),
        value_formatted: z.string(),
        wallet: z.object({
            id: z.string().uuid(),
            address: z.string(),
            artist: z.object({ id: z.number().int(), name: z.string() }).optional().nullable(),
            institution: z.object({ id: z.number().int(), name: z.string() }).optional().nullable(),
            verified: z.boolean(),
            is_platform_address: z.boolean(),
        }),
    })),
});
const readActualSaleOrderSharesParamsSchema$1 = z.object({
    art_asset_id: integerQueryParamSchema,
});

const getTokenDataParamsSchema = z.object({
    artAssetId: z.union([z.number().int(), z.string().transform((val) => Number.parseInt(val, 10))]),
});

const readInstitutionIncomesOrderSchema = z.enum([
    'block_timestamp',
    'artist',
    'art_asset.name_english',
    'price',
    'earnings',
    'tx_hash',
]);
const readInstitutionIncomesParamsSchema = z.object({
    institution_id: z.union([z.number().int(), z.string().transform((val) => parseInt(val, 10))]),
});
const readInstitutionIncomesQuerySchema = z
    .object({
    from_timestamp: z.union([z.number().int(), z.string().transform((val) => parseInt(val, 10))]).optional(),
    to_timestamp: z.union([z.number().int(), z.string().transform((val) => parseInt(val, 10))]).optional(),
    sort_column: readInstitutionIncomesOrderSchema.default('block_timestamp'),
    sort_order: orderSchema$1.default('desc'),
    search: z.string().optional().nullable(),
})
    .merge(paginationQuerySchema);
const readInstitutionTransactionCountQuerySchema = z.object({
    from_timestamp: z.union([z.number().int(), z.string().transform((val) => parseInt(val, 10))]).optional(),
    to_timestamp: z.union([z.number().int(), z.string().transform((val) => parseInt(val, 10))]).optional(),
    search: z.string().optional().nullable(),
});
const institutionIncomeShareSchema = z.object({
    institution_id: z.number().int().optional().nullable(),
    artist_id: z.number().int().optional().nullable(),
    is_platform_address: z.boolean(),
    percent: z.number(),
    amount: z.string(),
    amount_formatted: z.string(),
});
const institutionIncomeItemSchema = z.object({
    id: z.string(),
    tx_hash: z.string(),
    kind: z.nativeEnum(TokenHistoryRecordKind),
    block_timestamp: z.number(),
    block_number: z.number(),
    earnings: z.string(),
    earnings_formatted: z.string(),
    price: z.string(),
    price_formatted: z.string(),
    buyer: evmAddressSchema,
    wallet: z.object({
        id: z.string(),
        address: evmAddressSchema,
    }),
    art_asset: z.object({
        id: z.number().int(),
        name_english: z.string(),
        name_original: z.string().optional().nullable(),
        art_asset_type: z.nativeEnum(ArtAssetType),
        format: z.nativeEnum(ArtAssetFormat),
        artists: z.array(z.object({
            id: z.number().int(),
            name: z.string(),
        })),
        main_photo: photoSchema.nullable(),
    }),
    currency: currencySchema,
    details: z.object({
        shares: z.array(institutionIncomeShareSchema),
        secondary_market_royalty: z.number().int(),
    }),
});
const institutionIncomesListSchema = z.object({
    items: z.array(institutionIncomeItemSchema),
    total: z.number().int(),
});
const readInstitutionTotalIncomeParamsSchema = z.object({
    institution_id: z.union([z.number().int(), z.string().transform((val) => parseInt(val, 10))]),
});
const institutionTotalIncomeSchema$1 = z.object({
    wallet: z.object({
        id: z.string().uuid(),
        address: evmAddressSchema,
    }),
    rewards: z.array(z.object({
        currency: currencySchema,
        amount: z.string(),
        amount_formatted: z.string(),
    })),
    balances: z.array(z.object({
        currency: currencySchema,
        amount: z.string(),
        amount_formatted: z.string(),
    })),
});
const readInstitutionTotalCountQuerySchema = z.object({
    institution_id: z.union([z.number().int(), z.string().transform((val) => parseInt(val, 10))]),
});

const userSchema$1 = z.object({
    id: z.string(),
    first_name: z.string(),
    last_name: z.string(),
});
z.object({
    id: z.number(),
    data: z.record(z.any()).nullable(),
    delta: z.record(z.any()).nullable(),
    collection: z.string(),
    item: z.union([z.string(), z.number()]),
    activity: z.object({
        action: z.string(),
        ip: z.string(),
        user_agent: z.string(),
        origin: z.string(),
        timestamp: z.string(),
        user: userSchema$1.nullable(),
    }),
    child_revisions: z.array(z.record(z.any())),
});
const changeSchema$1 = z.object({
    removed: z.boolean().optional(),
    added: z.boolean().optional(),
    value: z.any(),
});
const changedFieldSchema$1 = z.object({
    name: z.string(),
    changes: z.array(changeSchema$1),
});
const logEntrySchema$1 = z.object({
    action: z.string(),
    user: userSchema$1.nullable(),
    date: z.string(),
    fields: z.array(changedFieldSchema$1),
});
const readOneActivityResponseSchema$1 = z.array(logEntrySchema$1);
const readOneActivityQuerySchema$1 = z.object({
    item: z.string(),
    collection: z.string(),
});

const booleanOption$1 = z.enum(['true', 'false']).transform((v) => v === 'true');
const arrayStringsOption$1 = z.string().transform((val) => val.split(',').map((v) => v));
const createUserNotificationsRequestBodySchema = z.object({
    institutions_directus_users_ids: z.array(z.number()),
    item_id: z.union([z.string(), z.number()]),
    type: z.string(),
    collection: z.string(),
    payload: z.record(z.any()),
});
const readUserNotificationsRequestQuerySchema = z.object({
    institution_id: z.union([z.number().int(), z.string().transform((val) => parseInt(val, 10))]),
    collection: z.string().optional(),
});
const updateNotificationsToViewedRequestBodySchema = z.object({
    user_id: z.string().uuid(),
    item_id: z.string().optional(),
    collection: z.string().optional(),
});
const readUserNotificationsResponseSchema = z.object({
    viewed: z.boolean(),
});
const readManyNotificationsRequestQuerySchema = z
    .object({
    viewed: booleanOption$1.optional(),
    user_id: z.string().uuid().optional(),
    item_id: z.string().optional(),
    collection: z.string().optional(),
    types: arrayStringsOption$1.optional(),
    column: z.enum(['id', 'collection', 'type', 'date_created', 'date_updated']).optional().default('date_created'),
    order: orderSchema$1.optional().default('asc'),
})
    .merge(paginationQuerySchema);
const readNotificationsTotalCountRequestQuerySchema = z.object({
    viewed: booleanOption$1.optional(),
    user_id: z.string().uuid().optional(),
    item_id: z.string().optional(),
    collection: z.string().optional(),
    types: arrayStringsOption$1.optional(),
    column: z.enum(['id', 'collection', 'type', 'date_created', 'date_updated']).optional().default('date_created'),
    order: orderSchema$1.optional().default('asc'),
});

const createDomainParamsSchema = z.object({
    domain: z.string().trim(),
    clientId: z.string(),
});
const DomainStatus = {
    DnsLookupFailed: 'DNS_LOOKUP_FAILED',
    DnsRecordMismatch: 'DNS_RECORD_MISMATCH',
    HealthcheckFailed: 'HEALTHCHECK_FAILED',
    Healthy: 'HEALTHY',
};
const domainStatusEnum = {
    DNS_LOOKUP_FAILED: 'DNS_LOOKUP_FAILED',
    DNS_RECORD_MISMATCH: 'DNS_RECORD_MISMATCH',
    HEALTHCHECK_FAILED: 'HEALTHCHECK_FAILED',
    HEALTHY: 'HEALTHY',
};
const domainDtoSchema = z.object({
    id: z.string(),
    domain: z.string().trim(),
    clientId: z.string(),
    status: z.nativeEnum(domainStatusEnum),
});
const readDomainByClientIdParamsSchema = z.object({
    clientId: z.string(),
});
const readDomainByValueParamsSchema = z.object({
    domain: z.string().trim(),
});
const dnsConfigDtoSchema = z.object({
    ipAddress: z.string(),
});
const deleteDomainByClientIdParamsSchema = readDomainByClientIdParamsSchema;

var index$5 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    createDomainParamsSchema: createDomainParamsSchema,
    DomainStatus: DomainStatus,
    domainStatusEnum: domainStatusEnum,
    domainDtoSchema: domainDtoSchema,
    readDomainByClientIdParamsSchema: readDomainByClientIdParamsSchema,
    readDomainByValueParamsSchema: readDomainByValueParamsSchema,
    dnsConfigDtoSchema: dnsConfigDtoSchema,
    deleteDomainByClientIdParamsSchema: deleteDomainByClientIdParamsSchema
});

const institutionsIdParamsSchema = z.object({
    institution_id: z.union([z.number().int(), z.string().transform((v) => parseInt(v, 10))]),
});
const institutionsSlugParamsSchema = z.object({
    institution_slug: z.string(),
});
const institutionsAddStoreDomainParamsSchema$1 = z.object({
    domain: z.string().trim(),
});
const institutionStoreDomainDtoSchema$2 = domainDtoSchema.omit({ clientId: true });
const readInstitutionByDomainParamsSchema = z.object({
    domain: z.string().trim(),
});
const institutionStoreDnsConfigDtoSchema$1 = dnsConfigDtoSchema;
const updateInstitutionStoreFaviconParamsSchema = institutionsIdParamsSchema;
const updateInstitutionStoreFaviconRequestBodySchema$1 = z.object({
    file_id: z.string(),
});
const institutionSiteSchema = z.object({
    id: z.number().int(),
    status: z.nativeEnum(SiteStatus),
    favicon_html: z.string().nullable().optional(),
    open_graph: z
        .object({
        width: z.number().int(),
        height: z.number().int(),
        url: z.string(),
    })
        .nullable()
        .optional(),
});
const institutionDtoSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    slug: z.string().optional().nullable(),
    slug_approved: z.boolean().nullable().optional(),
    status: z.nativeEnum(Status),
    invite_status: z.nativeEnum(InviteStatus),
    type: z.nativeEnum(InstitutionType),
    logo: photoSchema.nullable().optional(),
    description: editorBlockSchema.nullable().optional(),
    site: z.union([z.number().int(), institutionSiteSchema]).nullable().optional(),
});
const createInstitutionSubscription = z.object({
    email: z.string().email(),
});
const restrictInstitutionRequestBodySchema = z.object({
    restriction_reason: z.string(),
});
const readInstitutionProfileByUserParamsSchema = z.object({
    user_id: z.string(),
});
const readInstitutionProfileByUserResponseSchema = z.object({
    id: z.number().int(),
    status: z.nativeEnum(Status),
    verification_status: z.nativeEnum(InstitutionVerificationStatus),
    slug: z.string(),
    slug_approved: z.boolean(),
    name: z.string(),
    tagline: z.string().nullable(),
    type: z.nativeEnum(InstitutionType),
    description: editorBlockSchema.optional().nullable(),
    locations: locationSchema.optional().nullable(),
    logo: photoSchema.optional().nullable(),
    date_created: z.string(),
    date_updated: z.string(),
    social_profiles: socialProfilesSchema,
    contacts: contactsSchema,
    wallet: z.string().nullable().optional(),
    wizard_finished: z.boolean(),
    is_suspicious: z.boolean(),
    associated_artist: z.number().nullable(),
    associated_gallery: z.number().nullable(),
    associated_curator: z.number().nullable(),
    associated_collector: z.number().nullable(),
    associated_agency: z.number().nullable(),
});
const readInstitutionClassificationsParamsSchema$1 = z.object({
    id: z
        .string()
        .transform((v) => Number(v))
        .pipe(z.number()),
});
const readInstitutionClassificationsQueryParamsSchema$1 = z.object({
    limit: z
        .string()
        .transform((v) => Number(v))
        .pipe(z.number()),
    status: z.nativeEnum(Status),
    token_status: arrayStringsOption$3.optional(),
    auctions_visible: booleanOptionSchema,
});
const readInstitutionClassificationsResponseSchema$1 = z.object({
    styles: z.array(classificationItemSchema$2),
    mediums: z.array(classificationItemSchema$2),
});

const inviteInstitutionUserParamsSchema = z.object({
    first_name: z.string(),
    last_name: z.string(),
    email: z.string(),
    phone: z.string(),
    role: z.nativeEnum(InstitutionUserRole),
    institution_id: z.number(),
    directus_role_id: z.string(),
});
const institutionUserAcceptInviteParamsSchema = z.object({
    password: passwordSchema,
    invitation_token: z.string(),
});
const institutionUserResendInviteParamsSchema = z.object({
    institution_user_id: z.union([z.number().int(), z.string().transform(Number)]),
});
const institutionUserDtoSchema = z.object({
    id: z.number(),
    directus_user_id: z.string(),
    institution_id: z.number(),
    role: z.nativeEnum(InstitutionUserRole),
});
const institutionUserReadOneParamsSchema = z.object({
    institution_user_id: z.union([z.number().int(), z.string().transform(Number)]),
});
const institutionUserReadOneQuerySchema = z.object({
    institution_id: z.string(),
});
const institutionUserListItemDtoSchema = z.object({
    id: z.number(),
    role: z.nativeEnum(InstitutionUserRole),
    email: z.string(),
    phone: z.string().nullable(),
    date_created: z.string(),
    status: z.nativeEnum(UserStatus),
    first_name: z.string().optional().nullable(),
    last_name: z.string().optional().nullable(),
});
const institutionUserSignUpRequestBodySchema = z.object({
    name: z.string(),
    country: z.number(),
    type: z.nativeEnum(InstitutionType),
    creator: z.object({
        firstName: z.string(),
        lastName: z.string(),
        email: z.string().email({ message: 'Registration with this email is impossible. Please try a different email' }),
        password: strongPasswordSchema,
    }),
});
const institutionUserSortField = z.enum(['role', 'email', 'date_created', 'name', 'phone', 'status']);
const readInstitutionUsersListQuerySchema = z
    .object({
    institution_id: z.union([z.number().int(), z.string().transform(Number)]),
    sort_field: institutionUserSortField.default('date_created'),
    sort_order: z.enum(['asc', 'desc']).default('desc'),
})
    .merge(paginationQuerySchema);
const institutionUsersListDtoSchema = z.object({
    total: z.number(),
    items: z.array(institutionUserListItemDtoSchema),
});
const institutionUserSuspendParamsSchema = z.object({
    institution_user_id: z.union([z.number().int(), z.string().transform(Number)]),
});
const institutionUserRevokeParamsSchema = z.object({
    institution_user_id: z.union([z.number().int(), z.string().transform(Number)]),
});
const institutionUserUpdateParamsSchema = z.object({
    institution_user_id: z.union([z.number().int(), z.string().transform(Number)]),
});
const institutionUserUpdateBodySchema = z
    .object({
    first_name: z.string(),
    last_name: z.string(),
    email: z.string(),
    phone: z.string(),
    role: z.nativeEnum(InstitutionUserRole),
})
    .partial();
const institutionUserDeleteParamsSchema = z.object({
    institution_user_id: z.union([z.number().int(), z.string().transform(Number)]),
});

const institutionSubscriptionDtoSchema = z.object({
    id: z.number(),
    institution_id: z.number(),
    email: z.string().email(),
});
const institutionSubscriptionSortField = z.enum(['email', 'date_created']);
const readInstitutionSubscriptionsListQuerySchema = z
    .object({
    sort_field: institutionSubscriptionSortField.default('date_created'),
    sort_order: z.enum(['asc', 'desc']).default('desc'),
    email_search: z.string().optional(),
})
    .merge(paginationQuerySchema);
const institutionSubscriptionListItemDtoSchema = z.object({
    id: z.number(),
    email: z.string(),
    unsubscribe_hash: z.string(),
    date_created: z.string(),
});
const institutionSubscriptionsListDtoSchema = z.object({
    total: z.number(),
    items: z.array(institutionSubscriptionListItemDtoSchema),
});
const institutionSubscriptionDeleteOneParamsSchema$1 = z.object({
    unsubscribe_hash: z.string(),
});
const institutionSubscriptionsExportParamsSchema = z.object({
    institution_id: z.string(),
});
const institutionSubscriptionExportDataSchema = z.object({
    data: z.array(z.string().email()),
});
const importInstitutionSubscriptionsRequestBodySchema$1 = z.object({
    emails: z.array(z.string()).min(1),
    institution_id: z.number(),
});
const importInstitutionSubscriptionsResponseSchema$1 = z.object({
    skipped: z.array(z.object({ email: z.string(), reason: z.string() })),
});

const tilesFormatSchema = z.enum(['jpeg', 'png', 'webp']);
const readTilesByBasenameParamsSchema = z.object({
    basename: z.string(),
});
const tileDtoSchema$1 = z.object({
    id: z.string(),
    label: z.string(),
    clientId: z.string(),
    format: tilesFormatSchema,
    basename: z.string(),
});
const tilesListSchema$3 = z.array(tileDtoSchema$1);

const tilesTaskSchema = z.object({
    clientId: z.string(),
    basename: z.string(),
    imgLink: z.string(),
    label: z.string(),
    force: z.boolean().optional(),
    format: tilesFormatSchema.optional(),
});

var index$4 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    tilesTaskSchema: tilesTaskSchema,
    tilesFormatSchema: tilesFormatSchema,
    readTilesByBasenameParamsSchema: readTilesByBasenameParamsSchema,
    tileDtoSchema: tileDtoSchema$1,
    tilesListSchema: tilesListSchema$3
});

const readTilesByFileIdParamsSchema$2 = z.object({
    id: z.string(),
});
const tileDtoSchema = z.object({
    entity_type: z.string(),
    entity_id: z.string(),
    format: tilesFormatSchema,
    basename: z.string(),
});
const tilesListSchema$2 = z.array(tileDtoSchema);

const requestSiweNonceParamsSchema$1 = z.object({
    address: evmAddressSchema,
});
const siweNonceDtoSchema$1 = z.object({
    nonce: z.string(),
    token: z.string(),
});
const siweNonceTokenPayloadSchema = z.object({
    address: evmAddressSchema,
    nonce: z.string(),
});
const institutionWalletRelationDtoSchema = z.object({ institution: z.number() });
const artistWalletRelationDtoSchema = z.object({ artist: z.number() });
const isInstitutionWalletRelation = (relation) => {
    return 'institution' in relation;
};
const isArtistWalletRelation = (relation) => {
    return 'artist' in relation;
};
const verifyWalletParamsSchema$1 = z.object({
    address: evmAddressSchema,
    signature: z.string(),
    message: z.string(),
    token: z.string(),
    relations: z.union([institutionWalletRelationDtoSchema, artistWalletRelationDtoSchema]),
});
const walletDtoSchema$1 = z.object({
    id: z.string().uuid(),
    address: z.string().regex(/^0x[a-fA-F0-9]{40}$/),
    nonce: z.string(),
    signed_message: z.string(),
    signature: z.string(),
    is_platform_address: z.boolean(),
    verified: z.boolean(),
    institutions: z.array(z.number()).length(1),
});
const suspendWalletParamsSchema = z.object({
    id: z.string().uuid(),
});

const artistFilterOption$1 = optionSchema.merge(z.object({ artists_count: z.number() }));
const readArtistFilterOptionsResponseSchema$2 = z.object({
    styles: z.array(artistFilterOption$1),
    countries: z.array(artistFilterOption$1),
    medium: z.array(artistFilterOption$1),
    items_total_count: z.number(),
    verification_statuses: z.array(z.nativeEnum(ArtistVerificationStatus)),
    publication_statuses: z.array(z.nativeEnum(ArtistStatus)),
    date_of_birth: minMaxSchema$1,
});
const artistFilterQuerySchema$1 = z.object({
    price: minMaxOptionSchema.optional(),
    statuses: arrayStringsOption$3.optional(),
    verification_statuses: arrayStringsOption$3.optional(),
    institutions: arrayIdsOption$2.optional(),
    artists: arrayIdsOption$2.optional(),
    mediums: arrayIdsOption$2.optional(),
    styles: arrayIdsOption$2.optional(),
    countries: arrayIdsOption$2.optional(),
    search: z.string().min(2).optional(),
    featured: booleanOptionSchema.optional(),
    character: z
        .string()
        .regex(/^[a-zA-Z]$/, 'Value must be a single alphabetic character')
        .optional(),
    artists_slugs: arrayStringsOption$3.optional(),
    mediums_slugs: arrayStringsOption$3.optional(),
    styles_slugs: arrayStringsOption$3.optional(),
    countries_slugs: arrayStringsOption$3.optional(),
    date_of_birth: minMaxOptionSchema.optional(),
    count_only_public_art_assets: booleanOptionSchema.optional(),
});
const ArtistSortColumn$1 = {
    Name: 'name',
    PortfolioValue: 'portfolio_value',
    ArtAssetsCount: 'art_assets_count',
    DateUpdated: 'date_updated',
    Country: 'country',
    DateOfBirth: 'date_of_birth',
};
const artistSortQuerySchema$1 = z.object({
    column: z.nativeEnum(ArtistSortColumn$1).optional().default(ArtistSortColumn$1.Name),
    order: orderSchema$1.optional().default(Order.Asc),
});
const artistSortLiteQuerySchema$1 = z.object({
    column: z.enum([ArtistSortColumn$1.Name, ArtistSortColumn$1.DateUpdated]).optional().default(ArtistSortColumn$1.Name),
    order: orderSchema$1.optional().default(Order.Asc),
});
const createArtistParamsSchema = z.object({
    name: z
        .string({
        errorMap: () => ({
            message: "The artist's name must be 2-100 symbols long.",
        }),
    })
        .min(2)
        .max(100),
    name_transliteration: z
        .string({
        errorMap: () => ({
            message: "The artist's name transliteration must be 2-100 symbols long.",
        }),
    })
        .min(2)
        .max(100),
    pseudonym: z
        .string({
        errorMap: () => ({
            message: "The artist's pseudonym must be 1-100 symbols long.",
        }),
    })
        .min(1)
        .max(100)
        .nullable()
        .optional(),
    name_aliases: z
        .string({
        errorMap: () => ({
            message: "The artist's name must be 2-100 symbols long.",
        }),
    })
        .min(2)
        .max(100)
        .nullable()
        .optional(),
    biography: editorBlockSchema.nullable().optional(),
    relationship_information: editorBlockSchema.nullable().optional(),
    gender: z.nativeEnum(Gender).optional(),
    birth_year: z.number().nullable().optional(),
    date_of_birth: z.string().nullable().optional(),
    date_of_death: z.string().nullable().optional(),
    artist_type: z.nativeEnum(ArtistType).optional().default(ArtistType.Individual),
    creation_year: z.number().nullable().optional(),
    disband_year: z.number().nullable().optional(),
    alive: z.boolean().optional(),
    contacts: contactsSchema.nullable().optional(),
    social_profiles: socialProfilesSchema.nullable().optional(),
    photo: z.string().nullable().optional(),
    photo_attribution: z.string().optional().nullable(),
    art_assets: z
        .array(z.union([z.string(), z.number()]), {
        errorMap: () => ({
            message: 'Please select related artworks',
        }),
    })
        .optional(),
    locations: z.array(locationSchema).optional().nullable(),
    groups: idArraySchema.optional(),
    individuals: idArraySchema.optional(),
    documents: idArraySchema.optional(),
    institution: z.number(),
});
const createArtistResponseSchema = z.object({
    id: z.number(),
});
const readManyArtistsQuerySchema$2 = artistSortQuerySchema$1
    .partial()
    .merge(paginationQuerySchema.partial())
    .merge(artistFilterQuerySchema$1.partial());
const readManyArtistsLiteQuerySchema$1 = artistSortLiteQuerySchema$1
    .partial()
    .merge(paginationQuerySchema.partial())
    .merge(artistFilterQuerySchema$1.pick({ institutions: true, artists: true }).partial());
const readManyArtistsResponseSchema$2 = z
    .object({
    id: z.number(),
    slug: z.string().nullable(),
    name: z.string(),
    status: z.nativeEnum(ArtistStatus),
    date_created: z.string(),
    date_updated: z.string().nullable(),
    portfolio_value: z.string(),
    featured: z.boolean(),
    verified: z.boolean(),
    verification_status: z.nativeEnum(ArtistVerificationStatus),
    viewed: z.boolean(),
    photo_attribution: z.string().nullable(),
    date_of_birth: z.coerce
        .string()
        .nullable()
        .transform((date) => (date ? new Date(date).toISOString() : null)),
    date_of_death: z.coerce
        .string()
        .nullable()
        .transform((date) => (date ? new Date(date).toISOString() : null)),
    countries: z
        .array(z.object({
        id: z.number(),
        name: z.string(),
        slug: z.string(),
    }))
        .nullable(),
    photo: photoSchema.nullable(),
    artist_type: z.nativeEnum(ArtistType),
    individuals: z
        .array(z.object({
        id: z.number(),
        slug: z.string().nullable(),
        name: z.string(),
        status: z.string(),
        photo: photoSchema.nullable(),
    }))
        .nullable(),
    groups: z
        .array(z.object({
        id: z.number(),
        slug: z.string().nullable(),
        name: z.string(),
        status: z.string(),
        photo: photoSchema.nullable(),
    }))
        .nullable(),
    art_assets_count: z.number(),
})
    .array();
const readManyArtistsLiteResponseSchema$1 = z
    .object({
    id: z.number(),
    slug: z.string().nullable(),
    name: z.string(),
    status: z.nativeEnum(ArtistStatus),
    date_created: z.string(),
    date_updated: z.string().nullable(),
    photo: photoSchema.nullable(),
    artist_type: z.nativeEnum(ArtistType),
})
    .array();
const readManyArtistsUFDAPhotosResponseSchema$1 = readManyArtistsLiteResponseSchema$1;
const readOneArtistResponseSchema$2 = z.object({
    id: z.number(),
    slug: z.string().nullable(),
    date_created: z.string(),
    date_updated: z.string().nullable(),
    name: z.string(),
    name_aliases: z.string().nullable(),
    contacts: contactsSchema,
    social_profiles: socialProfilesSchema,
    individuals: z.array(z.object({
        individuals_id: z
            .object({
            id: z.number(),
            slug: z.string().nullable(),
            name: z.string(),
            status: z.string(),
            photo: photoSchema.nullable(),
        })
            .nullable(),
    })),
    groups: z.array(z.object({
        groups_id: z
            .object({
            id: z.number(),
            slug: z.string().nullable(),
            name: z.string(),
            status: z.string(),
            photo: photoSchema.nullable(),
        })
            .nullable(),
    })),
    photo: photoSchema.nullable(),
    biography: editorBlockSchema.nullable(),
    gender: z.nativeEnum(Gender).nullable(),
    birth_year: z.number().nullable(),
    date_of_birth: z.string().nullable(),
    date_of_death: z.string().nullable(),
    locations: z.array(locationSchema).nullable(),
    status: z.nativeEnum(ArtistStatus),
    alive: z.boolean(),
    photo_attribution: z.string().nullable(),
    documents: z.array(z.object({
        documents_id: z.object({
            id: z.number(),
            name: z.string(),
            type: z.nativeEnum(DocumentType).or(z.string()),
            subtype: z.nativeEnum(DocumentSubtype).or(z.string()).nullable(),
        }),
    })),
    wallets: z.array(z.object({
        wallets_id: z.object({
            id: z.string(),
            address: z.string(),
            verified: z.boolean(),
        }),
    })),
});
const deleteOneArtistParamsSchema$1 = z.object({
    id: z.coerce.number(),
});
const portfolioValueRequestParamsSchema = z.object({
    artist_id: z.coerce.number(),
    institutions: arrayIdsOption$2.optional(),
    statuses: arrayStringsOption$3.optional(),
});
const valueStatisticsSchema$1 = z.object({
    value: z.number().nonnegative(),
    count: z.number().nonnegative(),
    percentage: z.number().nonnegative(),
});
const portfolioValueSchema = z.object({
    initialValue: z.number().nonnegative(),
    realized: valueStatisticsSchema$1,
    unrealized: valueStatisticsSchema$1,
    total: valueStatisticsSchema$1,
    growth: valueStatisticsSchema$1.partial(),
});
const artistUnpublishParamsSchema$1 = z.object({
    id: z.string(),
});
const artistPublishParamsSchema$1 = z.object({
    id: z.string(),
});
const createNewArtAssetArtistSchema = z.object({
    name: z.string(),
    name_transliteration: z.string(),
    institution_id: z.number().int(),
});

const changeEmailRequestParamsSchema$1 = z.object({
    newEmail: z.string(),
    userId: z.string(),
    confirmationLink: z.string(),
});
const sendArtistReportMailRequestParamsSchema$1 = z.object({
    artist_id: z.number(),
    message: z.string(),
    email: z.string().email(),
});

const sendArtistReportSlackNotificationParamsSchema$1 = z.object({
    artist_id: z.number(),
    message: z.string(),
    email: z.string().email(),
});
const sendArtAssetReportSlackNotificationParamsSchema$1 = z.object({
    art_asset_id: z.number(),
    message: z.string(),
    email: z.string().email(),
});
const sendAuctionReportSlackNotificationParamsSchema$1 = z.object({
    auction_id: z.string(),
    message: z.string(),
    email: z.string().email(),
});
const sendContactUsReportRequestBodySchema = z.object({
    from: z.nativeEnum(ContactUsFrom),
    email: z.string().email(),
    inquiry_type: z.nativeEnum(ContactUsInquiryType).optional(),
    major: z.string().optional(),
    full_name: z.string(),
    phone: z.string().optional(),
    message: z.string().optional(),
    record_id: z.number(),
    captured_url: z.string(),
});

const readPhotoDataParamsSchema$2 = z.object({ id: z.string() });
const readPhotoResponseSchema$1 = photoSchema;

const readManyArtAssetsRequestsFiltersQuerySchema = z.object({
    institution_id: z.union([z.number().int(), z.string().transform((val) => parseInt(val, 10))]).optional(),
    art_assets: arrayIdsOption$2.optional(),
});
const ArtAssetsRequestsSortColumn = {
    DateCreated: 'date_created',
    DateUpdated: 'date_updated',
};
const artAssetsRequestsSortQuerySchema = z.object({
    column: z.nativeEnum(ArtAssetsRequestsSortColumn).optional().default(ArtAssetsRequestsSortColumn.DateUpdated),
    order: orderSchema$1.optional().default(Order.Asc),
});
const readManyArtAssetsRequestsQuerySchema$1 = artAssetsRequestsSortQuerySchema
    .and(readManyArtAssetsRequestsFiltersQuerySchema)
    .and(paginationQuerySchema);
const readManyArtAssetsRequestsCountParamsSchema = readManyArtAssetsRequestsFiltersQuerySchema;
const artAssetRequestListItemSchema = z.object({
    id: z.number(),
    status: z.nativeEnum(ArtworkRequestStatus),
    email: z.string(),
    name: z.string().nullable(),
    phone: z.string().nullable(),
    date_created: z.coerce.date(),
    art_asset: z.object({
        id: z.number().int(),
        name_english: z.string(),
        name_original: z.string().nullable(),
        art_asset_type: z.nativeEnum(ArtAssetType),
        format: z.nativeEnum(ArtAssetFormat),
        main_photo: photoSchema.nullable(),
        artists: z
            .array(z.object({
            id: z.number().int(),
            name: z.string(),
        }))
            .default([]),
    }),
    viewed: z.boolean(),
});
const artAssetRequestListSchema$1 = z.object({
    total: z.number().int(),
    data: z.array(artAssetRequestListItemSchema),
});
const createArtAssetPreOrderRequestBodySchema$1 = z.object({
    email: z.string().email(),
    art_asset: z.number(),
    name: z.string().trim(),
    phone: z
        .string()
        .trim()
        .optional(),
    captured_url: z.string(),
});

const createArtAssetMocksRequestBodySchema = z.object({
    data: z.object({
        dng: z.string(),
        photo: z.string(),
        artist_id: z.number(),
    }),
    user_info: z.object({
        user: z.string(),
    }),
});
const createArtistMockRequestBodySchema = z.object({
    data: z.object({
        wallet_address: z.string(),
        wallet_signature: z.string(),
        photo: z.string(),
        institution_id: z.number(),
        country_id: z.number(),
    }),
    user_info: z.object({
        user: z.string(),
    }),
});

const collectorBaseSchema = z.object({
    first_name: z.string(),
    last_name: z.string().nullable().optional(),
    nickname: z.string().nullable().optional(),
    photo: z.string().uuid().nullable().optional(),
    photo_attribution: z.string().nullable().optional(),
    date_of_birth: z.string().nullable(),
    gender: z.nativeEnum(Gender).nullable(),
    biography: editorBlockSchema.nullable().optional(),
});
const collectorIdParamsSchema$1 = z.object({
    id: z.string(),
});
const collectorQuerySchema = z.object({
    institution_id: z.string().optional(),
});
const updateOneCollectorRequestBodySchema$1 = collectorBaseSchema
    .extend({
    contacts: contactSchema.array().length(2),
    social_profiles: socialProfileSchema.array(),
    locations: z.array(locationSchema).nullable(),
})
    .partial();
const readOneCollectorResponseSchema = z.object({
    id: z.number(),
    slug: z.string(),
    first_name: z.string(),
    last_name: z.string().nullable(),
    nickname: z.string().nullable(),
    date_of_birth: z.string().nullable(),
    gender: z.nativeEnum(Gender).nullable(),
    photo: photoSchema.nullable(),
    photo_attribution: z.string().nullable(),
    biography: editorBlockSchema.nullable(),
    contacts: contactsSchema.nullable().optional(),
    social_profiles: socialProfilesSchema.nullable().optional(),
    locations: z.array(locationSchema).nullable().optional(),
    date_created: z.string(),
    date_updated: z.string().nullable(),
});
const readOneCollectorQuerySchema$1 = z.object({
    institution_id: z.string(),
});
const createOneCollectorRequestBodySchema = collectorBaseSchema.extend({
    contacts: contactsSchema.optional(),
    social_profiles: socialProfilesSchema.optional(),
    locations: z.array(locationSchema).nullable().optional(),
});
const createOneCollectorResponseSchema = z.object({
    id: z.number(),
});

const curatorBaseSchema = z.object({
    first_name: z.string(),
    last_name: z.string().nullable().optional(),
    nickname: z.string().nullable().optional(),
    position: z.string().nullable().optional(),
    photo: z.string().uuid().nullable().optional(),
    photo_attribution: z.string().nullable().optional(),
    date_of_birth: z.string().nullable().optional(),
    gender: z.nativeEnum(Gender).nullable(),
    biography: editorBlockSchema.nullable().optional(),
});
const curatorSchema = curatorBaseSchema.merge(z.object({
    contacts: contactsSchema.optional(),
    social_profiles: socialProfilesSchema.optional(),
    locations: z.array(locationSchema).nullable().optional(),
}));
const curatorIdParamsSchema = z.object({
    id: z.string(),
});
const curatorQuerySchema = z.object({
    institution_id: z.string().optional(),
});
const createOneCuratorRequestBodySchema$1 = curatorSchema.merge(z.object({
    institution: z.number(),
}));
const updateOneCuratorRequestBodySchema$1 = curatorSchema.partial();
const CuratorsSortColumn = {
    ReviewsCount: 'reviews_count',
};
const curatorsSortQuerySchema = z.object({
    column: z.nativeEnum(CuratorsSortColumn).optional().default(CuratorsSortColumn.ReviewsCount),
    order: orderSchema$1.optional().default(Order.Desc),
});
const curatorsFilterQuerySchema = z.object({
    search: z.string().min(2).trim().optional(),
    institution_id: z.string(),
    ids: z.array(z.number()).optional(),
});
const readManyCuratorsQuerySchema$2 = curatorsFilterQuerySchema
    .merge(curatorsSortQuerySchema)
    .merge(paginationQuerySchema);
const curatorListItemSchema = z.object({
    id: z.number(),
    slug: z.string(),
    first_name: z.string(),
    last_name: z.string(),
    position: z.string().nullable().optional(),
    photo: photoSchema.nullable(),
    reviews_count: z.number(),
});
const curatorsListSchema = z.array(curatorListItemSchema);
const readOneCuratorResponseSchema$1 = z.object({
    id: z.number(),
    slug: z.string(),
    first_name: z.string(),
    last_name: z.string().nullable(),
    nickname: z.string().nullable(),
    position: z.string().nullable().optional(),
    gender: z.nativeEnum(Gender).nullable(),
    photo: photoSchema.nullable(),
    photo_attribution: z.string().nullable(),
    date_of_birth: z.string().nullable(),
    biography: editorBlockSchema.nullable(),
    social_profiles: socialProfilesSchema.nullable().optional(),
    contacts: contactsSchema.nullable().optional(),
    locations: z.array(locationSchema).nullable().optional(),
    date_created: z.string(),
    date_updated: z.string().nullable(),
});

const readManyCountriesRequestQuerySchema$1 = z.object({
    search: z.string().optional(),
});
const readManyCitiesRequestQueryFiltersSchema$2 = z.object({
    country_id: z.string().optional(),
    search: z.string().optional(),
});
const readManyCitiesRequestPaginationQuerySchema = paginationQuerySchema;
const readManyCitiesRequestQuerySchema$2 = readManyCitiesRequestPaginationQuerySchema.merge(readManyCitiesRequestQueryFiltersSchema$2);

const readOneCuratorReviewRequestParamsSchema$2 = z.object({
    id: z.string(),
});
const readOneCuratorReviewRequestQuerySchema = z.object({
    institution_id: z.string(),
});
const readOneCuratorsReviewsResponseBodySchema$2 = z.object({
    id: z.number(),
    description: editorBlockSchema.nullable(),
    date_created: z.string(),
    date_updated: z.string().nullable(),
    curator: z.object({
        id: z.number(),
        slug: z.string(),
        first_name: z.string(),
        last_name: z.string(),
        position: z.string().nullable(),
        photo: photoSchema.nullable(),
    }),
    art_asset: z.object({
        id: z.number(),
        name_original: z.string().nullable(),
        name_english: z.string().nullable(),
        art_asset_type: z.nativeEnum(ArtAssetType),
        format: z.nativeEnum(ArtAssetFormat),
        content_flags: z.array(z.nativeEnum(ArtAssetContentFlags)),
    }),
});
const curatorsReviewsFilterQuerySchema = z.object({
    curators: arrayIdsOption$2.optional(),
    curators_slugs: arrayStringsOption$3.optional(),
    art_assets: arrayIdsOption$2.optional(),
    institution_id: z.string().optional(),
});
const CuratorsReviewsSortColumn = {
    DateCreated: 'date_created',
    DateUpdated: 'date_updated',
};
const curatorsReviewsSortQuerySchema = z.object({
    column: z.nativeEnum(CuratorsReviewsSortColumn).optional().default(CuratorsReviewsSortColumn.DateCreated),
    order: orderSchema$1.optional().default(Order.Asc),
});
const readManyCuratorsReviewsRequestQuerySchema$2 = curatorsReviewsFilterQuerySchema
    .merge(curatorsReviewsSortQuerySchema)
    .merge(paginationQuerySchema.partial());
const readManyCuratorsReviewsResponseBodySchema$2 = z.object({
    curators_reviews: z.array(readOneCuratorsReviewsResponseBodySchema$2),
    total_count: z.number(),
});
const createOneCuratorReviewRequestBodySchema$1 = z.object({
    curator: z.number(),
    art_asset: z.number(),
    description: editorBlockSchema.nullable().optional(),
    institution_id: z.number(),
});
const createOneCuratorReviewResponseBodySchema$1 = z.object({
    id: z.number(),
});
const updateOneCuratorReviewRequestParamsSchema$1 = z.object({
    id: z.string(),
});
const updateOneCuratorReviewRequestBodySchema$1 = z.object({
    description: editorBlockSchema.nullable().optional(),
    curator: z.number().optional(),
    institution_id: z.number().optional(),
    artAssetId: z.number().optional(),
});
const updateOneCuratorReviewResponseBodySchema$1 = createOneCuratorReviewResponseBodySchema$1;

const classificationsSchema$1 = z.array(z.object({ id: z.number(), name: z.string() }));
const readManyTagsRequestQuerySchema = z.object({
    institution_id: z
        .string()
        .transform((v) => Number(v))
        .optional(),
    name: z.string().optional(),
});
const createOneTagRequestBodySchema$1 = z.object({
    name: z.string(),
    institution_id: z.number().optional(),
});
const createOneTagResponseBodySchema$1 = z.object({ id: z.number() });
const createOneStyleRequestBodySchema$1 = z.object({
    name: z.string(),
    institution_id: z.number(),
});
const createOneStyleResponseSchema$1 = z.object({
    id: z.number(),
});
const createOneMediumRequestBodySchema$1 = z.object({
    name: z.string(),
    institution_id: z.number(),
});
const createOneMediumResponseSchema$1 = z.object({
    id: z.number(),
});

const createOneArtistsWaitlistItemParamsDtoSchema = z.object({
    first_name: z.string(),
    last_name: z.string(),
    pseudonym: z.string().nullable().optional(),
    email: z.string().email(),
    galleries: z.string().nullable().optional(),
    website_url: z.string().url().nullable().optional(),
    instagram_url: z.string().url().nullable().optional(),
    status: z.nativeEnum(ArtistsWaitlistItemStatus).optional().default(ArtistsWaitlistItemStatus.Pending),
});
const createArtistsWaitlistItemResponseSchema$1 = z.object({
    id: z.number(),
});

const newsAuthorSchema = z.object({
    id: z.number(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    avatar: photoSchema.nullable(),
});
const newsItemSchema = z.object({
    id: z.number(),
    slug: z.string().nullable(),
    status: z.nativeEnum(Status),
    header: z.string(),
    content: editorBlockSchema.nullable(),
    short_description: editorBlockSchema.nullable(),
    custom_publication_date: z.string().nullable(),
    cover: photoSchema.nullable(),
    publication_target: z.nativeEnum(NewsPublicationTarget),
    author: newsAuthorSchema.nullable(),
    date_created: z.string(),
    date_updated: z.string().nullable(),
});
const newsSlugParamsSchema = z.object({
    slug: z.string(),
});
const newsFilterSchema = z.object({
    search: z.string().min(2).optional(),
    statuses: arrayStringsOption$3.optional(),
    publication_target: z.nativeEnum(NewsPublicationTarget).default(NewsPublicationTarget.UFDA).optional(),
});
const NewsSortColumn = {
    Header: 'header',
    DateCreated: 'date_created',
    DateUpdated: 'date_updated',
    CustomPublicationDate: 'custom_publication_date',
};
const newsSortQuerySchema = z.object({
    column: z.nativeEnum(NewsSortColumn).optional().default(NewsSortColumn.CustomPublicationDate),
    order: orderSchema$1.optional().default(Order.Desc),
});
const readManyNewsQuerySchema$1 = newsFilterSchema
    .partial()
    .merge(newsSortQuerySchema)
    .merge(paginationQuerySchema);
const newsListSchema = z.array(newsItemSchema);

const readOneSelectionRequestParamsSchema$2 = z.object({
    slug: z.string(),
});
const readOneSelectionRequestQuerySchema$2 = z
    .object({
    institution_id: z.string(),
    token: z.string(),
    statuses: arrayStringsOption$3,
    restricted_access: booleanOptionSchema,
    visibility_statuses: arrayStringsOption$3,
})
    .partial();
const selectionArtAssetDigitalOriginalSchema = z.object({
    id: z.number(),
    mpx: z.number(),
    exif: exifSchema,
});
const selectionArtAssetArtistSchema$1 = z.object({
    id: z.number().int(),
    name: z.string(),
    pseudonym: z.string().nullable(),
    name_transliteration: z.string(),
    status: z.nativeEnum(ArtistStatus),
    photo: photoSchema.optional().nullable(),
    slug: z.string().nullable(),
});
const selectionArtAssetSchema$1 = z.object({
    id: z.number().int(),
    status: directusItemStatusSchema,
    name_english: z.string(),
    name_original: z.string().nullable(),
    slug: z.string().nullable(),
    art_asset_type: z.nativeEnum(ArtAssetType),
    format: z.nativeEnum(ArtAssetFormat),
    content_flags: z.array(z.nativeEnum(ArtAssetContentFlags)),
    creation_year: z.string().nullable(),
    physical_purchase_price: z.string().nullable(),
    estimate_price: z.string().nullable(),
    realized_price: z.string().nullable(),
    main_photo: photoSchema.nullable(),
    token_id: z.number().nullable(),
    token_status: z.nativeEnum(TokenStatus),
    digital_original: selectionArtAssetDigitalOriginalSchema.nullable(),
    artists: z.array(selectionArtAssetArtistSchema$1),
    original_size_x: z.number().nullable(),
    original_size_y: z.number().nullable(),
    original_size_z: z.number().nullable(),
    sale_order: artAssetSaleOrderInfoSchema.nullable(),
    auction: artAssetAuctionInfoSchema.nullable(),
});
const selectionUnpopulatedSchema = z.object({
    id: z.number(),
    art_assets: z.array(z.number()),
    slug: z.string().nullable(),
    status: z.string(),
    title: z.string(),
    description: editorBlockSchema.nullable(),
    access_token: z.string().nullable(),
    date_created: z.string(),
    date_updated: z.string().nullable(),
});
const readOneSelectionResponseBodySchema$2 = selectionUnpopulatedSchema.merge(z.object({ art_assets: z.array(selectionArtAssetSchema$1) }));
const readOneSelectionByAccessTokenRequestParamsSchema$1 = z.object({ token: z.string() });
const readOneSelectionByAccessTokenResponseBodySchema$1 = readOneSelectionResponseBodySchema$2;
const ReadManySelectionsRequestSortColumn = {
    Title: 'title',
    DateUpdated: 'date_updated',
    DateCreated: 'date_created',
    ArtAssetsCount: 'art_assets_count',
    Status: 'status',
};
const readManySelectionsRequestSortQuerySchema$2 = z.object({
    column: z
        .nativeEnum(ReadManySelectionsRequestSortColumn)
        .optional()
        .default(ReadManySelectionsRequestSortColumn.Title),
    order: orderSchema$1.optional().default(Order.Asc),
});
const readManySelectionsRequestFilterQuerySchema$2 = z.object({
    institution_id: z.string().optional(),
    statuses: arrayStringsOption$3.optional(),
    art_assets: arrayIdsOption$2.optional(),
    art_assets_slugs: arrayStringsOption$3.optional(),
    artists: arrayIdsOption$2.optional(),
    artists_slugs: arrayStringsOption$3.optional(),
    search: z.string().optional(),
    visibility_statuses: arrayStringsOption$3.optional(),
});
const readManySelectionsRequestQuerySchema$2 = readManySelectionsRequestSortQuerySchema$2
    .partial()
    .merge(paginationQuerySchema.partial())
    .merge(readManySelectionsRequestFilterQuerySchema$2.partial());
const selectionListItemDtoSchema = z.object({
    id: z.number(),
    slug: z.string(),
    status: z.nativeEnum(SelectionStatus),
    title: z.string(),
    date_created: z.string(),
    date_updated: z.string(),
    cover: z.string(),
    art_assets_count: z.number(),
});
const readManySelectionsListItemSchema = z.object({
    id: z.number(),
    slug: z.string(),
    status: z.nativeEnum(SelectionStatus),
    title: z.string(),
    date_created: z.string(),
    date_updated: z.string().nullable(),
    cover: photoSchema.nullable(),
    art_assets_count: z.number(),
});
const readManySelectionsResponseBodySchema$2 = z.array(readManySelectionsListItemSchema);
const createOneSelectionRequestBodySchema$1 = z.object({
    art_assets: z
        .array(z.union([z.string(), z.number()]), {
        errorMap: () => ({
            message: 'Please select related artworks.',
        }),
    })
        .optional(),
    status: z.nativeEnum(SelectionStatus, {
        errorMap: () => ({
            message: 'Please select a status of the selection.',
        }),
    }),
    title: z.string().nonempty('Please provide a title.'),
    description: editorBlockSchema.nullish(),
    institution_id: z.number(),
});
const createOneSelectionResponseBodySchema$1 = z.object({
    id: z.number(),
    access_token: z.string().nullable(),
    slug: z.string(),
});
const updateOneSelectionRequestParamsSchema$1 = z.object({ slug: z.string() });
const updateOneSelectionRequestBodySchema$1 = createOneSelectionRequestBodySchema$1.partial();
const updateOneSelectionResponseBodySchema$1 = createOneSelectionResponseBodySchema$1;

var index$3 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    phoneContactSchema: phoneContactSchema,
    emailContactSchema: emailContactSchema,
    contactSchema: contactSchema,
    contactsSchema: contactsSchema,
    socialProfileSchema: socialProfileSchema,
    socialProfilesSchema: socialProfilesSchema,
    createOneAgencyRequestBodySchema: createOneAgencyRequestBodySchema$1,
    createOneAgencyResponseBodySchema: createOneAgencyResponseBodySchema$1,
    updateOneAgencyRequestBodySchema: updateOneAgencyRequestBodySchema$1,
    updateOneAgencyResponseBodySchema: updateOneAgencyResponseBodySchema$1,
    updateOneAgencyQuerySchema: updateOneAgencyQuerySchema$1,
    readOneAgencyResponseBodySchema: readOneAgencyResponseBodySchema$2,
    readOneAgencyQuerySchema: readOneAgencyQuerySchema$2,
    agencyIdParamsSchema: agencyIdParamsSchema$2,
    artAssetSaleOrderInfoSchema: artAssetSaleOrderInfoSchema,
    artAssetAuctionInfoSchema: artAssetAuctionInfoSchema,
    artAssetSalesStatusSchema: artAssetSalesStatusSchema,
    tokenMetadataSchema: tokenMetadataSchema,
    artAssetFilterParamsSchema: artAssetFilterParamsSchema,
    artAssetFilterOptionsResponse: artAssetFilterOptionsResponse,
    sortSchema: sortSchema,
    artAssetsListQueryParametersSchema: artAssetsListQueryParametersSchema,
    artAssetListItemSchema: artAssetListItemSchema,
    readManyArtAssetsResponseSchema: readManyArtAssetsResponseSchema$1,
    readManyArtAssetsResponseSchemaNew: readManyArtAssetsResponseSchemaNew,
    artAssetListCountersSchema: artAssetListCountersSchema,
    readManyUFDAPhotosResponseBodySchema: readManyUFDAPhotosResponseBodySchema$1,
    readManyArtAssetsByOwnerWalletRequestQuerySchema: readManyArtAssetsByOwnerWalletRequestQuerySchema$1,
    readManyArtAssetsByOwnerWalletResponseBodySchema: readManyArtAssetsByOwnerWalletResponseBodySchema$1,
    readOneArtAssetRequestQuerySchema: readOneArtAssetRequestQuerySchema,
    similarArtAssetsFilterSchema: similarArtAssetsFilterSchema,
    similarArtAssetsListQueryParametersSchema: similarArtAssetsListQueryParametersSchema,
    similarArtAssetsParamsSchema: similarArtAssetsParamsSchema,
    similarArtAssetsQuerySchema: similarArtAssetsQuerySchema,
    createManyArtAssetsFromPhotosRequestBodySchema: createManyArtAssetsFromPhotosRequestBodySchema$1,
    createManyArtAssetsFromPhotosResponseBodySchema: createManyArtAssetsFromPhotosResponseBodySchema$1,
    createOneArtAssetFromPhotoRequestBodySchema: createOneArtAssetFromPhotoRequestBodySchema$1,
    createOneArtAssetFromPhotoResponseBodySchema: createOneArtAssetFromPhotoResponseBodySchema$1,
    createArtAssetAddArtistPayloadSchema: createArtAssetAddArtistPayloadSchema$1,
    isCreateArtAssetAddArtistPayload: isCreateArtAssetAddArtistPayload$1,
    createArtAssetNewArtistPayloadSchema: createArtAssetNewArtistPayloadSchema$1,
    isCreateArtAssetNewArtistPayloadSchema: isCreateArtAssetNewArtistPayloadSchema$1,
    createArtAssetArtistsPayloadSchema: createArtAssetArtistsPayloadSchema,
    createArtAssetParamsSchema: createArtAssetParamsSchema$1,
    createArtAssetResponseBodySchema: createArtAssetResponseBodySchema$1,
    updateArtAssetAddArtistPayloadSchema: updateArtAssetAddArtistPayloadSchema$1,
    isUpdateArtAssetAddArtistPayload: isUpdateArtAssetAddArtistPayload$1,
    updateArtAssetNewArtistPayloadSchema: updateArtAssetNewArtistPayloadSchema$1,
    isUpdateArtistAssetNewArtistPayloadSchema: isUpdateArtistAssetNewArtistPayloadSchema$1,
    updateArtAssetArtistsPayloadSchema: updateArtAssetArtistsPayloadSchema,
    updateOneArtAssetsParamsSchema: updateOneArtAssetsParamsSchema,
    updateOneArtAssetBodySchema: updateOneArtAssetBodySchema,
    artAssetStatusChangeParamsSchema: artAssetStatusChangeParamsSchema$1,
    deleteOneArtAssetsParamsSchema: deleteOneArtAssetsParamsSchema,
    reaArtAssetTotalCountResponseSchema: reaArtAssetTotalCountResponseSchema,
    readOneArtAssetResponseSchema: readOneArtAssetResponseSchema$1,
    validateArtAssetParams: validateArtAssetParams,
    refuteManyArtAssetsBodyRequestSchema: refuteManyArtAssetsBodyRequestSchema,
    makeReadyForSaleManyArtAssetsBodyRequestSchema: makeReadyForSaleManyArtAssetsBodyRequestSchema,
    readArtistWalletAuthorizationsParamsSchema: readArtistWalletAuthorizationsParamsSchema,
    readArtistWalletAuthorizationDataResponseSchema: readArtistWalletAuthorizationDataResponseSchema,
    unauthorizeArtistWalletParamsSchema: unauthorizeArtistWalletParamsSchema$1,
    estimateAuctionParamsSchema: estimateAuctionParamsSchema$1,
    auctionEstimationShareSchema: auctionEstimationShareSchema,
    auctionEstimationSchema: auctionEstimationSchema$1,
    readManyAuctionsQuerySchema: readManyAuctionsQuerySchema$2,
    readAuctionsTotalCountParamsSchema: readAuctionsTotalCountParamsSchema,
    auctionListItemSchema: auctionListItemSchema$2,
    auctionListSchema: auctionListSchema$1,
    ClassificationWithArtAssetIdSchema: ClassificationWithArtAssetIdSchema,
    readAuctionSharesResponseSchema: readAuctionSharesResponseSchema$1,
    readAuctionSharesByArtAssetIdParamsSchema: readAuctionSharesByArtAssetIdParamsSchema$1,
    readLatestAuctionBidParamsSchema: readLatestAuctionBidParamsSchema,
    readLatestAuctionBidResponseSchema: readLatestAuctionBidResponseSchema,
    createAuctionParamsDtoSchema: createAuctionParamsDtoSchema,
    createAuctionResponseSchema: createAuctionResponseSchema$1,
    updateAuctionParamsDtoSchema: updateAuctionParamsDtoSchema,
    approveAuctionRunParamsSchema: approveAuctionRunParamsSchema$1,
    auctionRunApprovalSchema: auctionRunApprovalSchema$1,
    deleteAuctionParamsSchema: deleteAuctionParamsSchema,
    readOneAuctionQuerySchema: readOneAuctionQuerySchema,
    auctionMarkViewedQuerySchema: auctionMarkViewedQuerySchema,
    readOneAuctionRequestQuerySchema: readOneAuctionRequestQuerySchema,
    readOneAuctionParamsSchema: readOneAuctionParamsSchema$2,
    auctionArtAssetdigitalOriginalSchema: auctionArtAssetdigitalOriginalSchema,
    auctionArtAssetDigitalCommonSchema: auctionArtAssetDigitalCommonSchema,
    auctionArtAssetArtistSchema: auctionArtAssetArtistSchema,
    auctionArtAssetSchema: auctionArtAssetSchema,
    readOneAuctionResponseBodySchema: readOneAuctionResponseBodySchema$1,
    readOneAuctionByOnChainIdParamsSchema: readOneAuctionByOnChainIdParamsSchema$1,
    readOneDocumentResponseSchema: readOneDocumentResponseSchema,
    readManyDocumentsResponseSchema: readManyDocumentsResponseSchema,
    filterOptionsResponseSchema: filterOptionsResponseSchema,
    createOneGalleryRequestBodySchema: createOneGalleryRequestBodySchema$1,
    createOneGalleryResponseBodySchema: createOneGalleryResponseBodySchema$1,
    updateOneGalleryRequestBodySchema: updateOneGalleryRequestBodySchema$1,
    updateOneGalleryResponseBodySchema: updateOneGalleryResponseBodySchema$1,
    updateOneGalleryQuerySchema: updateOneGalleryQuerySchema$1,
    readOneGalleryResponseBodySchema: readOneGalleryResponseBodySchema$2,
    readOneGalleryQuerySchema: readOneGalleryQuerySchema$2,
    galleryIdParamsSchema: galleryIdParamsSchema$2,
    createSharePayloadSchema: createSharePayloadSchema$1,
    createSaleOrderParamsSchema: createSaleOrderParamsSchema$1,
    estimateSaleOrderParamsSchema: estimateSaleOrderParamsSchema$1,
    saleOrderEstimationSchema: saleOrderEstimationSchema,
    getSaleOrderPurchaseApproveParamsSchema: getSaleOrderPurchaseApproveParamsSchema$1,
    saleApprovalSchema: saleApprovalSchema,
    readSaleOrderSharesResponseSchema: readSaleOrderSharesResponseSchema$1,
    readActualSaleOrderSharesParamsSchema: readActualSaleOrderSharesParamsSchema$1,
    getTokenDataParamsSchema: getTokenDataParamsSchema,
    readInstitutionIncomesOrderSchema: readInstitutionIncomesOrderSchema,
    readInstitutionIncomesParamsSchema: readInstitutionIncomesParamsSchema,
    readInstitutionIncomesQuerySchema: readInstitutionIncomesQuerySchema,
    readInstitutionTransactionCountQuerySchema: readInstitutionTransactionCountQuerySchema,
    institutionIncomeShareSchema: institutionIncomeShareSchema,
    institutionIncomeItemSchema: institutionIncomeItemSchema,
    institutionIncomesListSchema: institutionIncomesListSchema,
    readInstitutionTotalIncomeParamsSchema: readInstitutionTotalIncomeParamsSchema,
    institutionTotalIncomeSchema: institutionTotalIncomeSchema$1,
    readInstitutionTotalCountQuerySchema: readInstitutionTotalCountQuerySchema,
    logEntrySchema: logEntrySchema$1,
    readOneActivityResponseSchema: readOneActivityResponseSchema$1,
    readOneActivityQuerySchema: readOneActivityQuerySchema$1,
    createUserNotificationsRequestBodySchema: createUserNotificationsRequestBodySchema,
    readUserNotificationsRequestQuerySchema: readUserNotificationsRequestQuerySchema,
    updateNotificationsToViewedRequestBodySchema: updateNotificationsToViewedRequestBodySchema,
    readUserNotificationsResponseSchema: readUserNotificationsResponseSchema,
    readManyNotificationsRequestQuerySchema: readManyNotificationsRequestQuerySchema,
    readNotificationsTotalCountRequestQuerySchema: readNotificationsTotalCountRequestQuerySchema,
    institutionsIdParamsSchema: institutionsIdParamsSchema,
    institutionsSlugParamsSchema: institutionsSlugParamsSchema,
    institutionsAddStoreDomainParamsSchema: institutionsAddStoreDomainParamsSchema$1,
    institutionStoreDomainDtoSchema: institutionStoreDomainDtoSchema$2,
    readInstitutionByDomainParamsSchema: readInstitutionByDomainParamsSchema,
    institutionStoreDnsConfigDtoSchema: institutionStoreDnsConfigDtoSchema$1,
    updateInstitutionStoreFaviconParamsSchema: updateInstitutionStoreFaviconParamsSchema,
    updateInstitutionStoreFaviconRequestBodySchema: updateInstitutionStoreFaviconRequestBodySchema$1,
    institutionSiteSchema: institutionSiteSchema,
    institutionDtoSchema: institutionDtoSchema,
    createInstitutionSubscription: createInstitutionSubscription,
    restrictInstitutionRequestBodySchema: restrictInstitutionRequestBodySchema,
    readInstitutionProfileByUserParamsSchema: readInstitutionProfileByUserParamsSchema,
    readInstitutionProfileByUserResponseSchema: readInstitutionProfileByUserResponseSchema,
    readInstitutionClassificationsParamsSchema: readInstitutionClassificationsParamsSchema$1,
    readInstitutionClassificationsQueryParamsSchema: readInstitutionClassificationsQueryParamsSchema$1,
    readInstitutionClassificationsResponseSchema: readInstitutionClassificationsResponseSchema$1,
    inviteInstitutionUserParamsSchema: inviteInstitutionUserParamsSchema,
    institutionUserAcceptInviteParamsSchema: institutionUserAcceptInviteParamsSchema,
    institutionUserResendInviteParamsSchema: institutionUserResendInviteParamsSchema,
    institutionUserDtoSchema: institutionUserDtoSchema,
    institutionUserReadOneParamsSchema: institutionUserReadOneParamsSchema,
    institutionUserReadOneQuerySchema: institutionUserReadOneQuerySchema,
    institutionUserListItemDtoSchema: institutionUserListItemDtoSchema,
    institutionUserSignUpRequestBodySchema: institutionUserSignUpRequestBodySchema,
    readInstitutionUsersListQuerySchema: readInstitutionUsersListQuerySchema,
    institutionUsersListDtoSchema: institutionUsersListDtoSchema,
    institutionUserSuspendParamsSchema: institutionUserSuspendParamsSchema,
    institutionUserRevokeParamsSchema: institutionUserRevokeParamsSchema,
    institutionUserUpdateParamsSchema: institutionUserUpdateParamsSchema,
    institutionUserUpdateBodySchema: institutionUserUpdateBodySchema,
    institutionUserDeleteParamsSchema: institutionUserDeleteParamsSchema,
    institutionSubscriptionDtoSchema: institutionSubscriptionDtoSchema,
    readInstitutionSubscriptionsListQuerySchema: readInstitutionSubscriptionsListQuerySchema,
    institutionSubscriptionListItemDtoSchema: institutionSubscriptionListItemDtoSchema,
    institutionSubscriptionsListDtoSchema: institutionSubscriptionsListDtoSchema,
    institutionSubscriptionDeleteOneParamsSchema: institutionSubscriptionDeleteOneParamsSchema$1,
    institutionSubscriptionsExportParamsSchema: institutionSubscriptionsExportParamsSchema,
    institutionSubscriptionExportDataSchema: institutionSubscriptionExportDataSchema,
    importInstitutionSubscriptionsRequestBodySchema: importInstitutionSubscriptionsRequestBodySchema$1,
    importInstitutionSubscriptionsResponseSchema: importInstitutionSubscriptionsResponseSchema$1,
    readTilesByFileIdParamsSchema: readTilesByFileIdParamsSchema$2,
    tileDtoSchema: tileDtoSchema,
    tilesListSchema: tilesListSchema$2,
    requestSiweNonceParamsSchema: requestSiweNonceParamsSchema$1,
    siweNonceDtoSchema: siweNonceDtoSchema$1,
    siweNonceTokenPayloadSchema: siweNonceTokenPayloadSchema,
    isInstitutionWalletRelation: isInstitutionWalletRelation,
    isArtistWalletRelation: isArtistWalletRelation,
    verifyWalletParamsSchema: verifyWalletParamsSchema$1,
    walletDtoSchema: walletDtoSchema$1,
    suspendWalletParamsSchema: suspendWalletParamsSchema,
    readArtistFilterOptionsResponseSchema: readArtistFilterOptionsResponseSchema$2,
    artistFilterQuerySchema: artistFilterQuerySchema$1,
    ArtistSortColumn: ArtistSortColumn$1,
    artistSortQuerySchema: artistSortQuerySchema$1,
    artistSortLiteQuerySchema: artistSortLiteQuerySchema$1,
    createArtistParamsSchema: createArtistParamsSchema,
    createArtistResponseSchema: createArtistResponseSchema,
    readManyArtistsQuerySchema: readManyArtistsQuerySchema$2,
    readManyArtistsLiteQuerySchema: readManyArtistsLiteQuerySchema$1,
    readManyArtistsResponseSchema: readManyArtistsResponseSchema$2,
    readManyArtistsLiteResponseSchema: readManyArtistsLiteResponseSchema$1,
    readManyArtistsUFDAPhotosResponseSchema: readManyArtistsUFDAPhotosResponseSchema$1,
    readOneArtistResponseSchema: readOneArtistResponseSchema$2,
    deleteOneArtistParamsSchema: deleteOneArtistParamsSchema$1,
    portfolioValueRequestParamsSchema: portfolioValueRequestParamsSchema,
    portfolioValueSchema: portfolioValueSchema,
    artistUnpublishParamsSchema: artistUnpublishParamsSchema$1,
    artistPublishParamsSchema: artistPublishParamsSchema$1,
    createNewArtAssetArtistSchema: createNewArtAssetArtistSchema,
    changeEmailRequestParamsSchema: changeEmailRequestParamsSchema$1,
    sendArtistReportMailRequestParamsSchema: sendArtistReportMailRequestParamsSchema$1,
    sendArtistReportSlackNotificationParamsSchema: sendArtistReportSlackNotificationParamsSchema$1,
    sendArtAssetReportSlackNotificationParamsSchema: sendArtAssetReportSlackNotificationParamsSchema$1,
    sendAuctionReportSlackNotificationParamsSchema: sendAuctionReportSlackNotificationParamsSchema$1,
    sendContactUsReportRequestBodySchema: sendContactUsReportRequestBodySchema,
    readPhotoDataParamsSchema: readPhotoDataParamsSchema$2,
    readPhotoResponseSchema: readPhotoResponseSchema$1,
    readManyArtAssetsRequestsFiltersQuerySchema: readManyArtAssetsRequestsFiltersQuerySchema,
    ArtAssetsRequestsSortColumn: ArtAssetsRequestsSortColumn,
    artAssetsRequestsSortQuerySchema: artAssetsRequestsSortQuerySchema,
    readManyArtAssetsRequestsQuerySchema: readManyArtAssetsRequestsQuerySchema$1,
    readManyArtAssetsRequestsCountParamsSchema: readManyArtAssetsRequestsCountParamsSchema,
    artAssetRequestListItemSchema: artAssetRequestListItemSchema,
    artAssetRequestListSchema: artAssetRequestListSchema$1,
    createArtAssetPreOrderRequestBodySchema: createArtAssetPreOrderRequestBodySchema$1,
    createArtAssetMocksRequestBodySchema: createArtAssetMocksRequestBodySchema,
    createArtistMockRequestBodySchema: createArtistMockRequestBodySchema,
    collectorBaseSchema: collectorBaseSchema,
    collectorIdParamsSchema: collectorIdParamsSchema$1,
    collectorQuerySchema: collectorQuerySchema,
    updateOneCollectorRequestBodySchema: updateOneCollectorRequestBodySchema$1,
    readOneCollectorResponseSchema: readOneCollectorResponseSchema,
    readOneCollectorQuerySchema: readOneCollectorQuerySchema$1,
    createOneCollectorRequestBodySchema: createOneCollectorRequestBodySchema,
    createOneCollectorResponseSchema: createOneCollectorResponseSchema,
    curatorBaseSchema: curatorBaseSchema,
    curatorSchema: curatorSchema,
    curatorIdParamsSchema: curatorIdParamsSchema,
    curatorQuerySchema: curatorQuerySchema,
    createOneCuratorRequestBodySchema: createOneCuratorRequestBodySchema$1,
    updateOneCuratorRequestBodySchema: updateOneCuratorRequestBodySchema$1,
    CuratorsSortColumn: CuratorsSortColumn,
    curatorsSortQuerySchema: curatorsSortQuerySchema,
    curatorsFilterQuerySchema: curatorsFilterQuerySchema,
    readManyCuratorsQuerySchema: readManyCuratorsQuerySchema$2,
    curatorListItemSchema: curatorListItemSchema,
    curatorsListSchema: curatorsListSchema,
    readOneCuratorResponseSchema: readOneCuratorResponseSchema$1,
    readManyCountriesRequestQuerySchema: readManyCountriesRequestQuerySchema$1,
    readManyCitiesRequestQueryFiltersSchema: readManyCitiesRequestQueryFiltersSchema$2,
    readManyCitiesRequestPaginationQuerySchema: readManyCitiesRequestPaginationQuerySchema,
    readManyCitiesRequestQuerySchema: readManyCitiesRequestQuerySchema$2,
    readOneCuratorReviewRequestParamsSchema: readOneCuratorReviewRequestParamsSchema$2,
    readOneCuratorReviewRequestQuerySchema: readOneCuratorReviewRequestQuerySchema,
    readOneCuratorsReviewsResponseBodySchema: readOneCuratorsReviewsResponseBodySchema$2,
    curatorsReviewsFilterQuerySchema: curatorsReviewsFilterQuerySchema,
    CuratorsReviewsSortColumn: CuratorsReviewsSortColumn,
    curatorsReviewsSortQuerySchema: curatorsReviewsSortQuerySchema,
    readManyCuratorsReviewsRequestQuerySchema: readManyCuratorsReviewsRequestQuerySchema$2,
    readManyCuratorsReviewsResponseBodySchema: readManyCuratorsReviewsResponseBodySchema$2,
    createOneCuratorReviewRequestBodySchema: createOneCuratorReviewRequestBodySchema$1,
    createOneCuratorReviewResponseBodySchema: createOneCuratorReviewResponseBodySchema$1,
    updateOneCuratorReviewRequestParamsSchema: updateOneCuratorReviewRequestParamsSchema$1,
    updateOneCuratorReviewRequestBodySchema: updateOneCuratorReviewRequestBodySchema$1,
    updateOneCuratorReviewResponseBodySchema: updateOneCuratorReviewResponseBodySchema$1,
    classificationsSchema: classificationsSchema$1,
    readManyTagsRequestQuerySchema: readManyTagsRequestQuerySchema,
    createOneTagRequestBodySchema: createOneTagRequestBodySchema$1,
    createOneTagResponseBodySchema: createOneTagResponseBodySchema$1,
    createOneStyleRequestBodySchema: createOneStyleRequestBodySchema$1,
    createOneStyleResponseSchema: createOneStyleResponseSchema$1,
    createOneMediumRequestBodySchema: createOneMediumRequestBodySchema$1,
    createOneMediumResponseSchema: createOneMediumResponseSchema$1,
    createOneArtistsWaitlistItemParamsDtoSchema: createOneArtistsWaitlistItemParamsDtoSchema,
    createArtistsWaitlistItemResponseSchema: createArtistsWaitlistItemResponseSchema$1,
    newsAuthorSchema: newsAuthorSchema,
    newsItemSchema: newsItemSchema,
    newsSlugParamsSchema: newsSlugParamsSchema,
    newsFilterSchema: newsFilterSchema,
    NewsSortColumn: NewsSortColumn,
    newsSortQuerySchema: newsSortQuerySchema,
    readManyNewsQuerySchema: readManyNewsQuerySchema$1,
    newsListSchema: newsListSchema,
    readOneSelectionRequestParamsSchema: readOneSelectionRequestParamsSchema$2,
    readOneSelectionRequestQuerySchema: readOneSelectionRequestQuerySchema$2,
    selectionArtAssetDigitalOriginalSchema: selectionArtAssetDigitalOriginalSchema,
    selectionArtAssetArtistSchema: selectionArtAssetArtistSchema$1,
    selectionArtAssetSchema: selectionArtAssetSchema$1,
    selectionUnpopulatedSchema: selectionUnpopulatedSchema,
    readOneSelectionResponseBodySchema: readOneSelectionResponseBodySchema$2,
    readOneSelectionByAccessTokenRequestParamsSchema: readOneSelectionByAccessTokenRequestParamsSchema$1,
    readOneSelectionByAccessTokenResponseBodySchema: readOneSelectionByAccessTokenResponseBodySchema$1,
    ReadManySelectionsRequestSortColumn: ReadManySelectionsRequestSortColumn,
    readManySelectionsRequestSortQuerySchema: readManySelectionsRequestSortQuerySchema$2,
    readManySelectionsRequestFilterQuerySchema: readManySelectionsRequestFilterQuerySchema$2,
    readManySelectionsRequestQuerySchema: readManySelectionsRequestQuerySchema$2,
    selectionListItemDtoSchema: selectionListItemDtoSchema,
    readManySelectionsListItemSchema: readManySelectionsListItemSchema,
    readManySelectionsResponseBodySchema: readManySelectionsResponseBodySchema$2,
    createOneSelectionRequestBodySchema: createOneSelectionRequestBodySchema$1,
    createOneSelectionResponseBodySchema: createOneSelectionResponseBodySchema$1,
    updateOneSelectionRequestParamsSchema: updateOneSelectionRequestParamsSchema$1,
    updateOneSelectionRequestBodySchema: updateOneSelectionRequestBodySchema$1,
    updateOneSelectionResponseBodySchema: updateOneSelectionResponseBodySchema$1
});

const createOneAgencyRequestBodySchema = createOneAgencyRequestBodySchema$1;
const createOneAgencyResponseBodySchema = createOneAgencyResponseBodySchema$1;
const updateOneAgencyRequestBodySchema = updateOneAgencyRequestBodySchema$1;
const updateOneAgencyResponseBodySchema = updateOneAgencyResponseBodySchema$1;
const updateOneAgencyQuerySchema = updateOneAgencyQuerySchema$1;
const readOneAgencyResponseBodySchema$1 = readOneAgencyResponseBodySchema$2;
const readOneAgencyQuerySchema$1 = readOneAgencyQuerySchema$2;
const agencyIdParamsSchema$1 = agencyIdParamsSchema$2;

const auctionEstimationSchema = auctionEstimationSchema$1;
const estimateAuctionParamsSchema = estimateAuctionParamsSchema$1;
const readOneAuctionParamsSchema$1 = z.object({
    auctionId: z.string().uuid(),
});
const readAuctionSharesResponseSchema = readAuctionSharesResponseSchema$1;
const auctionSchema = z.object({
    id: z.string().uuid(),
    lot_id: z.number().int(),
    on_chain_id: z.string().nullable(),
    bid_step_value: z.string(),
    buyer_fee_premium_percent: z.number(),
    currency: currencySchema,
    start_date: z.coerce.date().nullable().optional(),
    end_date: z.coerce.date().nullable().optional(),
    duration: z.number().int(),
    initial_bid_value: z.string(),
    secondary_market_royalty: z.number(),
    seller_fee_percent: z.number(),
    status: z.nativeEnum(AuctionStatus),
    storage_fee_value: z.string(),
    auction_shares: z.array(z.object({
        id: z.string().uuid(),
        percent: z.number(),
        wallet: z.object({
            id: z.string().uuid(),
            address: z.string(),
            artist_id: z.number().int().optional().nullable(),
            institution_id: z.number().int().optional().nullable(),
            is_platform_address: z.boolean(),
        }),
    })),
    art_asset: z.object({
        id: z.number().int(),
        name_english: z.string(),
        name_original: z.string().optional().nullable(),
        art_asset_type: z.nativeEnum(ArtAssetType),
        format: z.nativeEnum(ArtAssetFormat),
        content_flags: z.array(z.nativeEnum(ArtAssetContentFlags)),
        main_photo: photoSchema.nullable().optional(),
        token_id: z.number().optional().nullable(),
        digital_original: z
            .object({
            id: z.number(),
            mpx: z.number(),
            exif: exifSchema,
        })
            .nullable(),
        digital_common: z
            .object({
            id: z.number(),
            preview: z.string().nullable(),
            file: z.string().nullable(),
            filesize: z.string().nullable(),
            filename_disk: z.string().nullable(),
            type: z.string().nullable(),
        })
            .nullable(),
        artists: z.array(z.object({
            id: z.number().int(),
            name: z.string(),
            photo: photoSchema.optional().nullable(),
        })),
    }),
    page_viewed: z.boolean(),
    date_created: z.string(),
    date_updated: z.string().nullable(),
});
const readAuctionSharesByArtAssetIdParamsSchema = readAuctionSharesByArtAssetIdParamsSchema$1;
const createAuctionShareParamsSchema = z.object({
    wallet: z.string().uuid(),
    percent: z.number().positive().max(100).min(0.01),
});
const createAuctionParamsSchema = z.object({
    initial_bid_value: z.string(),
    currency: z.string(),
    bid_step_value: z.string(),
    storage_fee_value: z.string(),
    art_asset: z.number().int(),
    duration: z.number().int(),
    seller_fee_percent: z.number().nonnegative(),
    buyer_fee_premium_percent: z.number().nonnegative(),
    secondary_market_royalty: z.number().nonnegative(),
    auction_shares: z.array(createAuctionShareParamsSchema),
});
const createAuctionResponseSchema = z.object({
    id: z.string().uuid(),
});
const updateAuctionParamsSchema = z.object({
    initial_bid_value: z.string().optional(),
    bid_step_value: z.string().optional(),
    duration: z.number().int().optional(),
    storage_fee_value: z.string().optional(),
    seller_fee_percent: z.number().optional(),
    buyer_fee_premium_percent: z.number().optional(),
    secondary_market_royalty: z.number().optional(),
    auction_shares: z.array(createAuctionShareParamsSchema).optional(),
});
const auctionListItemSchema$1 = auctionListItemSchema$2;
const readManyAuctionsQuerySchema$1 = readManyAuctionsQuerySchema$2.omit({ institution_id: true });
const auctionListSchema = auctionListSchema$1;
const approveAuctionRunParamsSchema = approveAuctionRunParamsSchema$1;
const auctionRunApprovalSchema = auctionRunApprovalSchema$1;

const readArtistFilterOptionsResponseSchema$1 = readArtistFilterOptionsResponseSchema$2;
const artistFilterQuerySchema = z.object({
    medium: z.string().optional(),
    mediums_slugs: arrayStringsOption$3.optional(),
    styles: z.string().optional(),
    styles_slugs: arrayStringsOption$3.optional(),
    countries: z.string().optional(),
    countries_slugs: arrayStringsOption$3.optional(),
    search: z.string().min(2).optional(),
    artists: z.string().optional(),
    verification_statuses: z.string().optional(),
    statuses: z.string().optional(),
});
const ArtistSortColumn = ArtistSortColumn$1;
const artistSortQuerySchema = artistSortQuerySchema$1;
const artistSortLiteQuerySchema = artistSortLiteQuerySchema$1;
const artistInviteParamsSchema = z.object({
    id: numberParamSchema,
});
const readManyArtistsQuerySchema$1 = artistFilterQuerySchema
    .merge(paginationQuerySchema)
    .merge(artistSortQuerySchema);
const artistListItemSchema$1 = z.object({
    id: z.number(),
    slug: z.string().nullable(),
    name: z.string(),
    status: z.nativeEnum(ArtistStatus),
    verification_status: z.nativeEnum(ArtistVerificationStatus),
    date_created: z.string(),
    date_updated: z.string().nullable(),
    portfolio_value: z.string(),
    verified: z.boolean(),
    viewed: z.boolean(),
    countries: z
        .array(z.object({
        id: z.number(),
        name: z.string(),
        slug: z.string(),
    }))
        .nullable(),
    photo_attribution: z.string().nullable(),
    photo: photoSchema.nullable(),
    artist_type: z.nativeEnum(ArtistType),
    individuals: z
        .array(z.object({
        id: z.number(),
        slug: z.string().nullable(),
        name: z.string(),
        status: z.string(),
        photo: photoSchema.nullable(),
    }))
        .nullable(),
    groups: z
        .array(z.object({
        id: z.number(),
        slug: z.string().nullable(),
        name: z.string(),
        status: z.string(),
        photo: photoSchema.nullable(),
    }))
        .nullable(),
    art_assets_count: z.number(),
});
const readManyArtistsResponseSchema$1 = z.object({
    artists: artistListItemSchema$1.array(),
    total_count: z.number(),
});
const readManyArtistsLiteQuerySchema = artistFilterQuerySchema
    .pick({ artists: true })
    .merge(paginationQuerySchema)
    .merge(artistSortLiteQuerySchema);
const readManyArtistsLiteResponseSchema = z.object({
    artists: readManyArtistsLiteResponseSchema$1,
    total_count: z.number(),
});
const readOneArtistParamsSchema$1 = z.object({
    id: numberParamSchema,
});
const valueStatisticsSchema = z.object({
    value: z.number(),
    count: z.number(),
    percentage: z.number(),
});
const artistPortfolioValueSchema = z
    .object({
    initialValue: z.number().nonnegative(),
    realized: valueStatisticsSchema,
    unrealized: valueStatisticsSchema,
    total: valueStatisticsSchema,
    growth: valueStatisticsSchema.partial(),
})
    .nullable();
const readOneArtistResponseSchema$1 = z.object({
    id: z.number(),
    slug: z.string().nullable(),
    verification_status: z.nativeEnum(ArtistVerificationStatus),
    date_created: z.string(),
    date_updated: z.string().nullable(),
    name: z.string(),
    name_transliteration: z.string(),
    pseudonym: z.string().nullable().optional(),
    name_aliases: z.string().nullable(),
    photo: photoSchema.nullable(),
    photo_attribution: z.string().nullable(),
    biography: editorBlockSchema.nullable(),
    relationship_information: editorBlockSchema.nullable(),
    gender: z.nativeEnum(Gender).nullable(),
    birth_year: z.number().nullable(),
    date_of_birth: z.string().nullable(),
    date_of_death: z.string().nullable(),
    locations: z.array(locationSchema).nullable(),
    status: z.nativeEnum(ArtistStatus),
    alive: z.boolean(),
    contacts: z.array(z.object({
        type: z.nativeEnum(ContactType),
        value: z.string().trim(),
    })),
    social_profiles: socialProfilesSchema,
    documents: z.array(z.object({
        id: z.number(),
        name: z.string(),
        type: z.nativeEnum(DocumentType).or(z.string()),
        subtype: z.nativeEnum(DocumentSubtype).or(z.string()).nullable(),
    })),
    wallets: z.array(z.object({
        id: z.string(),
        address: z.string(),
        verified: z.boolean(),
    })),
    verified: z.boolean(),
    individuals: z
        .array(z.object({
        id: z.number(),
        slug: z.string().nullable(),
        name: z.string(),
        status: z.string(),
        photo: photoSchema.nullable(),
    }))
        .nullable(),
    groups: z
        .array(z.object({
        id: z.number(),
        slug: z.string().nullable(),
        name: z.string(),
        status: z.string(),
        photo: photoSchema.nullable(),
    }))
        .nullable(),
    portfolio: artistPortfolioValueSchema,
    portfolio_value: z.number(),
    works_count: z.number(),
    artist_active_verification_invitation: z.object({ id: z.string() }).nullable(),
});
const inviteArtistVerificationResponseSchema = z.string();
const updateOneArtistParamsSchema = z.object({
    id: numberParamSchema,
});
const artistUnpublishParamsSchema = artistUnpublishParamsSchema$1;
const artistPublishParamsSchema = artistPublishParamsSchema$1;
const deleteOneArtistParamsSchema = z.object({
    id: numberParamSchema,
});

const createOneGalleryRequestBodySchema = createOneGalleryRequestBodySchema$1;
const createOneGalleryResponseBodySchema = createOneGalleryResponseBodySchema$1;
const updateOneGalleryRequestBodySchema = updateOneGalleryRequestBodySchema$1;
const updateOneGalleryResponseBodySchema = updateOneGalleryResponseBodySchema$1;
const updateOneGalleryQuerySchema = updateOneGalleryQuerySchema$1;
const readOneGalleryResponseBodySchema$1 = readOneGalleryResponseBodySchema$2;
const readOneGalleryQuerySchema$1 = readOneGalleryQuerySchema$2;
const galleryIdParamsSchema$1 = galleryIdParamsSchema$2;

const createSharePayloadSchema = createSharePayloadSchema$1;
const createSaleOrderParamsSchema = createSaleOrderParamsSchema$1;
const estimateSaleOrderParamsSchema = estimateSaleOrderParamsSchema$1;
const saleOrderEstimationResponseSchema = saleOrderEstimationSchema;
const cancelSaleOrderParamsSchema = z.object({
    id: z.string().uuid(),
});
const readSaleOrderSharesResponseSchema = readSaleOrderSharesResponseSchema$1;
const readActualSaleOrderSharesParamsSchema = readActualSaleOrderSharesParamsSchema$1;

const readInstitutionStatsSchema = z.object({
    art_assets_count: z.number(),
    artists_count: z.number(),
    documents_count: z.number(),
    incomes_count: z.number(),
    notifications_count: z.number(),
    auctions_count: z.number(),
    selections_count: z.number(),
});
const institutionSignUpRequestBodySchema = institutionUserSignUpRequestBodySchema;
const institutionsAddStoreDomainParamsSchema = institutionsAddStoreDomainParamsSchema$1;
const institutionStoreDomainDtoSchema$1 = institutionStoreDomainDtoSchema$2;
const updateInstitutionStoreFaviconRequestBodySchema = updateInstitutionStoreFaviconRequestBodySchema$1;
const readStoreConfigurationsResponseSchema$1 = z.object({
    institution_id: z.number(),
    status: z.nativeEnum(SiteStatus),
    slug: z.string(),
    slug_approved: z.boolean(),
});
const institutionStoreDnsConfigDtoSchema = institutionStoreDnsConfigDtoSchema$1;
const readInstitutionProfileSchema = z.object({
    id: z.number().int(),
    status: z.nativeEnum(Status),
    verification_status: z.nativeEnum(InstitutionVerificationStatus),
    slug: z.string(),
    slug_approved: z.boolean(),
    name: z.string(),
    tagline: z.string().nullable(),
    type: z.nativeEnum(InstitutionType),
    description: editorBlockSchema.optional().nullable(),
    locations: locationSchema.optional().nullable(),
    logo: photoSchema.optional().nullable(),
    date_created: z.string(),
    date_updated: z.string(),
    social_profiles: socialProfilesSchema.nullable(),
    contacts: contactsSchema.nullable(),
    wallet: z.string().nullable().optional(),
    wizard_finished: z.boolean(),
    is_suspicious: z.boolean(),
    associated_artist: z.number().nullable(),
    associated_gallery: z.number().nullable(),
    associated_curator: z.number().nullable(),
    associated_collector: z.number().nullable(),
    associated_agency: z.number().nullable(),
});
const institutionSubscriptionsReadManyQueryParamSchema = readInstitutionSubscriptionsListQuerySchema;
const institutionSubscriptionsListResponseSchema = institutionSubscriptionsListDtoSchema;
const institutionSubscriptionDeleteOneParamsSchema = institutionSubscriptionDeleteOneParamsSchema$1;
const institutionSubscriptionExportDataResponseSchema = institutionSubscriptionExportDataSchema;
const importInstitutionSubscriptionsRequestBodySchema = z.object({
    emails: z.array(z.string()),
});
const importInstitutionSubscriptionsResponseSchema = importInstitutionSubscriptionsResponseSchema$1;

const createArtAssetAddArtistPayloadSchema = z.object({
    id: z.number().int(),
});
const isCreateArtAssetAddArtistPayload = (payload) => {
    return createArtAssetAddArtistPayloadSchema.safeParse(payload).success;
};
const createArtAssetNewArtistPayloadSchema = z.object({
    name: z.string(),
});
const isCreateArtAssetNewArtistPayloadSchema = (payload) => {
    return createArtAssetNewArtistPayloadSchema.safeParse(payload).success;
};
const sizeError = {
    errorMap: () => ({
        message: 'Please enter a numeric size value',
    }),
};
const creationYearSchema = z.string().refine((value) => {
    const yearRegex = /^\d{4}$/;
    if (yearRegex.test(value)) {
        const year = parseInt(value, 10);
        return year >= 1000 && year <= 9999;
    }
    const decadeRegex = /^\d{3}0s$/;
    if (decadeRegex.test(value)) {
        const decade = parseInt(value.replace(/s/g, ''), 10);
        return decade >= 1000 && decade <= 9999;
    }
    const rangeRegex = /^\d{4}-\d{4}$/;
    if (rangeRegex.test(value)) {
        const [startYear, endYear] = value.split('-').map((year) => parseInt(year, 10));
        return startYear >= 1000 && endYear <= 9999 && startYear < endYear;
    }
    return false;
}, {
    message: 'Invalid creation year. Use formats: YYYY, YYY0s, or YYYY-YYYY. In ranges, the start must be less than the end.',
});
const createArtAssetParamsSchema = z
    .object({
    name_english: z.string().min(1),
    name_original: z.string().nullable().optional(),
    uid: z.string().nullable().optional(),
    artists: z
        .array(z.union([createArtAssetAddArtistPayloadSchema, createArtAssetNewArtistPayloadSchema]))
        .optional()
        .default([]),
    art_asset_type: z.nativeEnum(ArtAssetType),
    format: z.nativeEnum(ArtAssetFormat).default(ArtAssetFormat.Image),
    content_flags: z.array(z.nativeEnum(ArtAssetContentFlags)).default([]),
    initial_price: z
        .number({
        errorMap: () => ({
            message: 'Please enter a numeric price value',
        }),
    })
        .positive()
        .nullable()
        .optional(),
    physical_purchase_price: z
        .number({
        errorMap: () => ({
            message: 'Please enter a numeric price value',
        }),
    })
        .positive(),
    physical_purchase_date: z.coerce
        .date({
        errorMap: () => ({
            message: 'Please enter a valid purchase date',
        }),
    })
        .nullable()
        .optional(),
    creation_year: creationYearSchema,
    styles: z.array(z.union([z.string(), z.number()])).optional(),
    medium: z.array(z.union([z.string(), z.number()])).optional(),
    locations: z
        .array(locationSchema, {
        errorMap: () => ({
            message: 'Please add a location to the artwork.',
        }),
    })
        .min(1),
    framed_units: z.string().nullable().optional(),
    framed_size_x: z.number(sizeError).positive().nullable().optional(),
    framed_size_y: z.number(sizeError).positive().nullable().optional(),
    framed_size_z: z.number(sizeError).positive().nullable().optional(),
    original_units: z.string().nullable().optional(),
    original_size_x: z.number(sizeError).nullable().optional(),
    original_size_y: z.number(sizeError).nullable().optional(),
    original_size_z: z.number(sizeError).positive().nullable().optional(),
    description: editorBlockSchema.nullable().optional(),
    provenance: editorBlockSchema.nullable().optional(),
    photos: z.array(z.string()).optional(),
    main_photo: z.string().nullable().optional(),
    digital_common: z.string().nullable().optional(),
    documents: z
        .array(z.union([z.string(), z.number()]), {
        errorMap: () => ({
            message: 'You have to select or add a document that confirms your rights to this artwork',
        }),
    })
        .optional(),
    curator_review: z
        .object({
        curator: z.number(),
        description: editorBlockSchema.nullable(),
    })
        .optional(),
})
    .superRefine((data, ctx) => {
    if (data.art_asset_type === ArtAssetType.DigitalOriginal) {
        if (!data.original_units || data.original_units.trim() === '') {
            ctx.addIssue({
                path: ['original_units'],
                code: z.ZodIssueCode.custom,
                message: 'original_units is required for Digital Original assets.',
            });
        }
        const sizeMessage = 'Please enter a numeric size value';
        if (!data.original_size_x || data.original_size_x <= 0) {
            ctx.addIssue({
                path: ['original_size_x'],
                code: z.ZodIssueCode.custom,
                message: sizeMessage,
            });
        }
        if (!data.original_size_y || data.original_size_y <= 0) {
            ctx.addIssue({
                path: ['original_size_y'],
                code: z.ZodIssueCode.custom,
                message: sizeMessage,
            });
        }
    }
});
const createArtAssetResponseBodySchema = z.object({
    id: z.number(),
});
const createManyArtAssetsFromPhotosRequestBodySchema = createManyArtAssetsFromPhotosRequestBodySchema$1.pick({
    photos: true,
});
const createManyArtAssetsFromPhotosResponseBodySchema = createManyArtAssetsFromPhotosResponseBodySchema$1;
const createOneArtAssetFromPhotoRequestBodySchema = createOneArtAssetFromPhotoRequestBodySchema$1.pick({
    photo: true,
});
const createOneArtAssetFromPhotoResponseBodySchema = createOneArtAssetFromPhotoResponseBodySchema$1;
const updateArtAssetAddArtistPayloadSchema = createArtAssetAddArtistPayloadSchema;
const isUpdateArtAssetAddArtistPayload = (payload) => {
    return updateArtAssetAddArtistPayloadSchema.safeParse(payload).success;
};
const updateArtAssetNewArtistPayloadSchema = createArtAssetNewArtistPayloadSchema;
const isUpdateArtistAssetNewArtistPayloadSchema = (payload) => {
    return updateArtAssetNewArtistPayloadSchema.safeParse(payload).success;
};
const updateArtAssetParamsSchema = z
    .object({
    name_english: z.string().min(1).optional(),
    name_original: z.string().nullable().optional(),
    uid: z.string().nullable().optional(),
    art_asset_type: z.nativeEnum(ArtAssetType).optional(),
    format: z.nativeEnum(ArtAssetFormat).optional(),
    content_flags: z.array(z.nativeEnum(ArtAssetContentFlags)).nullable().optional(),
    artists: z.array(z.union([updateArtAssetAddArtistPayloadSchema, updateArtAssetNewArtistPayloadSchema])).optional(),
    initial_price: z
        .number({
        errorMap: () => ({
            message: 'Please enter a numeric price value',
        }),
    })
        .positive()
        .nullable()
        .optional(),
    physical_purchase_price: z
        .number({
        errorMap: () => ({
            message: 'Please enter a numeric price value',
        }),
    })
        .positive()
        .optional(),
    physical_purchase_date: z.coerce
        .date({
        errorMap: () => ({
            message: 'Please enter a valid purchase date',
        }),
    })
        .nullable()
        .optional(),
    creation_year: creationYearSchema.optional(),
    styles: z.array(z.union([z.string(), z.number()])).optional(),
    medium: z.array(z.union([z.string(), z.number()])).optional(),
    locations: z
        .array(locationSchema, {
        errorMap: () => ({
            message: 'Please add a location to the artwork.',
        }),
    })
        .min(1)
        .optional(),
    framed_units: z.string().nullable().optional(),
    framed_size_x: z.number(sizeError).positive().nullable().optional(),
    framed_size_y: z.number(sizeError).positive().nullable().optional(),
    framed_size_z: z.number(sizeError).positive().nullable().optional(),
    original_units: z.string().optional(),
    original_size_x: z.number(sizeError).positive().nullable().optional(),
    original_size_y: z.number(sizeError).positive().nullable().optional(),
    original_size_z: z.number(sizeError).positive().nullable().optional(),
    description: editorBlockSchema.nullable().optional(),
    provenance: editorBlockSchema.nullable().optional(),
    photos: z.array(z.string()).optional(),
    main_photo: z.string().nullable().optional(),
    documents: z
        .array(z.union([z.string(), z.number()]), {
        errorMap: () => ({
            message: 'You have to select or add a document that confirms your rights to this artwork',
        }),
    })
        .optional(),
    curator_review: z
        .object({
        id: z.number(),
        curator: z.number().optional(),
        description: editorBlockSchema.nullable().optional(),
    })
        .optional(),
    digital_common: z.string().nullable().optional(),
})
    .superRefine((data, ctx) => {
    const isDigitalCommon = data.art_asset_type === ArtAssetType.DigitalCommon;
    if (!isDigitalCommon) {
        if (data.original_size_x === null) {
            ctx.addIssue({
                path: ['original_size_x'],
                code: z.ZodIssueCode.custom,
                message: 'Please enter a numeric size value',
            });
        }
        if (data.original_size_y === null) {
            ctx.addIssue({
                path: ['original_size_y'],
                code: z.ZodIssueCode.custom,
                message: 'Please enter a numeric size value',
            });
        }
    }
});
const artAssetStatusChangeParamsSchema = z.object({
    id: z.string(),
});
const artAssetReadOneParamsSchema = z.object({
    id: z.string(),
});
const __DEPRECATED_saleDataSchema = z.object({
    price: z.string(),
    participants: z.array(z.object({
        address: z.string(),
        share: z.string(),
    })),
});
const artistSchema = z.object({
    id: z.number(),
    name: z.string(),
    verified: z.boolean(),
    photo: photoSchema.nullable(),
    slug: z.string().nullable(),
    wallets: z.array(z.object({
        id: z.string(),
        address: z.string(),
        verified: z.boolean(),
    })),
});
const styleSchema = z.object({
    id: z.number(),
    name: z.string(),
    slug: z.string(),
});
const mediumSchema = z.object({
    id: z.number(),
    name: z.string(),
});
const documentSchema = z.object({
    id: z.number(),
    name: z.string(),
    slug: z.string().nullable(),
    subtype: z.string().nullable(),
    type: z.string(),
});
const digitalOriginalSchema = z.object({
    color_profile_description: z.string(),
    color_space: z.string().nullable(),
    created: z.string().nullable(),
    date_created: z.string(),
    date_updated: z.string().nullable(),
    device_make: z.string().nullable(),
    device_model: z.string().nullable(),
    exprosure_program: z.string().nullable(),
    exprosure_time: z.string().nullable(),
    f_number: z.string().nullable(),
    file_id: z.string().nullable(),
    focal_length: z.string().nullable(),
    format: z.string().nullable(),
    height: z.string().nullable(),
    id: z.number(),
    lense_make: z.string().nullable(),
    lense_model: z.string().nullable(),
    metering_mode: z.string().nullable(),
    mpx: z.number(),
    size: z.string().nullable(),
    user_created: z.string(),
    user_updated: z.string().nullable(),
    width: z.string().nullable(),
});
const digitalCommonSchema = z.object({
    id: z.number(),
    preview: z.string().nullable(),
    file: z.string().nullable(),
    filesize: z.string().nullable(),
    filename_disk: z.string().nullable(),
    type: z.string().nullable(),
});
const artAssetDtoSchema = z.object({
    art_asset_type: z.nativeEnum(ArtAssetType),
    format: z.nativeEnum(ArtAssetFormat),
    content_flags: z.array(z.nativeEnum(ArtAssetContentFlags)),
    creation_year: z.string().nullable(),
    date_created: z.string(),
    date_updated: z.string().nullable(),
    estimate_price: z.number().nullable(),
    realized_price: z.number().nullable(),
    framed_size_x: z.number().nullable(),
    framed_size_y: z.number().nullable(),
    framed_size_z: z.number().nullable(),
    framed_units: z.string().nullable(),
    id: z.number(),
    meta_description: z.string().nullable(),
    meta_image: z.string().nullable(),
    meta_title: z.string().nullable(),
    name_english: z.string().nullable(),
    name_original: z.string().nullable(),
    original_size_x: z.number().nullable(),
    original_size_y: z.number().nullable(),
    original_size_z: z.number().nullable(),
    original_units: z.string().nullable(),
    physical_purchase_date: z.string().nullable(),
    physical_purchase_price: z.number().nullable(),
    initial_price: z.number().nullable(),
    sale_data: __DEPRECATED_saleDataSchema.nullable(),
    sale_status: z.string(),
    slug: z.string().nullable(),
    status: z.string(),
    token_id: z.number().nullable(),
    token_status: z.nativeEnum(TokenStatus),
    mint_tx_hash: z.string().nullable().optional(),
    uid: z.string().nullable(),
    user_created: z.string(),
    user_updated: z.string().nullable(),
    locations: locationSchema.array().nullable(),
    photos: z.array(photoSchema).default([]),
    main_photo: photoSchema.nullable(),
    styles: z.array(styleSchema).default([]),
    medium: z.array(mediumSchema).default([]),
    digital_original: digitalOriginalSchema.nullable(),
    digital_common: digitalCommonSchema.nullable(),
    description: editorBlockSchema.nullable(),
    provenance: editorBlockSchema.nullable(),
    documents: z.array(documentSchema).default([]),
    artists: z.array(artistSchema).default([]),
    uris: tokenMetadataSchema.optional().nullable(),
    sale_order: artAssetSaleOrderInfoSchema.optional().nullable(),
    auction: artAssetAuctionInfoSchema.optional().nullable(),
    mint_locked: z.boolean().nullable().optional(),
    curator_review: z
        .object({
        id: z.number(),
        description: editorBlockSchema,
        curator: z.object({
            id: z.number(),
            slug: z.string(),
            first_name: z.string(),
            last_name: z.string(),
            position: z.string().nullable().optional(),
            photo: photoSchema,
        }),
    })
        .nullable()
        .optional(),
    isDigitalRightsOwned: z.boolean(),
    selections: readManySelectionsResponseBodySchema$2,
});
const orderSchema = z.enum(['asc', 'desc']);
const minMaxSchema = z.tuple([z.number(), z.number()]);
const arrayIdsOption = z
    .string()
    .transform((val) => val.split(',').map((v) => Number(v)))
    .pipe(z.array(z.number()));
const arrayStringsOption = z.string().transform((val) => val.split(',').map((v) => v));
const minMaxOption$1 = z
    .string()
    .regex(/^\d+,\d+$/)
    .transform((val) => val.split(',').map((v) => Number(v)));
const booleanOption = z.enum(['true', 'false']).transform((v) => v === 'true');
const artAssetsReadManyQueryParamsSchema = z
    .object({
    statuses: arrayStringsOption.optional(),
    artists: arrayIdsOption.optional(),
    artists_slugs: arrayStringsOption.optional(),
    art_assets: arrayIdsOption.optional(),
    medium: arrayIdsOption.optional(),
    mediums_slugs: arrayStringsOption.optional(),
    styles: arrayIdsOption.optional(),
    styles_slugs: arrayStringsOption.optional(),
    countries: arrayIdsOption.optional(),
    countries_slugs: arrayStringsOption.optional(),
    original_size_x: minMaxOption$1.optional(),
    original_size_y: minMaxOption$1.optional(),
    original_size_z: minMaxOption$1.optional(),
    price: minMaxOption$1.optional(),
    dlct: booleanOption.optional(),
    documents: booleanOption.optional(),
    exclude_art_assets: arrayIdsOption.optional(),
    column: z
        .enum(['name_original', 'name_english', 'date_created', 'date_updated', 'year', 'artist', 'price'])
        .optional()
        .default('name_english'),
    order: orderSchema.optional().default('asc'),
    token_status: arrayStringsOption.optional(),
    search: z.string().min(2).optional(),
    creation_year: minMaxOption$1
        .refine(([min, max]) => min <= max, {
        message: 'In ranges of creation years, the start must be less than or equal to the end.',
    })
        .optional(),
    curator_review: booleanOption.optional(),
    curators: arrayIdsOption.optional(),
    curators_slugs: arrayStringsOption.optional(),
    art_asset_type: arrayStringsOption.optional(),
})
    .merge(paginationQuerySchema);
const artAssetListItemDtoSchema = artAssetListItemSchema.and(z.object({
    token_status: z.nativeEnum(TokenStatus),
}));
const artAssetListDtoSchema = z.array(artAssetListItemDtoSchema);
const artAssetsReadManyResponseBody = z.object({
    total_count: z.number(),
    art_assets: artAssetListDtoSchema,
});
const artAssetsReadManyStatsQueryParamsSchema$1 = artAssetsReadManyQueryParamsSchema.omit({
    limit: true,
    offset: true,
    order: true,
    column: true,
});
const artAssetReadManyStatsResponseBodySchema$1 = artAssetListCountersSchema;
const readManyUFDAPhotosResponseBodySchema = readManyUFDAPhotosResponseBodySchema$1;
const artAssetReadFilterOptionsResponseSchema$1 = z.object({
    artists: z.array(z.object({ id: z.number(), name: z.string(), slug: z.string().nullable(), art_assets_count: z.number() })),
    institutions: z.array(z.object({ id: z.number(), name: z.string() })),
    medium: z.array(z.object({ id: z.number(), name: z.string(), slug: z.string(), art_assets_count: z.number() })),
    styles: z.array(z.object({ id: z.number(), name: z.string(), slug: z.string(), art_assets_count: z.number() })),
    tags: z.array(z.object({ id: z.number(), name: z.string() })),
    original_size_x: z.tuple([z.number(), z.number()]).optional(),
    original_size_y: z.tuple([z.number(), z.number()]).optional(),
    original_size_z: z.tuple([z.number(), z.number()]).optional(),
    price: z.tuple([z.number(), z.number()]).optional(),
    dlct: z.boolean().optional(),
    documents: z.boolean().optional(),
    items_total_count: z.number(),
    token_status: z.array(z.nativeEnum(TokenStatus)),
    countries: z.array(z.object({ id: z.number(), name: z.string(), slug: z.string(), art_assets_count: z.number() })),
    creation_year: z.tuple([z.number(), z.number()]).optional(),
    curator_review: z.boolean().optional(),
    curators: z.array(z.object({
        id: z.number(),
        first_name: z.string(),
        last_name: z.string(),
        slug: z.string().nullable(),
        art_assets_count: z.number(),
    })),
    art_asset_type: z.array(z.nativeEnum(ArtAssetType)),
});

const limitSchema$1 = z
    .union([
    z
        .string()
        .transform((v) => parseInt(v))
        .pipe(z.number().max(1000)),
    z.number().max(1000),
])
    .default(10);
const readTokensHistoryQuerySchema$1 = z.object({
    limit: limitSchema$1,
    cursor: z.string().optional(),
    art_asset_id: z.union([z.number().int(), z.string().transform((v) => parseInt(v))]).optional(),
    auction_id: z.string().uuid().optional(),
});
const tokensHistoryIncomeSchema$1 = z.object({
    amount: z.string(),
    amount_formatted: z.string(),
    currency: z.string(),
    wallet_id: z.string(),
    wallet_address: evmAddressWithExplorerLinkSchema,
});
const baseTokensHistoryItemSchema$1 = z.object({
    id: z.string(),
    tx_hash: hashWithExplorerLinkSchema,
    from: evmAddressWithExplorerLinkSchema,
    index: z.number(),
    tx_fee: z.string(),
    tx_fee_formatted: z.string(),
    block_timestamp: z.string(),
    tx_index: z.number(),
    block_number: z.string(),
    art_asset: z.object({
        id: z.number(),
        name_english: z.string(),
        name_original: z.string().nullable().optional(),
        art_asset_type: z.nativeEnum(ArtAssetType),
        format: z.nativeEnum(ArtAssetFormat),
        main_photo: photoSchema.nullable().optional(),
    }),
    incomes: z.array(tokensHistoryIncomeSchema$1),
});
const tokenHistoryTransferItemSchema$1 = baseTokensHistoryItemSchema$1.extend({
    kind: z.literal(TokenHistoryRecordKind.Transfer),
    token_id: z.number(),
    transferred_to: evmAddressWithExplorerLinkSchema,
    transferred_from: evmAddressWithExplorerLinkSchema,
});
const tokenHistoryMintItemSchema$1 = baseTokensHistoryItemSchema$1.extend({
    kind: z.literal(TokenHistoryRecordKind.Mint),
    token_id: z.number(),
    minted_to: evmAddressWithExplorerLinkSchema,
});
const tokenHistoryBuyItemSchema$1 = baseTokensHistoryItemSchema$1.extend({
    kind: z.literal(TokenHistoryRecordKind.Buy),
    price: z.string(),
    price_formatted: z.string().nullable().optional(),
    token_id: z.number(),
    transferred_to: evmAddressWithExplorerLinkSchema,
});
const tokenAuctionCreatedItemSchema$1 = baseTokensHistoryItemSchema$1.extend({
    kind: z.literal(TokenHistoryRecordKind.AuctionCreated),
    auction: z.string(),
    token_id: z.number(),
    price: z.string(),
    price_formatted: z.string().optional().nullable(),
    end_time: z.number(),
});
const tokenAuctionBidPlacedItemSchema$1 = baseTokensHistoryItemSchema$1.extend({
    kind: z.literal(TokenHistoryRecordKind.AuctionBidPlaced),
    auction: z.string(),
    price: z.string(),
    price_formatted: z.string().optional().nullable(),
    buyer: evmAddressWithExplorerLinkSchema,
});
const tokenAuctionSoldItemSchema$1 = baseTokensHistoryItemSchema$1.extend({
    auction: z.string(),
    price: z.string(),
    price_formatted: z.string().optional().nullable(),
    buyer: evmAddressWithExplorerLinkSchema,
    kind: z.literal(TokenHistoryRecordKind.AuctionSold),
});
const tokenHistoryItemSchema$1 = z.discriminatedUnion('kind', [
    tokenHistoryTransferItemSchema$1,
    tokenHistoryMintItemSchema$1,
    tokenHistoryBuyItemSchema$1,
    tokenAuctionCreatedItemSchema$1,
    tokenAuctionBidPlacedItemSchema$1,
    tokenAuctionSoldItemSchema$1,
]);
const tokenHistoryResponseSchema$1 = z.object({
    data: z.array(tokenHistoryItemSchema$1),
    cursor: z.string().optional().nullable(),
});

const readIncomeHistoryQuerySchema = readInstitutionIncomesQuerySchema;
const incomesHistoryListSchema = institutionIncomesListSchema;
const institutionTotalIncomeSchema = institutionTotalIncomeSchema$1;

const userSchema = z.object({
    id: z.string(),
    first_name: z.string(),
    last_name: z.string(),
});
const changeSchema = z.object({
    removed: z.boolean().optional(),
    added: z.boolean().optional(),
    value: z.any(),
});
const changedFieldSchema = z.object({
    name: z.string(),
    changes: z.array(changeSchema),
});
const logEntrySchema = z.object({
    action: z.string(),
    user: userSchema.nullable(),
    date: z.string(),
    fields: z.array(changedFieldSchema),
});
const readOneActivityResponseSchema = z.array(logEntrySchema);
const readOneActivityQuerySchema = z.object({
    id: z.union([
        z.number(),
        z
            .string()
            .regex(/^\d+$/)
            .transform((sNum) => parseInt(sNum, 10)),
    ]),
    collection: z.enum([COLLECTIONS.ArtAssets, COLLECTIONS.Artists, COLLECTIONS.Documents]),
});

const UploadFileTag = DirectusFileTagsType;
const DownloadFileTag = {
    Document: 'document',
};
const readPhotoDataParamsSchema$1 = z.object({
    id: z.string(),
});

const usersNotificationSchema = z.object({
    collection: z.string(),
    date_created: z.coerce.date(),
    date_updated: z.coerce.date().nullable(),
    id: z.number().int(),
    institutions_directus_user: z.number().int(),
    viewed: z.boolean(),
    item: z.string(),
    type: z.string(),
    payload: z.record(z.any()),
});
const readManyUsersNotificationsRequestQuerySchema = z
    .object({
    viewed: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
    column: z.enum(['id', 'collection', 'type', 'date_created', 'date_updated']).optional().default('date_created'),
    order: orderSchema$1.optional().default('asc'),
})
    .merge(paginationQuerySchema);
const readManyUsersNotificationsResponseBodySchema = z.object({
    notifications: z.array(usersNotificationSchema),
    total_count: z.number(),
    viewed_count: z.number(),
});

const inviteColleagueParamsSchema = inviteInstitutionUserParamsSchema.omit({
    institution_id: true,
    directus_role_id: true,
});
const acceptColleagueInvitationParamsSchema = institutionUserAcceptInviteParamsSchema;
const resendColleagueInvitationParamsSchema = z.object({
    colleague_id: z.union([z.number().int(), z.string().transform(Number)]),
});
const editColleagueParamsSchema = inviteInstitutionUserParamsSchema
    .omit({
    institution_id: true,
    directus_role_id: true,
})
    .partial();
const readColleaguesListParamsSchema = readInstitutionUsersListQuerySchema.omit({
    institution_id: true,
});
const colleagueListItemDtoSchema = institutionUserListItemDtoSchema;
const colleaguesListDtoSchema = z.object({
    total: z.number(),
    items: z.array(colleagueListItemDtoSchema),
});
const readOneColleagueParamsSchema = z.object({
    colleague_id: z.union([z.number().int(), z.string().transform(Number)]),
});
const colleagueDtoSchema = institutionUserListItemDtoSchema;
const suspendColleagueParamsSchema = z.object({
    colleague_id: z.union([z.number().int(), z.string().transform(Number)]),
});
const revokeColleagueParamsSchema = z.object({
    colleague_id: z.union([z.number().int(), z.string().transform(Number)]),
});
const deleteColleagueParamsSchema = suspendColleagueParamsSchema;

const readTilesByFileIdParamsSchema$1 = readTilesByFileIdParamsSchema$2;
const tilesListSchema$1 = tilesListSchema$2;

const requestSiweNonceParamsSchema = requestSiweNonceParamsSchema$1;
const siweNonceDtoSchema = siweNonceDtoSchema$1;
const verifyWalletParamsSchema = verifyWalletParamsSchema$1;
const walletDtoSchema = walletDtoSchema$1;

const changeEmailRequestParamsSchema = changeEmailRequestParamsSchema$1;

const TokenScope = {
    ConfirmEmail: 'confirm-email',
    ChangeEmail: 'change-email',
    ResetPassword: 'reset-password',
};
const userSignUpRequestBodySchema = z.object({
    firstName: z.string(),
    lastName: z.string(),
    email: z.string().email(),
    password: strongPasswordSchema,
    role: z.string().uuid(),
});
const loginParamsSchema = z.object({
    email: z.string().email(),
    password: passwordSchema,
});
const confirmEmailParamsSchema = z.object({
    token: z.string(),
});
const loginResponseSchema = z.object({
    access_token: z.string().nullable(),
    refresh_token: z.string().nullable(),
    expires: z.number().int().nullable(),
    expires_at: z.number().int().nullable(),
});
const requestResetPasswordParamsSchema = z.object({
    email: z.string().email(),
});
const resetPasswordParamsSchema = z.object({
    token: z.string(),
    password: strongPasswordSchema,
});
const tokenPayloadSchema = z.object({
    email: z.string().email(),
    newEmail: z.string().email().optional(),
    exp: z.number().int(),
    scope: z.nativeEnum(TokenScope),
});
const changePasswordParamsSchema = z.object({
    old_password: passwordSchema,
    new_password: strongPasswordSchema,
});
const refreshTokenParamsSchema = z.object({
    token: z.string(),
});
const changeEmailParamsSchema = z.object({
    email: z.string(),
});

const readMeProfileSchema = z.object({
    first_name: z.string().nullable().optional(),
    last_name: z.string().nullable().optional(),
    email: z.string(),
    phone: z.string().nullable().optional(),
    institution_role: z.nativeEnum(InstitutionUserRole),
    institution_user_id: z.number(),
    art_assets_count: z.number(),
    artists_count: z.number(),
    documents_count: z.number(),
    incomes_count: z.number(),
    notifications_count: z.number(),
    auctions_count: z.number(),
    art_assets_requests_count: z.number(),
    selections_count: z.number(),
    notifications_notification: z.boolean(),
    art_assets_notification: z.boolean(),
    artists_notification: z.boolean(),
    auctions_notification: z.boolean(),
    art_assets_requests_notification: z.boolean(),
});
const updateMeProfileRequestSchema = z.object({
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    phone: z.string().optional(),
});

const artAssetRequestReadOneParamsSchema = z.object({
    id: z.coerce.number().positive().int(),
});
const artAssetRequestReadOneResponseSchema = artAssetRequestListItemSchema
    .omit({
    viewed: true,
})
    .merge(z.object({
    comment: z.string().nullable(),
}));
const readManyArtAssetsRequestsQuerySchema = readManyArtAssetsRequestsFiltersQuerySchema
    .omit({
    institution_id: true,
})
    .merge(artAssetsRequestsSortQuerySchema)
    .merge(paginationQuerySchema);
const artAssetRequestListSchema = artAssetRequestListSchema$1;

const updateOneCollectorRequestBodySchema = updateOneCollectorRequestBodySchema$1;
const updateOneCollectorRequestParamsSchema = z.object({
    id: z.string(),
});
const updateOneCollectorResponseBodySchema = z.object({
    id: z.string(),
});
const readOneCollectorRequestParamsSchema = z.object({
    id: z.string(),
});
const readOneCollectorResponseBodySchema$1 = readOneCollectorResponseSchema;

const createOneCuratorRequestBodySchema = curatorSchema;
const createOneCuratorResponseBodySchema = z.object({
    id: z.string(),
});
const updateOneCuratorRequestBodySchema = updateOneCuratorRequestBodySchema$1;
const updateOneCuratorRequestParamsSchema = z.object({
    id: z.string(),
});
const updateOneCuratorResponseBodySchema = z.object({
    id: z.string(),
});
const deleteOneCuratorParamsSchema = z.object({
    id: z.string(),
});
const readOneCuratorRequestParamsSchema$1 = z.object({
    id: z.string(),
});
const readOneCuratorResponseBodySchema = z.object({
    id: z.number(),
    slug: z.string(),
    first_name: z.string(),
    last_name: z.string().nullable(),
    nickname: z.string().nullable(),
    photo: photoSchema.nullable(),
    photo_attribution: z.string().nullable(),
    date_of_birth: z.string().nullable(),
    gender: z.nativeEnum(Gender).nullable(),
    biography: editorBlockSchema.nullable(),
    position: z.string().nullable().optional(),
    contacts: contactsSchema.nullable(),
    social_profiles: socialProfilesSchema.nullable(),
    locations: z.array(locationSchema).nullable(),
    date_created: z.string(),
    date_updated: z.string().nullable(),
});
const curatorFilterQuerySchema$1 = z.object({
    search: z.string().min(2).trim().optional(),
});
const readManyCuratorsQuerySchema$1 = curatorFilterQuerySchema$1.merge(paginationQuerySchema);
const readManyCuratorsResponseBodySchema = z.array(z.object({
    id: z.number(),
    slug: z.string().nullable(),
    photo: photoSchema.nullable(),
    first_name: z.string(),
    last_name: z.string(),
}));

const readManyCountriesRequestQuerySchema = readManyCountriesRequestQuerySchema$1;
const readManyCitiesRequestQueryFiltersSchema$1 = readManyCitiesRequestQueryFiltersSchema$2;
const readCitiesTotalCountResponseBodySchema$1 = z.object({ total_count: z.number() });
const readManyCitiesRequestQuerySchema$1 = z
    .object({
    country_id: z.string().optional(),
    search: z.string().optional(),
})
    .merge(paginationQuerySchema);

const readOneCuratorReviewRequestParamsSchema$1 = readOneCuratorReviewRequestParamsSchema$2;
const readOneCuratorsReviewsResponseBodySchema$1 = readOneCuratorsReviewsResponseBodySchema$2;
const readManyCuratorsReviewsRequestQuerySchema$1 = curatorsReviewsFilterQuerySchema
    .omit({ institution_id: true })
    .merge(curatorsReviewsSortQuerySchema)
    .merge(paginationQuerySchema);
const readManyCuratorsReviewsResponseBodySchema$1 = readManyCuratorsReviewsResponseBodySchema$2;
const createOneCuratorReviewRequestBodySchema = z.object({
    description: editorBlockSchema,
    curator: z.number(),
    art_asset: z.number(),
});
const createOneCuratorReviewResponseBodySchema = createOneCuratorReviewResponseBodySchema$1;
const updateOneCuratorReviewRequestParamsSchema = updateOneCuratorReviewRequestParamsSchema$1;
const updateOneCuratorReviewRequestBodySchema = z.object({
    description: editorBlockSchema.nullable().optional(),
});
const updateOneCuratorReviewResponseBodySchema = updateOneCuratorReviewResponseBodySchema$1;

const classificationsSchema = classificationsSchema$1;
const createOneTagRequestBodySchema = z.object({
    name: z.string(),
});
const createOneTagResponseBodySchema = createOneTagResponseBodySchema$1;
const classificationItemSchema$1 = z.object({
    id: z.number(),
    name: z.string(),
    slug: z.string(),
    institution_id: z.string().nullable(),
});
const stylesSchema$1 = z.array(classificationItemSchema$1);
const readManyStylesResponseSchema$1 = z.array(classificationItemSchema$1.omit({ institution_id: true }).merge(z.object({
    isGlobal: z.boolean(),
})));
const createOneStyleRequestBodySchema = z.object({
    name: z.string(),
});
const createOneStyleResponseSchema = createOneStyleResponseSchema$1;
const mediumsSchema$1 = z.array(classificationItemSchema$1);
const readManyMediumsResponseSchema$1 = z.array(classificationItemSchema$1.omit({ institution_id: true }).merge(z.object({
    isGlobal: z.boolean(),
})));
const createOneMediumRequestBodySchema = z.object({
    name: z.string(),
});
const createOneMediumResponseSchema = createOneMediumResponseSchema$1;

const createArtistsWaitlistItemResponseSchema = z.object({
    id: z.number(),
});
const createArtistsWaitlistItemBodySchema = z.object({
    first_name: z
        .string({
        errorMap: () => ({
            message: "The artist's first name must be 2-100 symbols long.",
        }),
    })
        .trim()
        .min(2)
        .max(100),
    last_name: z
        .string({
        errorMap: () => ({
            message: "The artist's last name must be 2-100 symbols long.",
        }),
    })
        .min(2)
        .trim()
        .max(100),
    pseudonym: z
        .string({
        errorMap: () => ({
            message: "The artist's pseudonym must be 1-100 symbols long.",
        }),
    })
        .trim()
        .min(1)
        .max(100)
        .nullable()
        .optional(),
    email: z.string().email().trim(),
    galleries: z.string().trim().nullable().optional(),
    website_url: z.string().trim().url().nullable().optional(),
    instagram_url: z
        .string()
        .trim()
        .url('Invalid url')
        .refine((url) => url.includes('instagram.com/'), {
        message: "URL must contain 'instagram.com/'",
    })
        .nullable()
        .optional(),
});

const readOneSelectionRequestQuerySchema$1 = z
    .object({
    token: z.string(),
})
    .partial();
const readOneSelectionRequestParamsSchema$1 = readOneSelectionRequestParamsSchema$2;
const readOneSelectionResponseBodySchema$1 = readOneSelectionResponseBodySchema$2;
const readManySelectionsRequestSortQuerySchema$1 = readManySelectionsRequestSortQuerySchema$2;
const readManySelectionsRequestFilterQuerySchema$1 = readManySelectionsRequestFilterQuerySchema$2.pick({
    search: true,
    art_assets: true,
    art_assets_slugs: true,
    artists: true,
    artists_slugs: true,
});
const readManySelectionsRequestQuerySchema$1 = readManySelectionsRequestSortQuerySchema$1
    .partial()
    .merge(readManySelectionsRequestFilterQuerySchema$1)
    .merge(paginationQuerySchema.partial());
const readManySelectionsResponseBodySchema$1 = z.object({
    selections: readManySelectionsResponseBodySchema$2,
    total_count: z.number(),
});
const readManySelectionsTotalCountResponseBodySchema$1 = z.object({
    total_count: z.number(),
});
const readSelectionsListResponseBodySchema = readManySelectionsResponseBodySchema$2;
const createOneSelectionRequestBodySchema = createOneSelectionRequestBodySchema$1.omit({
    institution_id: true,
});
const createOneSelectionResponseBodySchema = createOneSelectionResponseBodySchema$1;
const updateOneSelectionRequestParamsSchema = updateOneSelectionRequestParamsSchema$1;
const updateOneSelectionRequestBodySchema = updateOneSelectionRequestBodySchema$1.omit({
    institution_id: true,
});
const updateOneSelectionResponseBodySchema = createOneSelectionResponseBodySchema$1;

const readViewsRequestQuerySchema = z.object({
    propertyId: z.string(),
    pagePath: z.string(),
    startDate: z.string(),
    endDate: z.string(),
});
const readTotalViewsResponseBodySchema = z.number().int();
const readDailyViewsResponseBodySchema = z.array(z.object({
    date: z.string(),
    views: z.number(),
}));
const readMultipleViewsRequestQuerySchema = z.object({
    propertyId: z.string(),
    pagePaths: arrayStringsOption$3,
    startDate: z.string(),
    endDate: z.string(),
});
const readMultipleDailyViewsRequestQuerySchema = z.array(z.object({
    pagePath: z.string(),
    views: z.number(),
}));

var index$2 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    createOneAgencyRequestBodySchema: createOneAgencyRequestBodySchema,
    createOneAgencyResponseBodySchema: createOneAgencyResponseBodySchema,
    updateOneAgencyRequestBodySchema: updateOneAgencyRequestBodySchema,
    updateOneAgencyResponseBodySchema: updateOneAgencyResponseBodySchema,
    updateOneAgencyQuerySchema: updateOneAgencyQuerySchema,
    readOneAgencyResponseBodySchema: readOneAgencyResponseBodySchema$1,
    readOneAgencyQuerySchema: readOneAgencyQuerySchema$1,
    agencyIdParamsSchema: agencyIdParamsSchema$1,
    auctionEstimationSchema: auctionEstimationSchema,
    estimateAuctionParamsSchema: estimateAuctionParamsSchema,
    readOneAuctionParamsSchema: readOneAuctionParamsSchema$1,
    readAuctionSharesResponseSchema: readAuctionSharesResponseSchema,
    auctionSchema: auctionSchema,
    readAuctionSharesByArtAssetIdParamsSchema: readAuctionSharesByArtAssetIdParamsSchema,
    createAuctionParamsSchema: createAuctionParamsSchema,
    createAuctionResponseSchema: createAuctionResponseSchema,
    updateAuctionParamsSchema: updateAuctionParamsSchema,
    auctionListItemSchema: auctionListItemSchema$1,
    readManyAuctionsQuerySchema: readManyAuctionsQuerySchema$1,
    auctionListSchema: auctionListSchema,
    approveAuctionRunParamsSchema: approveAuctionRunParamsSchema,
    auctionRunApprovalSchema: auctionRunApprovalSchema,
    readArtistFilterOptionsResponseSchema: readArtistFilterOptionsResponseSchema$1,
    artistFilterQuerySchema: artistFilterQuerySchema,
    ArtistSortColumn: ArtistSortColumn,
    artistSortQuerySchema: artistSortQuerySchema,
    artistSortLiteQuerySchema: artistSortLiteQuerySchema,
    artistInviteParamsSchema: artistInviteParamsSchema,
    readManyArtistsQuerySchema: readManyArtistsQuerySchema$1,
    readManyArtistsResponseSchema: readManyArtistsResponseSchema$1,
    readManyArtistsLiteQuerySchema: readManyArtistsLiteQuerySchema,
    readManyArtistsLiteResponseSchema: readManyArtistsLiteResponseSchema,
    readOneArtistParamsSchema: readOneArtistParamsSchema$1,
    artistPortfolioValueSchema: artistPortfolioValueSchema,
    readOneArtistResponseSchema: readOneArtistResponseSchema$1,
    inviteArtistVerificationResponseSchema: inviteArtistVerificationResponseSchema,
    updateOneArtistParamsSchema: updateOneArtistParamsSchema,
    artistUnpublishParamsSchema: artistUnpublishParamsSchema,
    artistPublishParamsSchema: artistPublishParamsSchema,
    deleteOneArtistParamsSchema: deleteOneArtistParamsSchema,
    createOneGalleryRequestBodySchema: createOneGalleryRequestBodySchema,
    createOneGalleryResponseBodySchema: createOneGalleryResponseBodySchema,
    updateOneGalleryRequestBodySchema: updateOneGalleryRequestBodySchema,
    updateOneGalleryResponseBodySchema: updateOneGalleryResponseBodySchema,
    updateOneGalleryQuerySchema: updateOneGalleryQuerySchema,
    readOneGalleryResponseBodySchema: readOneGalleryResponseBodySchema$1,
    readOneGalleryQuerySchema: readOneGalleryQuerySchema$1,
    galleryIdParamsSchema: galleryIdParamsSchema$1,
    createSharePayloadSchema: createSharePayloadSchema,
    createSaleOrderParamsSchema: createSaleOrderParamsSchema,
    estimateSaleOrderParamsSchema: estimateSaleOrderParamsSchema,
    saleOrderEstimationResponseSchema: saleOrderEstimationResponseSchema,
    cancelSaleOrderParamsSchema: cancelSaleOrderParamsSchema,
    readSaleOrderSharesResponseSchema: readSaleOrderSharesResponseSchema,
    readActualSaleOrderSharesParamsSchema: readActualSaleOrderSharesParamsSchema,
    readInstitutionStatsSchema: readInstitutionStatsSchema,
    institutionSignUpRequestBodySchema: institutionSignUpRequestBodySchema,
    institutionsAddStoreDomainParamsSchema: institutionsAddStoreDomainParamsSchema,
    institutionStoreDomainDtoSchema: institutionStoreDomainDtoSchema$1,
    updateInstitutionStoreFaviconRequestBodySchema: updateInstitutionStoreFaviconRequestBodySchema,
    readStoreConfigurationsResponseSchema: readStoreConfigurationsResponseSchema$1,
    institutionStoreDnsConfigDtoSchema: institutionStoreDnsConfigDtoSchema,
    readInstitutionProfileSchema: readInstitutionProfileSchema,
    institutionSubscriptionsReadManyQueryParamSchema: institutionSubscriptionsReadManyQueryParamSchema,
    institutionSubscriptionsListResponseSchema: institutionSubscriptionsListResponseSchema,
    institutionSubscriptionDeleteOneParamsSchema: institutionSubscriptionDeleteOneParamsSchema,
    institutionSubscriptionExportDataResponseSchema: institutionSubscriptionExportDataResponseSchema,
    importInstitutionSubscriptionsRequestBodySchema: importInstitutionSubscriptionsRequestBodySchema,
    importInstitutionSubscriptionsResponseSchema: importInstitutionSubscriptionsResponseSchema,
    createArtAssetAddArtistPayloadSchema: createArtAssetAddArtistPayloadSchema,
    isCreateArtAssetAddArtistPayload: isCreateArtAssetAddArtistPayload,
    createArtAssetNewArtistPayloadSchema: createArtAssetNewArtistPayloadSchema,
    isCreateArtAssetNewArtistPayloadSchema: isCreateArtAssetNewArtistPayloadSchema,
    creationYearSchema: creationYearSchema,
    createArtAssetParamsSchema: createArtAssetParamsSchema,
    createArtAssetResponseBodySchema: createArtAssetResponseBodySchema,
    createManyArtAssetsFromPhotosRequestBodySchema: createManyArtAssetsFromPhotosRequestBodySchema,
    createManyArtAssetsFromPhotosResponseBodySchema: createManyArtAssetsFromPhotosResponseBodySchema,
    createOneArtAssetFromPhotoRequestBodySchema: createOneArtAssetFromPhotoRequestBodySchema,
    createOneArtAssetFromPhotoResponseBodySchema: createOneArtAssetFromPhotoResponseBodySchema,
    updateArtAssetAddArtistPayloadSchema: updateArtAssetAddArtistPayloadSchema,
    isUpdateArtAssetAddArtistPayload: isUpdateArtAssetAddArtistPayload,
    updateArtAssetNewArtistPayloadSchema: updateArtAssetNewArtistPayloadSchema,
    isUpdateArtistAssetNewArtistPayloadSchema: isUpdateArtistAssetNewArtistPayloadSchema,
    updateArtAssetParamsSchema: updateArtAssetParamsSchema,
    artAssetStatusChangeParamsSchema: artAssetStatusChangeParamsSchema,
    artAssetReadOneParamsSchema: artAssetReadOneParamsSchema,
    artAssetDtoSchema: artAssetDtoSchema,
    orderSchema: orderSchema,
    minMaxSchema: minMaxSchema,
    artAssetsReadManyQueryParamsSchema: artAssetsReadManyQueryParamsSchema,
    artAssetListItemDtoSchema: artAssetListItemDtoSchema,
    artAssetListDtoSchema: artAssetListDtoSchema,
    artAssetsReadManyResponseBody: artAssetsReadManyResponseBody,
    artAssetsReadManyStatsQueryParamsSchema: artAssetsReadManyStatsQueryParamsSchema$1,
    artAssetReadManyStatsResponseBodySchema: artAssetReadManyStatsResponseBodySchema$1,
    readManyUFDAPhotosResponseBodySchema: readManyUFDAPhotosResponseBodySchema,
    artAssetReadFilterOptionsResponseSchema: artAssetReadFilterOptionsResponseSchema$1,
    readTokensHistoryQuerySchema: readTokensHistoryQuerySchema$1,
    tokensHistoryIncomeSchema: tokensHistoryIncomeSchema$1,
    baseTokensHistoryItemSchema: baseTokensHistoryItemSchema$1,
    tokenHistoryTransferItemSchema: tokenHistoryTransferItemSchema$1,
    tokenHistoryMintItemSchema: tokenHistoryMintItemSchema$1,
    tokenAuctionCreatedItemSchema: tokenAuctionCreatedItemSchema$1,
    tokenAuctionBidPlacedItemSchema: tokenAuctionBidPlacedItemSchema$1,
    tokenAuctionSoldItemSchema: tokenAuctionSoldItemSchema$1,
    tokenHistoryItemSchema: tokenHistoryItemSchema$1,
    tokenHistoryResponseSchema: tokenHistoryResponseSchema$1,
    readIncomeHistoryQuerySchema: readIncomeHistoryQuerySchema,
    incomesHistoryListSchema: incomesHistoryListSchema,
    institutionTotalIncomeSchema: institutionTotalIncomeSchema,
    logEntrySchema: logEntrySchema,
    readOneActivityResponseSchema: readOneActivityResponseSchema,
    readOneActivityQuerySchema: readOneActivityQuerySchema,
    UploadFileTag: UploadFileTag,
    DownloadFileTag: DownloadFileTag,
    readPhotoDataParamsSchema: readPhotoDataParamsSchema$1,
    usersNotificationSchema: usersNotificationSchema,
    readManyUsersNotificationsRequestQuerySchema: readManyUsersNotificationsRequestQuerySchema,
    readManyUsersNotificationsResponseBodySchema: readManyUsersNotificationsResponseBodySchema,
    inviteColleagueParamsSchema: inviteColleagueParamsSchema,
    acceptColleagueInvitationParamsSchema: acceptColleagueInvitationParamsSchema,
    resendColleagueInvitationParamsSchema: resendColleagueInvitationParamsSchema,
    editColleagueParamsSchema: editColleagueParamsSchema,
    readColleaguesListParamsSchema: readColleaguesListParamsSchema,
    colleagueListItemDtoSchema: colleagueListItemDtoSchema,
    colleaguesListDtoSchema: colleaguesListDtoSchema,
    readOneColleagueParamsSchema: readOneColleagueParamsSchema,
    colleagueDtoSchema: colleagueDtoSchema,
    suspendColleagueParamsSchema: suspendColleagueParamsSchema,
    revokeColleagueParamsSchema: revokeColleagueParamsSchema,
    deleteColleagueParamsSchema: deleteColleagueParamsSchema,
    readTilesByFileIdParamsSchema: readTilesByFileIdParamsSchema$1,
    tilesListSchema: tilesListSchema$1,
    requestSiweNonceParamsSchema: requestSiweNonceParamsSchema,
    siweNonceDtoSchema: siweNonceDtoSchema,
    verifyWalletParamsSchema: verifyWalletParamsSchema,
    walletDtoSchema: walletDtoSchema,
    changeEmailRequestParamsSchema: changeEmailRequestParamsSchema,
    TokenScope: TokenScope,
    userSignUpRequestBodySchema: userSignUpRequestBodySchema,
    loginParamsSchema: loginParamsSchema,
    confirmEmailParamsSchema: confirmEmailParamsSchema,
    loginResponseSchema: loginResponseSchema,
    requestResetPasswordParamsSchema: requestResetPasswordParamsSchema,
    resetPasswordParamsSchema: resetPasswordParamsSchema,
    tokenPayloadSchema: tokenPayloadSchema,
    changePasswordParamsSchema: changePasswordParamsSchema,
    refreshTokenParamsSchema: refreshTokenParamsSchema,
    changeEmailParamsSchema: changeEmailParamsSchema,
    readMeProfileSchema: readMeProfileSchema,
    updateMeProfileRequestSchema: updateMeProfileRequestSchema,
    artAssetRequestReadOneParamsSchema: artAssetRequestReadOneParamsSchema,
    artAssetRequestReadOneResponseSchema: artAssetRequestReadOneResponseSchema,
    readManyArtAssetsRequestsQuerySchema: readManyArtAssetsRequestsQuerySchema,
    artAssetRequestListSchema: artAssetRequestListSchema,
    updateOneCollectorRequestBodySchema: updateOneCollectorRequestBodySchema,
    updateOneCollectorRequestParamsSchema: updateOneCollectorRequestParamsSchema,
    updateOneCollectorResponseBodySchema: updateOneCollectorResponseBodySchema,
    readOneCollectorRequestParamsSchema: readOneCollectorRequestParamsSchema,
    readOneCollectorResponseBodySchema: readOneCollectorResponseBodySchema$1,
    createOneCuratorRequestBodySchema: createOneCuratorRequestBodySchema,
    createOneCuratorResponseBodySchema: createOneCuratorResponseBodySchema,
    updateOneCuratorRequestBodySchema: updateOneCuratorRequestBodySchema,
    updateOneCuratorRequestParamsSchema: updateOneCuratorRequestParamsSchema,
    updateOneCuratorResponseBodySchema: updateOneCuratorResponseBodySchema,
    deleteOneCuratorParamsSchema: deleteOneCuratorParamsSchema,
    readOneCuratorRequestParamsSchema: readOneCuratorRequestParamsSchema$1,
    readOneCuratorResponseBodySchema: readOneCuratorResponseBodySchema,
    curatorFilterQuerySchema: curatorFilterQuerySchema$1,
    readManyCuratorsQuerySchema: readManyCuratorsQuerySchema$1,
    readManyCuratorsResponseBodySchema: readManyCuratorsResponseBodySchema,
    readManyCountriesRequestQuerySchema: readManyCountriesRequestQuerySchema,
    readManyCitiesRequestQueryFiltersSchema: readManyCitiesRequestQueryFiltersSchema$1,
    readCitiesTotalCountResponseBodySchema: readCitiesTotalCountResponseBodySchema$1,
    readManyCitiesRequestQuerySchema: readManyCitiesRequestQuerySchema$1,
    readOneCuratorReviewRequestParamsSchema: readOneCuratorReviewRequestParamsSchema$1,
    readOneCuratorsReviewsResponseBodySchema: readOneCuratorsReviewsResponseBodySchema$1,
    readManyCuratorsReviewsRequestQuerySchema: readManyCuratorsReviewsRequestQuerySchema$1,
    readManyCuratorsReviewsResponseBodySchema: readManyCuratorsReviewsResponseBodySchema$1,
    createOneCuratorReviewRequestBodySchema: createOneCuratorReviewRequestBodySchema,
    createOneCuratorReviewResponseBodySchema: createOneCuratorReviewResponseBodySchema,
    updateOneCuratorReviewRequestParamsSchema: updateOneCuratorReviewRequestParamsSchema,
    updateOneCuratorReviewRequestBodySchema: updateOneCuratorReviewRequestBodySchema,
    updateOneCuratorReviewResponseBodySchema: updateOneCuratorReviewResponseBodySchema,
    classificationsSchema: classificationsSchema,
    createOneTagRequestBodySchema: createOneTagRequestBodySchema,
    createOneTagResponseBodySchema: createOneTagResponseBodySchema,
    stylesSchema: stylesSchema$1,
    readManyStylesResponseSchema: readManyStylesResponseSchema$1,
    createOneStyleRequestBodySchema: createOneStyleRequestBodySchema,
    createOneStyleResponseSchema: createOneStyleResponseSchema,
    mediumsSchema: mediumsSchema$1,
    readManyMediumsResponseSchema: readManyMediumsResponseSchema$1,
    createOneMediumRequestBodySchema: createOneMediumRequestBodySchema,
    createOneMediumResponseSchema: createOneMediumResponseSchema,
    createArtistsWaitlistItemResponseSchema: createArtistsWaitlistItemResponseSchema,
    createArtistsWaitlistItemBodySchema: createArtistsWaitlistItemBodySchema,
    readOneSelectionRequestQuerySchema: readOneSelectionRequestQuerySchema$1,
    readOneSelectionRequestParamsSchema: readOneSelectionRequestParamsSchema$1,
    readOneSelectionResponseBodySchema: readOneSelectionResponseBodySchema$1,
    readManySelectionsRequestSortQuerySchema: readManySelectionsRequestSortQuerySchema$1,
    readManySelectionsRequestFilterQuerySchema: readManySelectionsRequestFilterQuerySchema$1,
    readManySelectionsRequestQuerySchema: readManySelectionsRequestQuerySchema$1,
    readManySelectionsResponseBodySchema: readManySelectionsResponseBodySchema$1,
    readManySelectionsTotalCountResponseBodySchema: readManySelectionsTotalCountResponseBodySchema$1,
    readSelectionsListResponseBodySchema: readSelectionsListResponseBodySchema,
    createOneSelectionRequestBodySchema: createOneSelectionRequestBodySchema,
    createOneSelectionResponseBodySchema: createOneSelectionResponseBodySchema,
    updateOneSelectionRequestParamsSchema: updateOneSelectionRequestParamsSchema,
    updateOneSelectionRequestBodySchema: updateOneSelectionRequestBodySchema,
    updateOneSelectionResponseBodySchema: updateOneSelectionResponseBodySchema,
    readViewsRequestQuerySchema: readViewsRequestQuerySchema,
    readTotalViewsResponseBodySchema: readTotalViewsResponseBodySchema,
    readDailyViewsResponseBodySchema: readDailyViewsResponseBodySchema,
    readMultipleViewsRequestQuerySchema: readMultipleViewsRequestQuerySchema,
    readMultipleDailyViewsRequestQuerySchema: readMultipleDailyViewsRequestQuerySchema
});

const readOneAgencyResponseBodySchema = readOneAgencyResponseBodySchema$2;
const readOneAgencyQuerySchema = readOneAgencyQuerySchema$2;
const agencyIdParamsSchema = agencyIdParamsSchema$2;

const readArtistWalletAuthorizationParamsSchema = z.object({
    id: z.string(),
});
const readArtistWalletAuthorizationQuerySchema = z.object({
    ip: z.string(),
});
const readArtistWalletAuthorizationResponseSchema = readArtistWalletAuthorizationDataResponseSchema;
const requestArtistWalletNonceParamsSchema = z.object({
    address: z.string().regex(/^0x[a-fA-F0-9]{40}$/),
    id: z.string().uuid(),
});
const artistWalletNonceResponseSchema = z.object({
    nonce: z.string(),
    token: z.string(),
});
const verifyArtistWalletParamsSchema = z.object({
    address: z.string().regex(/^0x[a-fA-F0-9]{40}$/),
    message: z.string(),
    signature: z.string(),
    token: z.string(),
    id: z.string().uuid(),
});
const artistWalletDtoSchema = z.object({
    id: z.string().uuid(),
    address: z.string().regex(/^0x[a-fA-F0-9]{40}$/),
    signature: z.string(),
    nonce: z.string(),
    signed_message: z.string(),
    is_platform_address: z.boolean(),
    artists: z.array(z.number()).length(1),
});
const unauthorizeArtistWalletParamsSchema = z.object({
    id: z.string().uuid(),
});
const readManyArtistsStatsResponseSchema = z.object({
    total_count: z.number(),
});
const artistListItemSchema = z.object({
    id: z.number(),
    slug: z.string().nullable(),
    name: z.string(),
    date_created: z.string(),
    date_updated: z.string().nullable(),
    portfolio_value: z.string(),
    verification_status: z.nativeEnum(ArtistVerificationStatus),
    verified: z.boolean(),
    featured: z.boolean(),
    date_of_birth: z.string().nullable(),
    date_of_death: z.string().nullable(),
    countries: z
        .array(z.object({
        id: z.number().nullable(),
        name: z.string().nullable(),
        slug: z.string().nullable(),
    }))
        .nullable(),
    photo: photoSchema.nullable(),
    artist_type: z.nativeEnum(ArtistType),
    art_assets_count: z.number(),
});
const artistsListSchema = z.array(artistListItemSchema);
const readManyArtistsResponseSchema = z
    .object({
    artists: artistsListSchema,
})
    .and(readManyArtistsStatsResponseSchema);
const readManyArtistsFilterQuerySchema = z.object({
    institutions: arrayIdsOption$2.optional(),
    artists: arrayIdsOption$2.optional(),
    character: z
        .string()
        .regex(/^[a-zA-Z]$/, 'Value must be a single alphabetic character')
        .optional(),
    featured: z
        .string()
        .transform((v) => v === 'true')
        .optional(),
    mediums: arrayIdsOption$2.optional(),
    mediums_slugs: arrayStringsOption$3.optional(),
    styles: arrayIdsOption$2.optional(),
    styles_slugs: arrayStringsOption$3.optional(),
    countries: arrayIdsOption$2.optional(),
    countries_slugs: arrayStringsOption$3.optional(),
    price: minMaxOptionSchema.optional(),
    date_of_birth: minMaxOptionSchema.optional(),
    search: z.string().min(2).optional(),
});
const readManyArtistsQuerySchema = readManyArtistsFilterQuerySchema
    .merge(z.object({
    column: z
        .enum([
        ArtistSortColumn$1.Name,
        ArtistSortColumn$1.DateUpdated,
        ArtistSortColumn$1.DateOfBirth,
        ArtistSortColumn$1.PortfolioValue,
        ArtistSortColumn$1.ArtAssetsCount,
    ])
        .optional()
        .default(ArtistSortColumn$1.Name),
    order: orderSchema$1.optional().default(Order.Asc),
}))
    .merge(paginationQuerySchema);
const readArtistsStatsQuerySchema = readManyArtistsFilterQuerySchema;
const firstLettersResponseSchema = z.array(z.string());
const readOneArtistParamsSchema = z.object({
    id: z.string().transform((v) => Number(v)),
});
const artAssetsOptionsSchema = z.object({
    styles: z.array(optionSchema),
    medium: z.array(optionSchema),
});
const readOneArtistResponseSchema = z.object({
    id: z.number(),
    name: z.string(),
    status: z.nativeEnum(ArtistStatus),
    verification_status: z.nativeEnum(ArtistVerificationStatus),
    photo: photoSchema.nullable(),
    slug: z.string().nullable(),
    biography: editorBlockSchema.nullable(),
    photo_attribution: z.string().nullable(),
    locations: z.array(z.object({
        country: z.object({
            id: z.number(),
            value: z.string(),
        }),
    })),
    social_profiles: socialProfilesSchema,
    documents: z.array(z.object({
        id: z.number(),
        name: z.string(),
        type: z.nativeEnum(DocumentType).or(z.string()),
        subtype: z.nativeEnum(DocumentSubtype).or(z.string()).nullable(),
    })),
    institutions: z.array(z.object({
        id: z.number(),
        name: z.string(),
    })),
    verified: z.boolean(),
    medium: z.array(optionSchema),
    styles: z.array(optionSchema),
    portfolio_value: z.number(),
    date_of_death: z.string().nullable(),
    birth_year: z.number().nullable(),
});
const artistFilterOption = optionSchema.merge(z.object({ artists_count: z.number() }));
const readArtistFilterOptionsResponseSchema = z.object({
    styles: z.array(artistFilterOption),
    countries: z.array(artistFilterOption),
    medium: z.array(artistFilterOption),
    items_total_count: z.number(),
    date_of_birth: minMaxSchema$1.optional(),
});
const readManyArtistsUFDAPhotosResponseSchema = readManyArtistsUFDAPhotosResponseSchema$1;

const readManyArtAssetsQuerySchema = z
    .object({
    artists: arrayIdsOption$2.optional(),
    artists_slugs: arrayStringsOption$3.optional(),
    medium: arrayIdsOption$2.optional(),
    medium_slugs: arrayStringsOption$3.optional(),
    styles: arrayIdsOption$2.optional(),
    styles_slugs: arrayStringsOption$3.optional(),
    countries: arrayIdsOption$2.optional(),
    countries_slugs: arrayStringsOption$3.optional(),
    institutions: arrayIdsOption$2.optional(),
    original_size_x: minMaxOptionSchema.optional(),
    original_size_y: minMaxOptionSchema.optional(),
    original_size_z: minMaxOptionSchema.optional(),
    price: minMaxOptionSchema.optional(),
    dlct: booleanOptionSchema.optional(),
    exclude_art_assets: arrayIdsOption$2.optional(),
    column: z
        .enum(['name_original', 'name_english', 'date_created', 'date_updated', 'year', 'artist', 'price'])
        .optional()
        .default('name_english'),
    order: z.enum(['asc', 'desc']).optional().default('asc'),
    token_status: arrayStringsOption$3.optional(),
    search: z.string().min(2).optional(),
    creation_year: minMaxOptionSchema
        .refine(([min, max]) => min <= max, {
        message: 'In ranges of creation years, the start must be less than or equal to the end.',
    })
        .optional(),
    curator_review: booleanOptionSchema.optional(),
    curators: arrayIdsOption$2.optional(),
    curators_slugs: arrayStringsOption$3.optional(),
    art_asset_type: arrayStringsOption$3.optional(),
})
    .merge(paginationQuerySchema);
const readManyArtAssetsResponseSchema = z.object({
    art_assets: z.array(z.object({
        id: z.number(),
        status: z.nativeEnum(Status),
        slug: z.string().nullable(),
        token_status: z.nativeEnum(TokenStatus),
        name_english: z.string(),
        name_original: z.string().nullable().optional(),
        art_asset_type: z.nativeEnum(ArtAssetType),
        format: z.nativeEnum(ArtAssetFormat),
        content_flags: z.array(z.nativeEnum(ArtAssetContentFlags)),
        creation_year: z.string().nullable().optional(),
        estimate_price: z.number().nullable(),
        realized_price: z.number().nullable(),
        physical_purchase_price: z.number().nullable(),
        initial_price: z.number().nullable(),
        date_created: z.string(),
        date_updated: z.string().nullable(),
        viewed: z.boolean(),
        sale_order: artAssetSaleOrderInfoSchema.optional().nullable(),
        auction: z
            .object({
            id: z.string().uuid(),
            bid_step_value: z.string(),
            buyer_fee_premium_percent: z.number(),
            best_bid: readLatestAuctionBidResponseSchema.optional().nullable(),
            currency: currencySchema,
            start_date: z.coerce.date().nullable().optional(),
            end_date: z.coerce.date().nullable().optional(),
            duration: z.number().int(),
            initial_bid_value: z.string(),
            secondary_market_royalty: z.number(),
            seller_fee_percent: z.number(),
            status: z.nativeEnum(AuctionStatus),
            storage_fee_value: z.string(),
        })
            .optional()
            .nullable(),
        main_photo: photoSchema.nullable(),
        digital_original: z
            .object({
            file_id: z.string(),
            width: z.string().nullable().optional(),
            height: z.string().nullable().optional(),
            size: z.string().nullable().optional(),
            mpx: z.number().nullable().nullable(),
        })
            .nullable(),
        digital_common: z
            .object({
            id: z.number(),
            preview: z.string().nullable(),
            file: z.string().nullable(),
            filesize: z.string().nullable(),
            filename_disk: z.string().nullable(),
            type: z.string().nullable(),
        })
            .nullable(),
        artists: z
            .array(z.object({
            id: z.number(),
            status: z.nativeEnum(ArtistStatus),
            name: z.string(),
            verification_status: z.nativeEnum(ArtistVerificationStatus),
        }))
            .nullable(),
        institutions: z
            .array(z.object({
            id: z.number(),
            name: z.string(),
        }))
            .nullable(),
        original_size_x: z.number().nullable().optional(),
        original_size_y: z.number().nullable().optional(),
        original_size_z: z.number().nullable().optional(),
        documents_count: z.number(),
        featured: z.boolean(),
    })),
    total_count: z.number(),
});
const readArtAssetsTotalCountQuerySchema = z.object({
    artists: arrayIdsOption$2.optional(),
    artists_slugs: arrayStringsOption$3.optional(),
    medium: arrayIdsOption$2.optional(),
    medium_slugs: arrayStringsOption$3.optional(),
    styles: arrayIdsOption$2.optional(),
    styles_slugs: arrayStringsOption$3.optional(),
    countries: arrayIdsOption$2.optional(),
    countries_slugs: arrayStringsOption$3.optional(),
    institutions: arrayIdsOption$2.optional(),
    original_size_x: minMaxOptionSchema.optional(),
    original_size_y: minMaxOptionSchema.optional(),
    original_size_z: minMaxOptionSchema.optional(),
    price: minMaxOptionSchema.optional(),
    dlct: booleanOptionSchema.optional(),
    token_status: arrayStringsOption$3.optional(),
    search: z.string().min(2).optional(),
    creation_year: minMaxOptionSchema
        .refine(([min, max]) => min <= max, {
        message: 'In ranges of creation years, the start must be less than or equal to the end.',
    })
        .optional(),
    curator_review: booleanOptionSchema.optional(),
    curators: arrayIdsOption$2.optional(),
    curators_slugs: arrayStringsOption$3.optional(),
    art_asset_type: arrayStringsOption$3.optional(),
});
const readArtAssetsTotalCountResponseSchema = z.number();
const readManyArtAssetsByOwnerWalletRequestQuerySchema = readManyArtAssetsByOwnerWalletRequestQuerySchema$1;
const readManyArtAssetsByOwnerWalletResponseBodySchema = readManyArtAssetsByOwnerWalletResponseBodySchema$1;
const readOneArtAssetParamsSchema = z.object({
    id: z.string().transform((v) => parseInt(v)),
});
const readOneArtAssetQuerySchema = z.object({
    without_similar: z
        .enum(['true', 'false'])
        .optional()
        .transform((v) => v === 'true'),
});
const readOneArtAssetResponseSchema = readOneArtAssetResponseSchema$1.unwrap().and(z.object({
    uris: z
        .object({
        metadata: z.string().url(),
        image: z.string().url(),
        digital_original: z.string().url(),
    })
        .or(z.object({}))
        .optional(),
    token_owner: z
        .object({
        value: z.string(),
        explorer_url: z.string(),
    })
        .optional(),
    latest_transfer_timestamp: z.number().nullable().optional(),
}));
const readManyVRArtAssetsQuerySchema = z
    .object({
    institutions: arrayIdsOption$2.optional(),
    column: z
        .enum(['name_original', 'name_english', 'date_created', 'date_updated', 'year', 'artist', 'price'])
        .optional()
        .default('date_created'),
    order: z.enum(['asc', 'desc']).optional().default('desc'),
})
    .merge(paginationQuerySchema);
const readManyVRArtAssetsResponseSchema = z
    .object({
    art_assets: z.array(z.object({
        id: z.number(),
        status: z.nativeEnum(Status),
        slug: z.string().nullable(),
        token_status: z.nativeEnum(TokenStatus),
        name_english: z.string(),
        name_original: z.string().nullable().optional(),
        art_asset_type: z.nativeEnum(ArtAssetType),
        format: z.nativeEnum(ArtAssetFormat),
        content_flags: z.array(z.nativeEnum(ArtAssetContentFlags)),
        creation_year: z.string().nullable().optional(),
        estimate_price: z.number().nullable(),
        realized_price: z.number().nullable(),
        physical_purchase_price: z.number().nullable(),
        initial_price: z.number().nullable(),
        date_created: z.string(),
        date_updated: z.string().nullable(),
        sale_order: artAssetSaleOrderInfoSchema.optional().nullable(),
        auction: z
            .object({
            id: z.string().uuid(),
            bid_step_value: z.string(),
            buyer_fee_premium_percent: z.number(),
            best_bid: readLatestAuctionBidResponseSchema.optional().nullable(),
            currency: currencySchema,
            start_date: z.coerce.date().nullable().optional(),
            end_date: z.coerce.date().nullable().optional(),
            duration: z.number().int(),
            initial_bid_value: z.string(),
            secondary_market_royalty: z.number(),
            seller_fee_percent: z.number(),
            status: z.nativeEnum(AuctionStatus),
            storage_fee_value: z.string(),
        })
            .optional()
            .nullable(),
        main_photo: photoSchema.nullable(),
        digital_original: z
            .object({
            file_id: z.string(),
            width: z.string().nullable().optional(),
            height: z.string().nullable().optional(),
            size: z.string().nullable().optional(),
            mpx: z.number().nullable().nullable(),
        })
            .nullable(),
        digital_common: z
            .object({
            id: z.number(),
            preview: z.string().nullable(),
            file: z.string().nullable(),
            filesize: z.string().nullable(),
            filename_disk: z.string().nullable(),
            type: z.string().nullable(),
        })
            .nullable(),
        artists: z
            .array(z.object({
            id: z.number(),
            status: z.nativeEnum(ArtistStatus),
            name: z.string(),
        }))
            .nullable(),
        institutions: z
            .array(z.object({
            id: z.number(),
            name: z.string(),
        }))
            .nullable(),
        original_size_x: z.number().nullable().optional(),
        original_size_y: z.number().nullable().optional(),
        documents_count: z.number(),
    })),
    total_count: z.number(),
})
    .strip();
const minMaxOption = z
    .string()
    .regex(/^\d+,\d+$/)
    .transform((val) => val.split(',').map((v) => Number(v)));
const artAssetsReadManyStatsQueryParamsSchema = z.object({
    art_assets: arrayIdsOption$2.optional(),
    artists: arrayIdsOption$2.optional(),
    artists_slugs: arrayStringsOption$3.optional(),
    medium: arrayIdsOption$2.optional(),
    medium_slugs: arrayStringsOption$3.optional(),
    styles: arrayIdsOption$2.optional(),
    styles_slugs: arrayStringsOption$3.optional(),
    countries: arrayIdsOption$2.optional(),
    countries_slugs: arrayStringsOption$3.optional(),
    institutions: arrayIdsOption$2.optional(),
    original_size_x: minMaxOption.optional(),
    original_size_y: minMaxOption.optional(),
    original_size_z: minMaxOption.optional(),
    price: minMaxOption.optional(),
    dlct: booleanOptionSchema.optional(),
    documents: booleanOptionSchema.optional(),
    exclude_art_assets: arrayIdsOption$2.optional(),
    token_status: arrayStringsOption$3.optional(),
    search: z.string().min(2).optional(),
    creation_year: minMaxOption
        .refine(([min, max]) => min <= max, {
        message: 'In ranges of creation years, the start must be less than or equal to the end.',
    })
        .optional(),
    curator_review: booleanOptionSchema.optional(),
    curators: arrayIdsOption$2.optional(),
    curators_slugs: arrayStringsOption$3.optional(),
    art_asset_type: arrayStringsOption$3.optional(),
});
const artAssetReadManyStatsResponseBodySchema = artAssetListCountersSchema;
const artAssetReadFilterOptionsResponseSchema = z.object({
    artists: z.array(z.object({ id: z.number(), name: z.string(), slug: z.string().nullable(), art_assets_count: z.number() })),
    institutions: z.array(z.object({ id: z.number(), name: z.string() })),
    medium: z.array(z.object({ id: z.number(), name: z.string(), slug: z.string(), art_assets_count: z.number() })),
    styles: z.array(z.object({ id: z.number(), name: z.string(), slug: z.string(), art_assets_count: z.number() })),
    tags: z.array(z.object({ id: z.number(), name: z.string() })),
    original_size_x: z.tuple([z.number(), z.number()]).optional(),
    original_size_y: z.tuple([z.number(), z.number()]).optional(),
    original_size_z: z.tuple([z.number(), z.number()]).optional(),
    price: z.tuple([z.number(), z.number()]).optional(),
    dlct: z.boolean().optional(),
    documents: z.boolean().optional(),
    items_total_count: z.number(),
    token_status: z.array(z.nativeEnum(TokenStatus)),
    countries: z.array(z.object({ id: z.number(), name: z.string(), slug: z.string(), art_assets_count: z.number() })),
    creation_year: z.tuple([z.number(), z.number()]).optional(),
    curator_review: z.boolean().optional(),
    curators: z.array(z.object({
        id: z.number(),
        first_name: z.string(),
        last_name: z.string(),
        slug: z.string().nullable(),
        art_assets_count: z.number(),
    })),
    art_asset_type: z.array(z.nativeEnum(ArtAssetType)),
});
const readManySimilarArtAssetsParamsSchema = z.object({
    id: z.string().transform((v) => parseInt(v)),
});
const readManySimilarArtAssetsQuerySchema = z
    .object({
    institutions: arrayIdsOption$2,
})
    .merge(paginationQuerySchema);
const readManySimilarArtAssetsResponseSchema = z.array(z.object({}));

const readOneAuctionParamsSchema = z.object({
    auctionId: z.string().uuid(),
});
const readOneAuctionResponseBodySchema = z.object({
    id: z.string().uuid(),
    lot_id: z.number().int(),
    on_chain_id: z.string(),
    bid_step_value: z.string(),
    buyer_fee_premium_percent: z.number(),
    currency: currencySchema,
    start_date: z.coerce.date().nullable().optional(),
    end_date: z.coerce.date().nullable().optional(),
    duration: z.number().int(),
    initial_bid_value: z.string(),
    secondary_market_royalty: z.number(),
    seller_fee_percent: z.number(),
    status: z.nativeEnum(AuctionStatus),
    storage_fee_value: z.string(),
    auction_shares: z.array(z.object({
        id: z.string().uuid(),
        percent: z.number(),
        wallet: z.object({
            id: z.string().uuid(),
            address: z.string(),
            artist_id: z.number().int().optional().nullable(),
            institution_id: z.number().int().optional().nullable(),
            is_platform_address: z.boolean(),
        }),
    })),
    best_bid: z
        .object({
        id: z.string().uuid(),
        value: z.string(),
        value_formatted: z.number(),
        bidder_wallet: z.string(),
    })
        .optional()
        .nullable(),
    art_asset: z.object({
        id: z.number().int(),
        name_english: z.string(),
        name_original: z.string().optional().nullable(),
        art_asset_type: z.nativeEnum(ArtAssetType),
        format: z.nativeEnum(ArtAssetFormat),
        content_flags: z.array(z.nativeEnum(ArtAssetContentFlags)),
        creation_year: z.string().nullable(),
        main_photo: photoSchema.nullable().optional(),
        token_id: z.number().optional().nullable(),
        digital_original: z
            .object({
            id: z.number(),
            mpx: z.number(),
            exif: exifSchema,
        })
            .nullable(),
        digital_common: z
            .object({
            id: z.number(),
            preview: z.string().nullable(),
            file: z.string().nullable(),
            filesize: z.string().nullable(),
            filename_disk: z.string().nullable(),
            type: z.string().nullable(),
        })
            .nullable(),
        artists: z.array(z.object({
            id: z.number().int(),
            name: z.string(),
            status: z.nativeEnum(ArtistStatus),
            photo: photoSchema.optional().nullable(),
        })),
    }),
    bids_count: z.number(),
    bids_wallets: z.array(z.string()),
    date_created: z.string(),
    date_updated: z.string().nullable(),
});
const readOneAuctionByOnChainIdParamsSchema = z.object({
    onChainId: z.string(),
});
const readOneAuctionByOnChainIdResponseBodySchema = readOneAuctionResponseBodySchema;
const auctionListItemSchema = auctionListItemSchema$2;
const readManyAuctionsQuerySchema = readManyAuctionsQuerySchema$2.omit({
    search: true,
    visible: true,
});
const readManyAuctionsFiltersParamsSchema = readManyAuctionsQuerySchema$2.omit({
    search: true,
    limit: true,
    offset: true,
    sort_column: true,
    sort_order: true,
});
const readManyAuctionsStatsParamsSchema = readManyAuctionsFiltersParamsSchema;
const readManyAuctionsPaginationAndSortParamsSchema = readManyAuctionsQuerySchema$2.pick({
    limit: true,
    offset: true,
    sort_column: true,
    sort_order: true,
});
const readManyAuctionsStatsResponseBodySchema = z.object({ total_count: z.number() });
const readManyAuctionsResponseBodySchema = auctionListSchema$1;

const readOneCollectorQuerySchema = readOneCollectorQuerySchema$1;
const readOneCollectorResponseBodySchema = readOneCollectorResponseSchema;
const collectorIdParamsSchema = z.object({
    id: z.string(),
});

const readOneGalleryResponseBodySchema = readOneGalleryResponseBodySchema$2;
const readOneGalleryQuerySchema = readOneGalleryQuerySchema$2;
const galleryIdParamsSchema = galleryIdParamsSchema$2;

const limitSchema = z
    .union([
    z
        .string()
        .transform((v) => parseInt(v))
        .pipe(z.number().max(1000)),
    z.number().max(1000),
])
    .default(10);
const readTokensHistoryQuerySchema = z.object({
    limit: limitSchema,
    cursor: z.string().optional(),
    auction_id: z.string().uuid().optional(),
    art_asset_id: z.union([z.number().int(), z.string().transform((v) => parseInt(v))]).optional(),
});
const tokensHistoryIncomeSchema = z.object({
    amount: z.string(),
    amount_formatted: z.string(),
    currency: z.string(),
    wallet_id: z.string(),
    wallet_address: evmAddressWithExplorerLinkSchema,
});
const baseTokensHistoryItemSchema = z.object({
    id: z.string(),
    tx_hash: hashWithExplorerLinkSchema,
    from: evmAddressWithExplorerLinkSchema,
    index: z.number(),
    tx_fee: z.string(),
    tx_fee_formatted: z.string(),
    block_timestamp: z.string(),
    tx_index: z.number(),
    block_number: z.string(),
    art_asset: z.object({
        id: z.number(),
        name_english: z.string(),
        name_original: z.string().nullable().optional(),
        art_asset_type: z.nativeEnum(ArtAssetType),
        format: z.nativeEnum(ArtAssetFormat),
        main_photo: photoSchema.nullable().optional(),
    }),
    incomes: z.array(tokensHistoryIncomeSchema),
});
const tokenHistoryTransferItemSchema = baseTokensHistoryItemSchema.extend({
    kind: z.literal(TokenHistoryRecordKind.Transfer),
    token_id: z.number(),
    transferred_to: evmAddressWithExplorerLinkSchema,
    transferred_from: evmAddressWithExplorerLinkSchema,
});
const tokenHistoryMintItemSchema = baseTokensHistoryItemSchema.extend({
    kind: z.literal(TokenHistoryRecordKind.Mint),
    token_id: z.number(),
    minted_to: evmAddressWithExplorerLinkSchema,
});
const tokenHistoryBuyItemSchema = baseTokensHistoryItemSchema.extend({
    kind: z.literal(TokenHistoryRecordKind.Buy),
    price: z.string(),
    price_formatted: z.string().nullable().optional(),
    token_id: z.number(),
    transferred_to: evmAddressWithExplorerLinkSchema,
});
const tokenAuctionCreatedItemSchema = baseTokensHistoryItemSchema.extend({
    kind: z.literal(TokenHistoryRecordKind.AuctionCreated),
    auction: z.string(),
    token_id: z.number(),
    price: z.string(),
    price_formatted: z.string().optional().nullable(),
    end_time: z.number(),
});
const tokenAuctionBidPlacedItemSchema = baseTokensHistoryItemSchema.extend({
    kind: z.literal(TokenHistoryRecordKind.AuctionBidPlaced),
    auction: z.string(),
    price: z.string(),
    price_formatted: z.string().optional().nullable(),
    buyer: evmAddressWithExplorerLinkSchema,
});
const tokenAuctionSoldItemSchema = baseTokensHistoryItemSchema.extend({
    auction: z.string(),
    price: z.string(),
    price_formatted: z.string().optional().nullable(),
    buyer: evmAddressWithExplorerLinkSchema,
    kind: z.literal(TokenHistoryRecordKind.AuctionSold),
});
const tokenHistoryItemSchema = z.discriminatedUnion('kind', [
    tokenHistoryTransferItemSchema,
    tokenHistoryMintItemSchema,
    tokenHistoryBuyItemSchema,
    tokenAuctionCreatedItemSchema,
    tokenAuctionBidPlacedItemSchema,
    tokenAuctionSoldItemSchema,
]);
const tokenHistoryResponseSchema = z.object({
    data: z.array(tokenHistoryItemSchema),
    cursor: z.string().optional().nullable(),
});

const generateFaviconSetParamsSchema = z.object({
    institutionName: z.string(),
    institutionId: z.number(),
    faviconSourceFilename: z.string(),
    fileId: z.string(),
});
const openGraphSchema = z.object({
    width: z.number(),
    height: z.number(),
    url: z.string(),
});
const generateFaviconSetResponseSchema = z.object({
    html: z.string(),
    openGraph: openGraphSchema,
});

var index$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    generateFaviconSetParamsSchema: generateFaviconSetParamsSchema,
    openGraphSchema: openGraphSchema,
    generateFaviconSetResponseSchema: generateFaviconSetResponseSchema
});

const readInstitutionStoreConfigurationByDomainParamsSchema = z.object({
    domain: z.string(),
});
const readStoreConfigurationsParamsSchema = z.object({
    institution_slug: z.string(),
});
const readStoreConfigurationsResponseSchema = z.object({
    id: z.number(),
    type: z.nativeEnum(InstitutionType),
    status: z.nativeEnum(SiteStatus),
    slug: z.string(),
    name: z.string(),
    tagline: z.string().nullable(),
    logo: photoSchema.nullable(),
    social_profiles: z
        .array(z.object({
        type: z.string(),
        value: z.string(),
    }))
        .nullable(),
    contacts: z
        .array(z.object({
        type: z.string(),
        value: z.string(),
    }))
        .nullable(),
    locations: locationSchema.array().nullable(),
    favicon_html: z.string().nullable(),
    open_graph: openGraphSchema.nullable(),
    domain_data: institutionStoreDomainDtoSchema$2.nullable().optional(),
    is_suspicious: z.boolean(),
    associated_artist: z.number().nullable(),
    associated_gallery: z.number().nullable(),
    associated_agency: z.number().nullable(),
    associated_curator: z.number().nullable(),
    associated_collector: z.number().nullable(),
});
const institutionStoreDomainDtoSchema = institutionStoreDomainDtoSchema$2;
const readInstitutionAboutParamsSchema = z.object({
    id: z
        .string()
        .transform((raw) => parseInt(raw, 10))
        .pipe(z.number()),
});
const readInstitutionAboutResponseSchema = z.object({
    description: editorBlockSchema.nullable(),
});
const readInstitutionInfoParamsSchema = z.object({
    id: z
        .string()
        .transform((raw) => parseInt(raw, 10))
        .pipe(z.number()),
});
const readInstitutionInfoResponseSchema = z.object({
    name: z.string(),
});
const readInstitutionClassificationsParamsSchema = readInstitutionClassificationsParamsSchema$1;
const readInstitutionClassificationsQueryParamsSchema = readInstitutionClassificationsQueryParamsSchema$1.pick({
    limit: true,
});
const readInstitutionClassificationsResponseSchema = readInstitutionClassificationsResponseSchema$1;
const createInstitutionSubscriptionSchema = z.object({
    institution_id: z.number().int(),
    email: z.string().email(),
});
const createInstitutionSubscriptionResponseSchema = z.object({
    id: z.number(),
});

const readFAQParamsSchema = z.object({
    domain: z.string(),
});
const readFAQResponseBody = z.object({
    date_created: z.string(),
    date_updated: z.string().nullable(),
    categories: z.array(z.object({
        id: z.number(),
        name: z.string(),
        faq_blocks: z.array(z.object({
            id: z.number(),
            question: z.string(),
            answer: editorBlockSchema.nullable(),
        })),
    })),
});
const reportRequestBodySchema = z
    .object({
    type: z.nativeEnum(ReportType),
    message: z.string(),
    full_name: z.string(),
    email: z.string().email(),
    url: z.string().url(),
})
    .and(z
    .object({
    reported_collection: z.literal(ReportedCollection.ArtAsset),
    [ReportedCollection.ArtAsset]: z.number(),
})
    .or(z.object({
    reported_collection: z.literal(ReportedCollection.Artist),
    [ReportedCollection.Artist]: z.number(),
}))
    .or(z.object({
    reported_collection: z.literal(ReportedCollection.Auction),
    [ReportedCollection.Auction]: z.string(),
})));
const contactUsRequestBodySchema = z.object({
    from: z.nativeEnum(ContactUsFrom),
    inquiry_type: z.nativeEnum(ContactUsInquiryType).optional(),
    major: z.string().optional(),
    full_name: z.string(),
    email: z.string().email(),
    phone: z.string().optional(),
    message: z.string().optional(),
    captured_url: z.string(),
    institution_id: z.number().optional(),
});
const subscribeRequestBodySchema = z.object({
    from: z.nativeEnum(SubscriptionFrom),
    email: z.string().email(),
});
const leadsRequestBodySchema = z.object({
    type: z.nativeEnum(LeadType).default(LeadType.Other),
    from: z.nativeEnum(LeadFrom),
    full_name: z.string(),
    gallery_name: z.string().optional(),
    position: z.string().optional(),
    phone: z.string().optional(),
    email: z.string().email(),
    country: z.number().optional(),
    send_blockchain_handbook: z.boolean().default(false),
});
const createArtAssetPreOrderRequestBodySchema = createArtAssetPreOrderRequestBodySchema$1;

const readTilesByFileIdParamsSchema = readTilesByFileIdParamsSchema$2;
const tilesListSchema = tilesListSchema$2;

const sendArtistReportMailRequestParamsSchema = sendArtistReportMailRequestParamsSchema$1;

const sendArtistReportSlackNotificationParamsSchema = sendArtistReportSlackNotificationParamsSchema$1;
const sendArtAssetReportSlackNotificationParamsSchema = sendArtAssetReportSlackNotificationParamsSchema$1;
const sendAuctionReportSlackNotificationParamsSchema = sendAuctionReportSlackNotificationParamsSchema$1;

const readPhotoDataParamsSchema = z.object({
    id: z.string(),
});
const readPhotoResponseSchema = readPhotoResponseSchema$1;
const fileDtoSchema = z.object({
    id: z.string(),
    type: z.string().nullable(),
    title: z.string().nullable(),
    tags: z.array(z.string()).default([]),
});
const readFileParamsSchema = z.object({
    id: z.string(),
});
const downloadFileParamsSchema = z.object({
    id: z.string(),
});

const readOneCuratorReviewRequestParamsSchema = readOneCuratorReviewRequestParamsSchema$2;
const readOneCuratorsReviewsResponseBodySchema = readOneCuratorsReviewsResponseBodySchema$2;
const readManyCuratorsReviewsRequestQuerySchema = curatorsReviewsFilterQuerySchema
    .merge(curatorsReviewsSortQuerySchema)
    .merge(paginationQuerySchema);
const readManyCuratorsReviewsResponseBodySchema = readManyCuratorsReviewsResponseBodySchema$2;

const readManyCitiesRequestQueryFiltersSchema = readManyCitiesRequestQueryFiltersSchema$2;
const readCitiesTotalCountResponseBodySchema = z.object({ total_count: z.number() });
const readManyCitiesRequestQuerySchema = z
    .object({
    country_id: z.string().optional(),
})
    .merge(paginationQuerySchema);

const readManyNewsFilterQuerySchema = newsFilterSchema.omit({ statuses: true });
const readManyNewsQuerySchema = readManyNewsQuerySchema$1.omit({ statuses: true });
const readManyNewsResponseSchema = z.object({
    data: newsListSchema,
    total_count: z.number(),
});
const readNewsStatsResponseSchema = z.object({ total_count: z.number() });
const readNewsStatsQuerySchema = readManyNewsFilterQuerySchema;
const readOneNewsBySlugParamsSchema = z.object({
    slug: z.string(),
});
const readOneNewsResponseSchema = newsItemSchema;

const readOneCuratorRequestParamsSchema = z.object({
    id: z.string(),
});
const readOneCuratorResponseSchema = z.object({
    id: z.number(),
    slug: z.string(),
    first_name: z.string(),
    last_name: z.string().nullable(),
    photo: photoSchema.nullable(),
    photo_attribution: z.string().nullable(),
    biography: editorBlockSchema.nullable(),
    position: z.string().nullable().optional(),
    contacts: contactsSchema.nullable().optional(),
    social_profiles: socialProfilesSchema.nullable().optional(),
    locations: z.array(locationSchema).nullable().optional(),
});
const curatorFilterQuerySchema = z.object({
    institution_id: z.string(),
});
const readManyCuratorsQuerySchema = curatorFilterQuerySchema
    .merge(curatorsSortQuerySchema)
    .merge(paginationQuerySchema);
const readManyCuratorsResponseSchema = z.object({
    data: z.array(z.object({
        id: z.number(),
        slug: z.string(),
        first_name: z.string(),
        last_name: z.string(),
        position: z.string().nullable().optional(),
        photo: photoSchema.nullable(),
        reviews_count: z.number(),
    })),
    total_count: z.number(),
});
const readCuratorsStatsResponseSchema = z.object({ total_count: z.number() });
const readCuratorsStatsQuerySchema = curatorFilterQuerySchema;

const searchFilterQuerySchema = z.object({
    search: z.string().min(2).optional(),
    institutions: arrayIdsOption$2.optional(),
});
const searchQuerySchema = searchFilterQuerySchema.merge(paginationQuerySchema);
const searchResponseSchema = z.object({
    data: z.object({
        art_assets: z.object({
            data: readManyArtAssetsResponseSchema.shape.art_assets,
            total_count: z.number(),
        }),
        artists: z.object({
            data: artistsListSchema,
            total_count: z.number(),
        }),
        selections: z.object({
            data: readManySelectionsResponseBodySchema$2,
            total_count: z.number(),
        }),
    }),
    total_count: z.number(),
});

const readOneSelectionRequestQuerySchema = z
    .object({
    token: z.string(),
})
    .partial();
const readOneSelectionRequestParamsSchema = readOneSelectionRequestParamsSchema$2;
const selectionArtAssetArtistSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    status: z.nativeEnum(ArtistStatus),
    photo: photoSchema.optional().nullable(),
});
const selectionArtAssetSchema = selectionArtAssetSchema$1.merge(z.object({
    artists: z.array(selectionArtAssetArtistSchema),
    physical_purchase_price: z.number().nullable(),
    estimate_price: z.number().nullable(),
    realized_price: z.number().nullable(),
}));
const readOneSelectionResponseBodySchema = readOneSelectionResponseBodySchema$2
    .omit({
    access_token: true,
})
    .merge(z.object({ art_assets: z.array(selectionArtAssetSchema) }));
const readOneSelectionByAccessTokenRequestParamsSchema = readOneSelectionByAccessTokenRequestParamsSchema$1;
const readOneSelectionByAccessTokenResponseBodySchema = readOneSelectionResponseBodySchema;
const readManySelectionsRequestFilterQuerySchema = z.object({
    institution_id: z.string().optional(),
    art_assets: arrayIdsOption$2.optional(),
    art_assets_slugs: arrayStringsOption$3.optional(),
    artists: arrayIdsOption$2.optional(),
    artists_slugs: arrayStringsOption$3.optional(),
    search: z.string().optional(),
});
const readManySelectionsRequestSortQuerySchema = readManySelectionsRequestSortQuerySchema$2;
const readManySelectionsRequestQuerySchema = readManySelectionsRequestSortQuerySchema
    .partial()
    .merge(readManySelectionsRequestFilterQuerySchema.partial())
    .merge(paginationQuerySchema.partial());
const readManySelectionsResponseBodySchema = z.object({
    selections: readManySelectionsResponseBodySchema$2,
    total_count: z.number(),
});
const readManySelectionsTotalCountRequestQuerySchema = readManySelectionsRequestFilterQuerySchema;
const readManySelectionsTotalCountResponseBodySchema = z.object({
    total_count: z.number(),
});
const ensureAccessCodeRequestQuerySchema = z.object({
    token: z
        .string()
        .regex(/^\d{4}$/)
        .optional(),
});
const ensureAccessCodeResponseBodySchema = z.object({ token: z.string() });

const classificationItemSchema = z.object({
    id: z.number(),
    name: z.string(),
    slug: z.string(),
    institution_id: z.string().nullable(),
});
const stylesSchema = z.array(classificationItemSchema);
const readManyStylesQuerySchema = z.object({
    institution_id: z.string(),
});
const readManyStylesResponseSchema = z.array(classificationItemSchema.omit({ institution_id: true }).merge(z.object({
    isGlobal: z.boolean(),
})));
const mediumsSchema = z.array(classificationItemSchema);
const readManyMediumsQuerySchema = z.object({
    institution_id: z.string(),
});
const readManyMediumsResponseSchema = z.array(classificationItemSchema.omit({ institution_id: true }).merge(z.object({
    isGlobal: z.boolean(),
})));

const getSaleOrderPurchaseApproveParamsSchema = z.object({
    id: z.string().uuid(),
});
const getSaleOrderPurchaseApproveQuerySchema = z.object({
    buyer_address: z.string(),
});
const approvalResponseSchema = z.object({
    to: z.string(),
    tokenId: z.number().int(),
    tokenURI: z.string(),
    price: z.string(),
    participants: z.array(z.string()),
    shares: z.array(z.string()),
    deadline: z.number().int(),
    signature: z.string(),
});

var index = /*#__PURE__*/Object.freeze({
    __proto__: null,
    readOneAgencyResponseBodySchema: readOneAgencyResponseBodySchema,
    readOneAgencyQuerySchema: readOneAgencyQuerySchema,
    agencyIdParamsSchema: agencyIdParamsSchema,
    readArtistWalletAuthorizationParamsSchema: readArtistWalletAuthorizationParamsSchema,
    readArtistWalletAuthorizationQuerySchema: readArtistWalletAuthorizationQuerySchema,
    readArtistWalletAuthorizationResponseSchema: readArtistWalletAuthorizationResponseSchema,
    requestArtistWalletNonceParamsSchema: requestArtistWalletNonceParamsSchema,
    artistWalletNonceResponseSchema: artistWalletNonceResponseSchema,
    verifyArtistWalletParamsSchema: verifyArtistWalletParamsSchema,
    artistWalletDtoSchema: artistWalletDtoSchema,
    unauthorizeArtistWalletParamsSchema: unauthorizeArtistWalletParamsSchema,
    readManyArtistsStatsResponseSchema: readManyArtistsStatsResponseSchema,
    artistListItemSchema: artistListItemSchema,
    artistsListSchema: artistsListSchema,
    readManyArtistsResponseSchema: readManyArtistsResponseSchema,
    readManyArtistsFilterQuerySchema: readManyArtistsFilterQuerySchema,
    readManyArtistsQuerySchema: readManyArtistsQuerySchema,
    readArtistsStatsQuerySchema: readArtistsStatsQuerySchema,
    firstLettersResponseSchema: firstLettersResponseSchema,
    readOneArtistParamsSchema: readOneArtistParamsSchema,
    artAssetsOptionsSchema: artAssetsOptionsSchema,
    readOneArtistResponseSchema: readOneArtistResponseSchema,
    readArtistFilterOptionsResponseSchema: readArtistFilterOptionsResponseSchema,
    readManyArtistsUFDAPhotosResponseSchema: readManyArtistsUFDAPhotosResponseSchema,
    readManyArtAssetsQuerySchema: readManyArtAssetsQuerySchema,
    readManyArtAssetsResponseSchema: readManyArtAssetsResponseSchema,
    readArtAssetsTotalCountQuerySchema: readArtAssetsTotalCountQuerySchema,
    readArtAssetsTotalCountResponseSchema: readArtAssetsTotalCountResponseSchema,
    readManyArtAssetsByOwnerWalletRequestQuerySchema: readManyArtAssetsByOwnerWalletRequestQuerySchema,
    readManyArtAssetsByOwnerWalletResponseBodySchema: readManyArtAssetsByOwnerWalletResponseBodySchema,
    readOneArtAssetParamsSchema: readOneArtAssetParamsSchema,
    readOneArtAssetQuerySchema: readOneArtAssetQuerySchema,
    readOneArtAssetResponseSchema: readOneArtAssetResponseSchema,
    readManyVRArtAssetsQuerySchema: readManyVRArtAssetsQuerySchema,
    readManyVRArtAssetsResponseSchema: readManyVRArtAssetsResponseSchema,
    artAssetsReadManyStatsQueryParamsSchema: artAssetsReadManyStatsQueryParamsSchema,
    artAssetReadManyStatsResponseBodySchema: artAssetReadManyStatsResponseBodySchema,
    artAssetReadFilterOptionsResponseSchema: artAssetReadFilterOptionsResponseSchema,
    readManySimilarArtAssetsParamsSchema: readManySimilarArtAssetsParamsSchema,
    readManySimilarArtAssetsQuerySchema: readManySimilarArtAssetsQuerySchema,
    readManySimilarArtAssetsResponseSchema: readManySimilarArtAssetsResponseSchema,
    readOneAuctionParamsSchema: readOneAuctionParamsSchema,
    readOneAuctionResponseBodySchema: readOneAuctionResponseBodySchema,
    readOneAuctionByOnChainIdParamsSchema: readOneAuctionByOnChainIdParamsSchema,
    readOneAuctionByOnChainIdResponseBodySchema: readOneAuctionByOnChainIdResponseBodySchema,
    auctionListItemSchema: auctionListItemSchema,
    readManyAuctionsQuerySchema: readManyAuctionsQuerySchema,
    readManyAuctionsFiltersParamsSchema: readManyAuctionsFiltersParamsSchema,
    readManyAuctionsStatsParamsSchema: readManyAuctionsStatsParamsSchema,
    readManyAuctionsPaginationAndSortParamsSchema: readManyAuctionsPaginationAndSortParamsSchema,
    readManyAuctionsStatsResponseBodySchema: readManyAuctionsStatsResponseBodySchema,
    readManyAuctionsResponseBodySchema: readManyAuctionsResponseBodySchema,
    readOneCollectorQuerySchema: readOneCollectorQuerySchema,
    readOneCollectorResponseBodySchema: readOneCollectorResponseBodySchema,
    collectorIdParamsSchema: collectorIdParamsSchema,
    readOneGalleryResponseBodySchema: readOneGalleryResponseBodySchema,
    readOneGalleryQuerySchema: readOneGalleryQuerySchema,
    galleryIdParamsSchema: galleryIdParamsSchema,
    readTokensHistoryQuerySchema: readTokensHistoryQuerySchema,
    tokensHistoryIncomeSchema: tokensHistoryIncomeSchema,
    baseTokensHistoryItemSchema: baseTokensHistoryItemSchema,
    tokenHistoryTransferItemSchema: tokenHistoryTransferItemSchema,
    tokenHistoryMintItemSchema: tokenHistoryMintItemSchema,
    tokenAuctionCreatedItemSchema: tokenAuctionCreatedItemSchema,
    tokenAuctionBidPlacedItemSchema: tokenAuctionBidPlacedItemSchema,
    tokenAuctionSoldItemSchema: tokenAuctionSoldItemSchema,
    tokenHistoryItemSchema: tokenHistoryItemSchema,
    tokenHistoryResponseSchema: tokenHistoryResponseSchema,
    readInstitutionStoreConfigurationByDomainParamsSchema: readInstitutionStoreConfigurationByDomainParamsSchema,
    readStoreConfigurationsParamsSchema: readStoreConfigurationsParamsSchema,
    readStoreConfigurationsResponseSchema: readStoreConfigurationsResponseSchema,
    institutionStoreDomainDtoSchema: institutionStoreDomainDtoSchema,
    readInstitutionAboutParamsSchema: readInstitutionAboutParamsSchema,
    readInstitutionAboutResponseSchema: readInstitutionAboutResponseSchema,
    readInstitutionInfoParamsSchema: readInstitutionInfoParamsSchema,
    readInstitutionInfoResponseSchema: readInstitutionInfoResponseSchema,
    readInstitutionClassificationsParamsSchema: readInstitutionClassificationsParamsSchema,
    readInstitutionClassificationsQueryParamsSchema: readInstitutionClassificationsQueryParamsSchema,
    readInstitutionClassificationsResponseSchema: readInstitutionClassificationsResponseSchema,
    createInstitutionSubscriptionSchema: createInstitutionSubscriptionSchema,
    createInstitutionSubscriptionResponseSchema: createInstitutionSubscriptionResponseSchema,
    readFAQParamsSchema: readFAQParamsSchema,
    readFAQResponseBody: readFAQResponseBody,
    reportRequestBodySchema: reportRequestBodySchema,
    contactUsRequestBodySchema: contactUsRequestBodySchema,
    subscribeRequestBodySchema: subscribeRequestBodySchema,
    leadsRequestBodySchema: leadsRequestBodySchema,
    createArtAssetPreOrderRequestBodySchema: createArtAssetPreOrderRequestBodySchema,
    readTilesByFileIdParamsSchema: readTilesByFileIdParamsSchema,
    tilesListSchema: tilesListSchema,
    sendArtistReportMailRequestParamsSchema: sendArtistReportMailRequestParamsSchema,
    sendArtistReportSlackNotificationParamsSchema: sendArtistReportSlackNotificationParamsSchema,
    sendArtAssetReportSlackNotificationParamsSchema: sendArtAssetReportSlackNotificationParamsSchema,
    sendAuctionReportSlackNotificationParamsSchema: sendAuctionReportSlackNotificationParamsSchema,
    readPhotoDataParamsSchema: readPhotoDataParamsSchema,
    readPhotoResponseSchema: readPhotoResponseSchema,
    fileDtoSchema: fileDtoSchema,
    readFileParamsSchema: readFileParamsSchema,
    downloadFileParamsSchema: downloadFileParamsSchema,
    readOneCuratorReviewRequestParamsSchema: readOneCuratorReviewRequestParamsSchema,
    readOneCuratorsReviewsResponseBodySchema: readOneCuratorsReviewsResponseBodySchema,
    readManyCuratorsReviewsRequestQuerySchema: readManyCuratorsReviewsRequestQuerySchema,
    readManyCuratorsReviewsResponseBodySchema: readManyCuratorsReviewsResponseBodySchema,
    readManyCitiesRequestQueryFiltersSchema: readManyCitiesRequestQueryFiltersSchema,
    readCitiesTotalCountResponseBodySchema: readCitiesTotalCountResponseBodySchema,
    readManyCitiesRequestQuerySchema: readManyCitiesRequestQuerySchema,
    readManyNewsFilterQuerySchema: readManyNewsFilterQuerySchema,
    readManyNewsQuerySchema: readManyNewsQuerySchema,
    readManyNewsResponseSchema: readManyNewsResponseSchema,
    readNewsStatsResponseSchema: readNewsStatsResponseSchema,
    readNewsStatsQuerySchema: readNewsStatsQuerySchema,
    readOneNewsBySlugParamsSchema: readOneNewsBySlugParamsSchema,
    readOneNewsResponseSchema: readOneNewsResponseSchema,
    readOneCuratorRequestParamsSchema: readOneCuratorRequestParamsSchema,
    readOneCuratorResponseSchema: readOneCuratorResponseSchema,
    curatorFilterQuerySchema: curatorFilterQuerySchema,
    readManyCuratorsQuerySchema: readManyCuratorsQuerySchema,
    readManyCuratorsResponseSchema: readManyCuratorsResponseSchema,
    readCuratorsStatsResponseSchema: readCuratorsStatsResponseSchema,
    readCuratorsStatsQuerySchema: readCuratorsStatsQuerySchema,
    searchFilterQuerySchema: searchFilterQuerySchema,
    searchQuerySchema: searchQuerySchema,
    searchResponseSchema: searchResponseSchema,
    readOneSelectionRequestQuerySchema: readOneSelectionRequestQuerySchema,
    readOneSelectionRequestParamsSchema: readOneSelectionRequestParamsSchema,
    selectionArtAssetArtistSchema: selectionArtAssetArtistSchema,
    selectionArtAssetSchema: selectionArtAssetSchema,
    readOneSelectionResponseBodySchema: readOneSelectionResponseBodySchema,
    readOneSelectionByAccessTokenRequestParamsSchema: readOneSelectionByAccessTokenRequestParamsSchema,
    readOneSelectionByAccessTokenResponseBodySchema: readOneSelectionByAccessTokenResponseBodySchema,
    readManySelectionsRequestFilterQuerySchema: readManySelectionsRequestFilterQuerySchema,
    readManySelectionsRequestSortQuerySchema: readManySelectionsRequestSortQuerySchema,
    readManySelectionsRequestQuerySchema: readManySelectionsRequestQuerySchema,
    readManySelectionsResponseBodySchema: readManySelectionsResponseBodySchema,
    readManySelectionsTotalCountRequestQuerySchema: readManySelectionsTotalCountRequestQuerySchema,
    readManySelectionsTotalCountResponseBodySchema: readManySelectionsTotalCountResponseBodySchema,
    ensureAccessCodeRequestQuerySchema: ensureAccessCodeRequestQuerySchema,
    ensureAccessCodeResponseBodySchema: ensureAccessCodeResponseBodySchema,
    stylesSchema: stylesSchema,
    readManyStylesQuerySchema: readManyStylesQuerySchema,
    readManyStylesResponseSchema: readManyStylesResponseSchema,
    mediumsSchema: mediumsSchema,
    readManyMediumsQuerySchema: readManyMediumsQuerySchema,
    readManyMediumsResponseSchema: readManyMediumsResponseSchema,
    getSaleOrderPurchaseApproveParamsSchema: getSaleOrderPurchaseApproveParamsSchema,
    getSaleOrderPurchaseApproveQuerySchema: getSaleOrderPurchaseApproveQuerySchema,
    approvalResponseSchema: approvalResponseSchema
});

export { index$2 as AmsApiDto, index$6 as CommonDto, index$3 as DirectusDto, index as ShopApiDto, index$1 as SitesDataManagerDto, index$5 as SitesProxyManagerDto, index$4 as TilesDto };
